import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";
import {
  fetchUserAttributes,
  getCurrentUser,
  signOut
} from "aws-amplify/auth";
import "./App.css";
import { ApplicationIcon } from "./components/ApplicationIcon";
import { AuthPanel } from "./components/AuthPanel";
import { downloadReceiptDocument } from "./lib/receiptDocument";
import { slugify } from "./lib/slugify";
import {
  addHouseholdMember,
  addMemberDocument,
  addMemberFlag,
  addMemberQualification,
  checkResourceAvailability,
  changeMemberPlan,
  closePaymentReconciliationPeriod,
  createDonation,
  createEvent,
  createEventRegistration,
  createMember,
  createMembershipDocumentUploadUrl,
  createMembershipType,
  createOperationNote,
  createRange,
  createResource,
  createResourceBlock,
  createResourceReservation,
  deliverNotification,
  createStripeConnectAccountSession,
  downloadAuditCsv,
  downloadDonationsCsv,
  downloadEventsCsv,
  downloadEventAttendanceCsv,
  downloadEventRegistrationsCsv,
  downloadMemberFlagsCsv,
  downloadMemberGuestPassesCsv,
  downloadMemberHouseholdCsv,
  downloadMembersCsv,
  downloadMemberQualificationsCsv,
  downloadNotificationsCsv,
  downloadPaymentEventsCsv,
  downloadPaymentPeriodClosesCsv,
  downloadPaymentReconciliationCsv,
  downloadPaymentReceiptsCsv,
  downloadRangeAccessCsv,
  downloadResourceBlocksCsv,
  downloadResourcesCsv,
  downloadResourceReservationsCsv,
  disableRangeApplication,
  enableRangeApplication,
  getBusinessHours,
  getDonationReceipt,
  getMemberDetail,
  getMemberTimeline,
  getDonationSettings,
  getNotificationPreferences,
  getPublicDomainContext,
  getRangeReadiness,
  getMembershipSummary,
  getRangePayments,
  getResourceDetail,
  getResourceReservationConflicts,
  getResourceSummary,
  grantRangeAccess,
  listAuditEvents,
  listApplications,
  listChapters,
  listDonations,
  listEvents,
  listEventRegistrations,
  listEventAttendance,
  listMembers,
  listMyRanges,
  listNonMemberAttendees,
  listNotifications,
  listOperationNotes,
  listPaymentEvents,
  listPaymentIssues,
  listPaymentReceipts,
  getPublicChapters,
  listRangeAccess,
  listRangeApplications,
  listResourceBlocks,
  listResourceReservations,
  promoteNextEventWaitlist,
  previewCommunicationAudience,
  queueEventReminders,
  queueCustomNotification,
  previewMemberImport,
  refreshStripeConnectAccount,
  refreshSenderEmailVerification,
  recordDonationRefund,
  refundPaymentReceipt,
  renewMember,
  reviewEventApproval,
  sendRangeLeadEmail,
  sendNonMemberAttendeeEmail,
  sendCommunicationBroadcast,
  setDefaultMembershipType,
  signOffEvent,
  startSenderEmailVerification,
  syncPaymentReconciliation,
  updateMemberQualification,
  updateMemberProfile,
  updateMemberReview,
  updateMemberGuestPasses,
  updatePaymentReceiptReconciliation,
  updateMemberStatus,
  updateMembershipConfig,
  updateMembershipType,
  updateNotificationPreferences,
  updateNotificationStatus,
  updateRangeAccess,
  updateRangeBranding,
  updateRangeProductionMenu,
  updateRangeProfile,
  recordEventAttendance,
  updateEventAttendance,
  updateEventRegistration,
  updateBusinessHours,
  updateDonationSettings,
  updateResource,
  updateResourceBlock,
  updateResourceSectionCommand,
  updateResourceReservation,
  updateEvent
} from "./lib/rangegridApi";
import { DonationsPage } from "./modules/donations/DonationsPage";
import { EventsPage } from "./modules/events/EventsPage";
import { createBlankApplicationForm, normalizeApplicationFormForClient } from "./modules/membership/applicationForm";
import { MembershipPage } from "./modules/membership/MembershipPage";
import { ResourcesPage } from "./modules/resources/ResourcesPage";
import { AccessPage } from "./pages/AccessPage";
import { ApplicationsPage } from "./pages/ApplicationsPage";
import { AuditPage } from "./pages/AuditPage";
import { AccountingPage } from "./pages/AccountingPage";
import { ArmoryPage } from "./pages/ArmoryPage";
import { BuildE2EDashboardPage } from "./pages/BuildE2EDashboardPage";
import { ChaptersPage } from "./pages/ChaptersPage";
import { CommunicationsPage } from "./pages/CommunicationsPage";
import { CreateRangePage } from "./pages/CreateRangePage";
import { DailyOpsPage } from "./pages/DailyOpsPage";
import { EquipmentPage } from "./pages/EquipmentPage";
import { HomePage } from "./pages/HomePage";
import { InsightsPage } from "./pages/InsightsPage";
import { LandingPage } from "./pages/LandingPage";
import { LaunchCommandPage } from "./pages/LaunchCommandPage";
import { LeadsPage } from "./pages/LeadsPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { NonMemberAttendeesPage } from "./pages/NonMemberAttendeesPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { PlatformAdminPage } from "./pages/PlatformAdminPage";
import { PublicRangePage } from "./pages/PublicRangePage";
import { PublicDonationReceiptPage } from "./pages/PublicDonationReceiptPage";
import { ReportsPage } from "./pages/ReportsPage";
import { StaffPortalPage } from "./pages/StaffPortalPage";
import { SettingsPage } from "./pages/SettingsPage";

const DEV_USER_ID = process.env.REACT_APP_RANGEGRID_DEV_USER_ID || "";

function defaultMembershipTypeForm() {
  return {
    name: "",
    description: "",
    category: "membership",
    billingModel: "subscription",
    billingInterval: "year",
    termCount: "1",
    termUnit: "year",
    price: "0",
    paymentMode: "full_payment",
    prescreeningFee: "",
    prescreeningLabel: "Prescreening fee",
    prescreeningNonRefundable: true,
    balanceDueDays: "14",
    status: "active",
    publicSignupEnabled: false,
    campaignEnabled: false,
    campaignLabel: "",
    campaignMaxSignups: "",
    campaignShowRemaining: true,
    benefitsItemsText: "",
    benefitsNotes: "",
    requiredQualificationKeysText: "",
    guestPasses: "0",
    guestPassPeriod: "month",
    guestPassRollover: false,
    laneReservationAllowed: false,
    advanceBookingDays: "0",
    includedHoursPerMonth: "0",
    maxActiveReservations: "0",
    gunRentalAllowed: false,
    gunRentalDiscountPercent: "0",
    membersOnlyEventsAllowed: false,
    chapterScopeMode: "all",
    chapterScopeIds: "",
    primaryChapterId: ""
  };
}

function defaultMembershipConfigForm() {
  return {
    waiverRequired: true,
    waiverInstructions: "",
    waiverExpiresInDays: "365",
    waiverDocument: null,
    waiverDocumentHistory: [],
    applicationForm: createBlankApplicationForm(),
    prequalificationRequired: false,
    prequalificationInstructions: "",
    prequalificationChecklistText: "",
    backgroundCheckRequired: false,
    backgroundCheckInstructions: "",
    backgroundCheckChecklistText: "",
    backgroundCheckProvider: "",
    backgroundCheckEnvironment: "test",
    backgroundCheckPackageId: "",
    backgroundCheckPackageName: "",
    backgroundCheckWorkLocationId: "",
    backgroundCheckAutoStartAfterPrescreen: true,
    backgroundCheckMockMode: true,
    qualificationCatalogText: "",
    guestWaiverRequired: true,
    guestWaiverInstructions: "",
    approvalMode: "manual",
    chapterMemberVisibilityPhone: false,
    chapterMemberVisibilityAddress: false,
    chapterMemberVisibilityStatus: false,
    chapterMemberVisibilityJoinDate: false,
    chapterMemberVisibilityMemberNumber: false
  };
}

function defaultDonationForm() {
  return {
    donorName: "",
    donorEmail: "",
    amount: "50.00",
    status: "paid",
    note: ""
  };
}

function defaultDonationSettingsForm() {
  return {
    publicDonationsEnabled: true,
    customAmountEnabled: true,
    donorNotesEnabled: true,
    currency: "usd",
    minimumAmount: "1.00",
    suggestedAmounts: "25, 50, 100, 250"
  };
}

function defaultDonationFilters() {
  return {
    status: "",
    source: "",
    q: "",
    donorEmail: "",
    createdFrom: "",
    createdTo: "",
    paidFrom: "",
    paidTo: "",
    minAmountCents: "",
    maxAmountCents: ""
  };
}

function defaultPaymentReceiptFilters() {
  return {
    memberId: "",
    donationId: "",
    signupIntentId: "",
    checkoutSessionId: ""
  };
}

function defaultPaymentEventFilters() {
  return {
    memberId: "",
    eventId: "",
    registrationId: "",
    checkoutSessionId: ""
  };
}

function defaultMemberFilters() {
  return {
    status: "",
    membershipTypeId: "",
    chapterId: "",
    source: "",
    q: ""
  };
}

function defaultEventFilters() {
  return {
    status: "",
    visibility: "",
    type: "",
    resourceId: "",
    chapterId: "",
    q: ""
  };
}

function apiEventFilters(filters = {}) {
  return {
    ...filters,
    status: ["upcoming", "completed"].includes(filters.status) ? "" : filters.status
  };
}

function defaultEventRegistrationFilters() {
  return {
    status: "",
    paymentStatus: "",
    source: "",
    q: ""
  };
}

function defaultEventAttendanceFilters() {
  return {
    status: "",
    q: ""
  };
}

function defaultResourceFilters() {
  return {
    type: "",
    status: "",
    targetControlStatus: "",
    reservable: "",
    q: ""
  };
}

function defaultAccessFilters() {
  return {
    role: "",
    status: "",
    q: ""
  };
}

function defaultAuditFilters() {
  return {
    action: "",
    entityType: "",
    actorUserId: "",
    q: ""
  };
}

function defaultNotificationFilters() {
  return {
    status: "",
    audience: "",
    chapterId: "",
    type: "",
    recipientEmail: "",
    deliveryStatus: "",
    q: ""
  };
}

function dollarsFromCents(value, fallback = "0.00") {
  const cents = Number.parseInt(value || 0, 10);
  if (!Number.isFinite(cents)) {
    return fallback;
  }
  return (cents / 100).toFixed(2);
}

function donationSettingsFormFromSettings(settings = {}) {
  return {
    publicDonationsEnabled: settings.publicDonationsEnabled !== false,
    customAmountEnabled: settings.customAmountEnabled !== false,
    donorNotesEnabled: settings.donorNotesEnabled !== false,
    currency: settings.currency || "usd",
    minimumAmount: dollarsFromCents(settings.minimumAmountCents || 100, "1.00"),
    suggestedAmounts: (settings.suggestedAmountsCents || [2500, 5000, 10000, 25000])
      .map((amount) => dollarsFromCents(amount))
      .join(", ")
  };
}

function formatChecklistText(items = []) {
  return items
    .map((item) => [
      item.label,
      item.instructions
    ].filter(Boolean).join(" | "))
    .filter(Boolean)
    .join("\n");
}

function parseChecklistText(text) {
  return String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 20)
    .map((line, index) => {
      const [label, instructions] = line.split("|").map((item) => item.trim());
      return {
      key: `step_${index + 1}`,
      label,
      required: true,
        instructions: instructions || ""
      };
    });
}

function parseQualificationCatalogText(text) {
  return String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 100)
    .map((line) => {
      const [label, key, defaultExpiresInDays, description] = line.split("|").map((item) => item.trim());
      return {
        label,
        key,
        defaultExpiresInDays: Number.parseInt(defaultExpiresInDays || "0", 10) || 0,
        description,
        status: "active"
      };
    })
    .filter((item) => item.label);
}

function emptyAddress() {
  return {
    line1: "",
    line2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "US"
  };
}

function defaultCreateRangeForm(contactEmail = "") {
  return {
    templateKey: "physical_range",
    selectedApplicationKeys: ["membership", "resources", "events", "payments", "insights"],
    name: "",
    slug: "",
    timezone: "America/Los_Angeles",
    legalName: "",
    description: "",
    contactEmail,
    contactPhone: "",
    website: "",
    address: emptyAddress()
  };
}

function sortResourcesByName(resources = []) {
  return [...resources].sort((left, right) => (
    String(left.name || "").localeCompare(String(right.name || ""), undefined, {
      numeric: true,
      sensitivity: "base"
    })
  ));
}

function rangeProfileFormFromRange(range) {
  const profile = range?.profile || {};
  const address = profile.address || {};
  return {
    displayName: profile.displayName || range?.name || "",
    legalName: profile.legalName || "",
    description: profile.description || "",
    contactEmail: profile.contactEmail || "",
    contactPhone: profile.contactPhone || "",
    website: profile.website || "",
    timezone: profile.timezone || range?.timezone || "America/Los_Angeles",
    address: {
      ...emptyAddress(),
      ...address
    }
  };
}

const DEFAULT_MEMBER_PORTAL_ACTIONS = {
  membershipStatus: true,
  waiver: true,
  onboarding: true,
  qualifications: true,
  billing: true,
  reservations: true,
  events: true,
  guestPasses: true,
  rentals: false
};

function rangeBrandingFormFromRange(range) {
  const branding = range?.branding || {};
  const portalActions = branding.memberPortal?.actions || {};
  return {
    shortName: branding.shortName || "RG",
    primaryColor: branding.primaryColor || "#4f46e5",
    accentColor: branding.accentColor || "#2563eb",
    safetyColor: branding.safetyColor || branding.accentColor || "#b7791f",
    logoUrl: branding.logoUrl || "",
    logoFileName: branding.logoFileName || "",
    consoleIconUrl: branding.consoleIconUrl || "",
    consoleIconFileName: branding.consoleIconFileName || "",
    heroImageUrl: branding.heroImageUrl || "",
    heroImageFileName: branding.heroImageFileName || "",
    memberPortal: {
      actions: {
        ...DEFAULT_MEMBER_PORTAL_ACTIONS,
        ...portalActions
      }
    }
  };
}

function defaultBusinessHoursForm(timezone = "America/Los_Angeles") {
  const openDay = { closed: false, periods: [{ opens: "09:00", closes: "17:00" }] };
  return {
    timezone,
    status: "active",
    notes: "",
    exceptions: [],
    sun: { latitude: "", longitude: "" },
    weeklyHours: {
      monday: openDay,
      tuesday: openDay,
      wednesday: openDay,
      thursday: openDay,
      friday: openDay,
      saturday: { closed: true, periods: [] },
      sunday: { closed: true, periods: [] }
    }
  };
}

function businessHoursFormFromConfig(config, range) {
  return {
    ...defaultBusinessHoursForm(range?.profile?.timezone || range?.timezone || "America/Los_Angeles"),
    ...(config || {}),
    weeklyHours: {
      ...defaultBusinessHoursForm().weeklyHours,
      ...(config?.weeklyHours || {})
    },
    sun: {
      latitude: config?.sun?.latitude || "",
      longitude: config?.sun?.longitude || ""
    },
    exceptions: config?.exceptions || []
  };
}

function membershipConfigFormFromSummary(summary) {
  const publicSignup = summary?.config?.settings?.reviewPolicy?.publicSignup || {};
  const qualificationCatalog = summary?.config?.settings?.qualificationCatalog || [];
  const guestPolicy = summary?.config?.settings?.guestPolicy || {};
  const chapterMemberVisibility = summary?.config?.settings?.chapterMemberVisibility || {};
  return {
    waiverRequired: publicSignup.waiver?.required !== false,
    waiverInstructions: publicSignup.waiver?.instructions || "",
    waiverExpiresInDays: String(publicSignup.waiver?.expiresInDays ?? 365),
    waiverDocument: publicSignup.waiver?.document || null,
    waiverDocumentHistory: publicSignup.waiver?.documentHistory || [],
    applicationForm: normalizeApplicationFormForClient(publicSignup.applicationForm),
    prequalificationRequired: Boolean(publicSignup.prequalification?.required),
    prequalificationInstructions: publicSignup.prequalification?.instructions || "",
    prequalificationChecklistText: formatChecklistText(publicSignup.prequalification?.checklist || []),
    backgroundCheckRequired: Boolean(publicSignup.backgroundCheck?.required),
    backgroundCheckInstructions: publicSignup.backgroundCheck?.instructions || "",
    backgroundCheckChecklistText: formatChecklistText(publicSignup.backgroundCheck?.checklist || []),
    backgroundCheckProvider: publicSignup.backgroundCheck?.provider || "",
    backgroundCheckEnvironment: publicSignup.backgroundCheck?.checkr?.environment || "test",
    backgroundCheckPackageId: publicSignup.backgroundCheck?.checkr?.packageId || "",
    backgroundCheckPackageName: publicSignup.backgroundCheck?.checkr?.packageName || "",
    backgroundCheckWorkLocationId: publicSignup.backgroundCheck?.checkr?.workLocationId || "",
    backgroundCheckAutoStartAfterPrescreen: publicSignup.backgroundCheck?.checkr?.autoStartAfterPrescreen !== false,
    backgroundCheckMockMode: publicSignup.backgroundCheck?.checkr?.mockMode !== false,
    qualificationCatalogText: qualificationCatalog.map((item) => [
      item.label,
      item.key,
      item.defaultExpiresInDays || "",
      item.description || ""
    ].join(" | ")).join("\n"),
    guestWaiverRequired: guestPolicy.waiverRequired !== false,
    guestWaiverInstructions: guestPolicy.waiverInstructions || "",
    approvalMode: publicSignup.approvalMode || "manual",
    chapterMemberVisibilityPhone: Boolean(chapterMemberVisibility.phone),
    chapterMemberVisibilityAddress: Boolean(chapterMemberVisibility.address),
    chapterMemberVisibilityStatus: Boolean(chapterMemberVisibility.membershipStatus),
    chapterMemberVisibilityJoinDate: Boolean(chapterMemberVisibility.joinDate),
    chapterMemberVisibilityMemberNumber: Boolean(chapterMemberVisibility.memberNumber)
  };
}

function defaultAccessForm() {
  return {
    email: "",
    role: "staff",
    scopeMode: "all",
    chapterIdsText: "",
    applicationScopes: [],
    featurePermissions: defaultAccessFeaturePermissions()
  };
}

function defaultAccessFeaturePermissions() {
  return {
    events: {
      createClasses: true,
      requestClassChanges: true,
      requestClassCancellation: true
    }
  };
}

function normalizeAccessFeaturePermissions(value = {}) {
  return {
    events: {
      createClasses: value.events?.createClasses === undefined ? true : Boolean(value.events.createClasses),
      requestClassChanges: value.events?.requestClassChanges === undefined ? true : Boolean(value.events.requestClassChanges),
      requestClassCancellation: value.events?.requestClassCancellation === undefined ? true : Boolean(value.events.requestClassCancellation)
    }
  };
}

function normalizeAccessApplicationScopes(value = []) {
  const aliases = {
    communications: "notifications",
    messages: "notifications"
  };
  return Array.from(new Set((Array.isArray(value) ? value : [])
    .map((scope) => aliases[String(scope || "").trim()] || String(scope || "").trim())
    .filter(Boolean)));
}

function readPublicAuthContextFallback() {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    return JSON.parse(window.sessionStorage.getItem("rangegrid.publicAuthContext") || "{}");
  } catch {
    return {};
  }
}

function buildAccessGrantPayload(form) {
  const selectedChapterIds = String(form.chapterIdsText || "")
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  const selectedApplicationScopes = Array.isArray(form.applicationScopes) ? form.applicationScopes : [];
  const limitedBySelection = selectedChapterIds.length > 0 || selectedApplicationScopes.length > 0;
  const scopeMode = form.scopeMode === "all" && !limitedBySelection ? "all" : "limited";
  return {
    email: form.email,
    role: form.role,
    scopeMode,
    chapterIds: scopeMode === "limited" ? selectedChapterIds : [],
    applicationScopes: scopeMode === "limited" ? selectedApplicationScopes : [],
    featurePermissions: normalizeAccessFeaturePermissions(form.featurePermissions)
  };
}

function defaultResourceForm() {
  return {
    type: "bay",
    name: "",
    parentResourceId: "",
    laneCount: "0",
    laneNamePrefix: "Lane",
    status: "open",
    targetControlStatus: "firing_allowed",
    targetControlNote: "",
    targetCycleEnabled: false,
    targetCycleFiringMinutes: "45",
    targetCycleCheckMinutes: "15",
    targetCycleStartsAt: "",
    targetCycleNote: "",
    reservable: true,
    shooterCapacity: "1",
    laneCapacity: "0",
    distanceMin: "0",
    distanceMax: "0",
    fixedDistances: "",
    minDurationMinutes: "30",
    maxDurationMinutes: "120",
    slotSizeMinutes: "30",
    bufferMinutes: "0",
    cancellationCutoffHours: "0",
    reservationFeeEnabled: false,
    reservationFeeAmount: "0",
    reservationFeeCurrency: "usd",
    reservationFeeLabel: "Reservation fee",
    requiredQualificationKeys: "",
    availabilityInheritRangeHours: true,
    availabilityStartsOn: "",
    availabilityEndsOn: "",
    availabilityOpenTime: "",
    availabilityCloseTime: "",
    availabilityNotes: "",
    environment: "outdoor",
    assetClass: "",
    serialNumber: "",
    rentalStatus: "not_rental",
    maintenanceDueAt: "",
    maintenancePlanName: "",
    maintenanceIntervalDays: "",
    maintenanceChecklist: "",
    maintenanceAssignedTo: "",
    notes: ""
  };
}

function toDateTimeLocalInput(date) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function defaultEventForm() {
  const startsAt = new Date();
  startsAt.setMinutes(0, 0, 0);
  startsAt.setHours(startsAt.getHours() + 24);
  const endsAt = new Date(startsAt);
  endsAt.setHours(endsAt.getHours() + 2);

  return {
    title: "",
    description: "",
    type: "class",
    status: "draft",
    visibility: "private",
    startsAt: toDateTimeLocalInput(startsAt),
    endsAt: toDateTimeLocalInput(endsAt),
    timezone: "America/Los_Angeles",
    location: "",
    maxAttendees: "12",
    waitlistLimit: "0",
    price: "0",
    currency: "usd",
    publicRegistrationEnabled: true,
    registrationOpensAt: "",
    registrationClosesAt: "",
    registrationInstructions: "",
    instructorLines: "",
    requiredQualificationKeys: "",
    chapterScopeMode: "all",
    chapterScopeIds: "",
    primaryChapterId: "",
    includedForMembers: false,
    resourceIds: []
  };
}

const INTERNAL_DEMO_EVENT_COPY = /(?:chapter lead to owner|owner\/chapter|owner handoff|handoff note|workflow verification|participant notes|reaudit|demo|internal|test event|audit)/i;

function displayProgramTitle(title = "") {
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) {
    return "";
  }
  if (!INTERNAL_DEMO_EVENT_COPY.test(cleanTitle)) {
    return cleanTitle;
  }
  return cleanTitle
    .replace(/^Demo\s+/i, "")
    .replace(/\bHandoff Drill\b/i, "Member Skills Session")
    .replace(/\bLead Reaudit\b/i, "Chapter Practice Session")
    .replace(/\bLead Final\b/i, "Chapter Training Session")
    .replace(/\s+\d{4}-\d{2}-\d{2}(?:\s+\d+)?$/i, "")
    .trim();
}

function displayInstructorName(instructor = {}) {
  const name = String(instructor.name || instructor.email || "").trim();
  if (!name) {
    return "";
  }
  return INTERNAL_DEMO_EVENT_COPY.test(name) ? "Chapter instructor" : name;
}

function defaultEventRegistrationForm() {
  return {
    fullName: "",
    email: "",
    status: "confirmed",
    notes: ""
  };
}

function defaultResourceBlockForm() {
  const startsAt = new Date();
  startsAt.setMinutes(0, 0, 0);
  startsAt.setHours(startsAt.getHours() + 1);
  const endsAt = new Date(startsAt);
  endsAt.setHours(endsAt.getHours() + 2);

  return {
    resourceId: "",
    type: "staff_hold",
    title: "",
    reason: "",
    startsAt: toDateTimeLocalInput(startsAt),
    endsAt: toDateTimeLocalInput(endsAt),
    appliesToChildren: true,
    recurrenceFrequency: "none",
    recurrenceInterval: "1"
  };
}

function defaultAvailabilityForm() {
  const startsAt = new Date();
  startsAt.setMinutes(0, 0, 0);
  startsAt.setHours(startsAt.getHours() + 1);
  const endsAt = new Date(startsAt);
  endsAt.setHours(endsAt.getHours() + 1);

  return {
    resourceId: "",
    startsAt: toDateTimeLocalInput(startsAt),
    endsAt: toDateTimeLocalInput(endsAt)
  };
}

function defaultReservationForm() {
  const startsAt = new Date();
  startsAt.setMinutes(0, 0, 0);
  startsAt.setHours(startsAt.getHours() + 1);
  const endsAt = new Date(startsAt);
  endsAt.setHours(endsAt.getHours() + 1);

  return {
    resourceId: "",
    memberId: "",
    customerName: "",
    contactEmail: "",
    startsAt: toDateTimeLocalInput(startsAt),
    endsAt: toDateTimeLocalInput(endsAt),
    notes: "",
    overrideAvailability: false,
    overrideReason: "",
    source: "owner",
    status: "confirmed"
  };
}

const REWORKED_APPLICATION_MODULE_KEYS = new Set([
  "home",
  "membership",
  "resources",
  "armory",
  "daily-ops",
  "events",
  "chapters",
  "staff-portal",
  "leads",
  "payments",
  "insights",
  "non-members",
  "notifications",
  "communications",
  "reports",
  "accounting",
  "launch",
  "access",
  "audit",
  "range-setup"
]);

function buildViewCopy({ location, routeModuleKey }) {
  if (location.pathname === "/console/onboarding") {
    return {
      title: "Start RangeGrid",
      iconKey: "launch",
      description: "Create your owner workspace and begin launch setup."
    };
  }

  if (location.pathname === "/console/ranges/new") {
    return {
      title: "Start RangeGrid",
      iconKey: "launch",
      description: "Create your owner workspace and begin launch setup."
    };
  }

  if (location.pathname.endsWith("/applications")) {
    return {
      title: "Workspace Menu",
      iconKey: "application-hub",
      description: "Choose what this workspace uses."
    };
  }

  if (location.pathname.endsWith("/settings")) {
    return {
      title: "Range Setup",
      iconKey: "range-setup",
      description: "Configure range profile, operating schedule, and member portal actions."
    };
  }

  if (location.pathname.endsWith("/access")) {
    return {
      title: "Access",
      description: ""
    };
  }

  if (location.pathname.endsWith("/audit")) {
    return {
      title: "Audit",
      description: "Review recent activity for the active range."
    };
  }

  if (location.pathname.endsWith("/notifications")) {
    return {
      title: "Notifications",
      description: "Review message delivery, approval, and audience settings for the active range."
    };
  }

  if (location.pathname.endsWith("/payments")) {
    return {
      title: "Payments",
      iconKey: "payments",
      description: "Review Stripe readiness, payment activity, and receipts."
    };
  }

  if (location.pathname.endsWith("/build-e2e")) {
    return {
      title: "Build/E2E",
      description: "Track real owner-flow tests, deployment checks, and evidence."
    };
  }

  if (routeModuleKey === "membership") {
    return {
      title: "Membership",
      iconKey: "membership",
      description: "Manage membership types, members, and signup readiness."
    };
  }

  if (routeModuleKey === "resources") {
    return {
      title: "Range Facilities",
      iconKey: "resources",
      description: "Manage the physical map of zones, ranges, and lanes."
    };
  }

  if (routeModuleKey === "events") {
    return {
      title: "Programs & Events",
      iconKey: "events",
      description: ""
    };
  }

  if (routeModuleKey === "non-members") {
    return {
      title: "Guest Leads",
      iconKey: "non-members",
      description: ""
    };
  }

  if (routeModuleKey === "chapters") {
    return {
      title: "Chapters",
      iconKey: "chapters",
      description: "Manage local chapters, public directory visibility, and leader assignment."
    };
  }

  if (routeModuleKey === "insights") {
    return {
      title: "Insights",
      iconKey: "insights",
      description: "Review growth, retention, usage, and member journey signals."
    };
  }

  if (routeModuleKey === "staff-portal") {
    return {
      title: "Team Workspace",
      iconKey: "staff-portal",
      description: ""
    };
  }

  if (routeModuleKey === "donations") {
    return {
      title: "Donations",
      description: "Review public donations and record offline contributions."
    };
  }

  if (routeModuleKey === "daily-ops") {
    return {
      title: "Range Operations",
      iconKey: "daily-ops",
      description: "Run line operations, shooter check-in, safety records, equipment readiness, and same-day resource actions."
    };
  }

  if (routeModuleKey === "compliance") {
    return {
      title: "Compliance",
      description: "Record incident, safety, and compliance follow-up against member records."
    };
  }

  if (routeModuleKey === "equipment") {
    return {
      title: "Maintenance Hub",
      iconKey: "maintenance",
      description: "Track asset maintenance, inspection due dates, and equipment readiness history."
    };
  }

  if (routeModuleKey === "armory") {
    return {
      title: "Armory",
      iconKey: "armory",
      description: "Track range-owned firearms, checkout, return, round counts, and maintenance readiness."
    };
  }

  if (routeModuleKey === "launch") {
    return {
      title: "Launch",
      description: "Work through publish readiness with guided launch checkpoints."
    };
  }

  if (routeModuleKey === "accounting") {
    return {
      title: "Accounting",
      description: "Payment reconciliation will open here after payment setup is ready."
    };
  }

  if (routeModuleKey === "reports") {
    return {
      title: "Reports",
      description: "Review operational health, membership flow, facility use, programs, and exports."
    };
  }

  if (routeModuleKey === "communications") {
    return {
      title: "Communications",
      description: "Member broadcasts will open here after delivery setup is ready."
    };
  }

  if (routeModuleKey === "leads") {
    return {
      title: "Leads",
      iconKey: "leads",
      description: "Review public update requests, launch interest, and membership lead sources."
    };
  }

  if (location.pathname === "/console/platform") {
    return {
      title: "Platform",
      description: "Operate across ranges before drilling into a tenant console."
    };
  }

  return {
    title: "Dashboard",
    iconKey: "console-home",
    description: "Monitor daily operations, member flow, range readiness, and launch blockers."
  };
}

function teamWorkspaceViewDescription() {
  return "";
}

const APPLICATION_WORKSPACE_TEMPLATES = new Map([
  ["home", ["physical_range", "chapter_organization", "training_business"]],
  ["membership", ["physical_range", "chapter_organization", "training_business"]],
  ["events", ["physical_range", "chapter_organization", "training_business"]],
  ["attendance", ["physical_range", "chapter_organization", "training_business"]],
  ["insights", ["physical_range", "chapter_organization", "training_business"]],
  ["payments", ["physical_range", "chapter_organization", "training_business"]],
  ["leads", ["physical_range", "chapter_organization", "training_business"]],
  ["non-members", ["chapter_organization"]],
  ["notifications", ["physical_range", "chapter_organization", "training_business"]],
  ["communications", ["physical_range", "chapter_organization", "training_business"]],
  ["reports", ["physical_range", "chapter_organization", "training_business"]],
  ["accounting", ["physical_range", "chapter_organization", "training_business"]],
  ["launch", ["physical_range", "chapter_organization", "training_business"]],
  ["access", ["physical_range", "chapter_organization", "training_business"]],
  ["audit", ["physical_range", "chapter_organization", "training_business"]],
  ["range-setup", ["physical_range", "chapter_organization", "training_business"]],
  ["daily-ops", ["physical_range"]],
  ["resources", ["physical_range"]],
  ["armory", ["physical_range"]],
  ["chapters", ["chapter_organization"]],
  ["staff-portal", ["chapter_organization"]]
]);

function workspaceTemplateKeyForRange(range = {}) {
  return String(
    range?.config?.templateKey ||
    range?.creationTemplate?.templateKey ||
    range?.templateKey ||
    "physical_range"
  ).trim().toLowerCase();
}

function applicationSupportsWorkspace(application, templateKey) {
  const key = application.applicationId || application.moduleKey || application.key || application.iconKey;
  const supportedTemplates = APPLICATION_WORKSPACE_TEMPLATES.get(key) || [];
  return supportedTemplates.length === 0 || supportedTemplates.includes(templateKey || "physical_range");
}

function rangeGridPublicWebsiteLive(range = {}) {
  const publicDomainStatus = String(range?.publicDomain?.active?.status || "").trim().toLowerCase();
  const publicWebsiteStatus = String(range?.publicWebsite?.status || range?.config?.publicWebsite?.status || "").trim().toLowerCase();
  return publicDomainStatus === "live" || publicWebsiteStatus === "live";
}

function applicationAvailableForWorkspace(application, range, templateKey) {
  const key = application.applicationId || application.moduleKey || application.key || application.iconKey;
  if (!applicationSupportsWorkspace(application, templateKey)) {
    return false;
  }
  if (key === "leads") {
    return rangeGridPublicWebsiteLive(range);
  }
  return true;
}

function selectedWorkspaceVocabulary(selectedRange = {}, applications = []) {
  const vocabulary =
    selectedRange?.config?.vocabulary ||
    selectedRange?.creationTemplate?.vocabulary ||
    selectedRange?.vocabulary ||
    {};
  const enabledApplicationKeys = new Set((applications || [])
    .filter((application) => application.status === "enabled")
    .flatMap((application) => [application.moduleKey, application.applicationId])
    .map((key) => String(key || "").trim())
    .filter(Boolean));
  const templateKey = String(
    selectedRange?.config?.templateKey ||
    selectedRange?.creationTemplate?.templateKey ||
    selectedRange?.templateKey ||
    ""
  ).toLowerCase();
  const chapterOrganization =
    templateKey === "chapter_organization" ||
    String(vocabulary.unit || "").toLowerCase() === "chapter" ||
    enabledApplicationKeys.has("chapters");
  const workspace = vocabulary.workspace || (chapterOrganization ? "Organization" : "Range");
  return {
    workspace,
    workspacePlural: vocabulary.workspacePlural || `${workspace}s`,
    operationsLabel: chapterOrganization ? "Operations" : "Range Operations",
    facilitiesLabel: chapterOrganization ? "Facilities" : "Range Facilities",
    setupLabel: chapterOrganization ? "Organization Setup" : "Range Setup"
  };
}

function linesFromValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (item && typeof item === "object") {
          return item.label || item.name || item.title || item.key || "";
        }
        return "";
      })
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .join("\n");
  }
  if (typeof value === "string") {
    return value;
  }
  return "";
}

function linesToArray(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function stableKeyFromLabel(label, fallback) {
  const key = String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return key || fallback;
}

function benefitItemsFromText(value) {
  return linesToArray(value).map((label, index) => ({
    key: stableKeyFromLabel(label, `benefit_${index + 1}`),
    label
  }));
}

function eventChapterIdsFromRecord(item = {}) {
  const chapterIds = Array.isArray(item.chapterIds)
    ? item.chapterIds
    : item.chapterId
      ? [item.chapterId]
      : [];
  return chapterIds
    .map((chapterId) => String(chapterId || "").trim())
    .filter(Boolean);
}

function EmptyPanel({ actionLabel, message, onAction, title }) {
  return (
    <section className="panel page-panel">
      <div className="empty-panel">
        <strong>{title}</strong>
        <span>{message}</span>
        {actionLabel && onAction && (
          <button type="button" className="empty-panel-action" onClick={onAction}>
            {actionLabel}
          </button>
        )}
      </div>
    </section>
  );
}

const ACTIVE_RANGE_STORAGE_KEY = "rangegrid.activeRangeId";
const RANGEGRID_PLATFORM_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "rangegrid.app",
  "www.rangegrid.app",
  "rangegrid-web-908027418615-us-east-1.s3-website-us-east-1.amazonaws.com"
]);
const TRANSIENT_BACKGROUND_ERROR_PATTERNS = [
  /temporarily busy/i,
  /receiving too many requests/i,
  /took too long/i,
  /could not reach one of its services/i
];

function isTransientBackgroundError(message = "") {
  return TRANSIENT_BACKGROUND_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function isChapterOrganizationRange(range = {}) {
  return (
    range?.config?.templateKey === "chapter_organization" ||
    range?.creationTemplate?.templateKey === "chapter_organization" ||
    range?.templateKey === "chapter_organization" ||
    String(range?.config?.vocabulary?.unit || range?.creationTemplate?.vocabulary?.unit || range?.vocabulary?.unit || "").toLowerCase() === "chapter"
  );
}

function preferredActiveRangeId(ranges = [], currentRangeId = "") {
  const currentRange = ranges.find((range) => range.rangeId === currentRangeId);
  const chapterOrganization = ranges.find(isChapterOrganizationRange);
  if (!currentRange) {
    return chapterOrganization?.rangeId || ranges[0]?.rangeId || "";
  }
  if (isChapterOrganizationRange(currentRange)) {
    return currentRange.rangeId;
  }
  return chapterOrganization?.rangeId || currentRange.rangeId;
}

function initialActiveRangeId() {
  if (typeof window === "undefined") {
    return "";
  }
  try {
    return window.localStorage.getItem(ACTIVE_RANGE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function currentBrowserHostname() {
  if (typeof window === "undefined") {
    return "";
  }
  return String(window.location.hostname || "").toLowerCase();
}

function isRangeGridPlatformHost(hostname = "") {
  const normalized = String(hostname || "").toLowerCase();
  return RANGEGRID_PLATFORM_HOSTS.has(normalized) || normalized.endsWith(".s3-website-us-east-1.amazonaws.com");
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState({
    status: DEV_USER_ID ? "authenticated" : "checking",
    user: DEV_USER_ID ? { username: DEV_USER_ID, userId: DEV_USER_ID } : null,
    attributes: DEV_USER_ID ? { email: `${DEV_USER_ID}@dev.rangegrid.local` } : null
  });
  const [ranges, setRanges] = useState([]);
  const [activeRangeId, setActiveRangeId] = useState(initialActiveRangeId);
  const [form, setForm] = useState(defaultCreateRangeForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [savingBusinessHours, setSavingBusinessHours] = useState(false);
  const [profileForm, setProfileForm] = useState(rangeProfileFormFromRange(null));
  const [brandingForm, setBrandingForm] = useState(rangeBrandingFormFromRange(null));
  const [businessHoursForm, setBusinessHoursForm] = useState(defaultBusinessHoursForm());
  const [businessHoursLoading, setBusinessHoursLoading] = useState(false);
  const [rangeReadiness, setRangeReadiness] = useState(null);
  const [rangeReadinessLoading, setRangeReadinessLoading] = useState(false);
  const [error, setError] = useState("");
  const [applications, setApplications] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [rangeApplications, setRangeApplications] = useState([]);
  const [rangeApplicationsLoading, setRangeApplicationsLoading] = useState(false);
  const [enablingApplicationId, setEnablingApplicationId] = useState("");
  const [disablingApplicationId, setDisablingApplicationId] = useState("");
  const [updatingProductionMenuKey, setUpdatingProductionMenuKey] = useState("");
  const [rangeAccessRecords, setRangeAccessRecords] = useState([]);
  const [rangeAccessLoading, setRangeAccessLoading] = useState(false);
  const [rangeAccessFilters, setRangeAccessFilters] = useState(defaultAccessFilters);
  const [exportingRangeAccess, setExportingRangeAccess] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilters, setAuditFilters] = useState(defaultAuditFilters);
  const [exportingAudit, setExportingAudit] = useState(false);
  const [operationNotes, setOperationNotes] = useState([]);
  const [operationNotesLoading, setOperationNotesLoading] = useState(false);
  const [savingOperationNote, setSavingOperationNote] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationSummary, setNotificationSummary] = useState(null);
  const [notificationPreferences, setNotificationPreferences] = useState(null);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationFilters, setNotificationFilters] = useState(defaultNotificationFilters);
  const [exportingNotifications, setExportingNotifications] = useState(false);
  const [savingNotificationPreferences, setSavingNotificationPreferences] = useState(false);
  const [updatingSenderEmail, setUpdatingSenderEmail] = useState(false);
  const [sendingGuestLeadEmailId, setSendingGuestLeadEmailId] = useState("");
  const [updatingNotificationId, setUpdatingNotificationId] = useState("");
  const [queueingCustomNotification, setQueueingCustomNotification] = useState(false);
  const [grantingAccess, setGrantingAccess] = useState(false);
  const [updatingAccessUserId, setUpdatingAccessUserId] = useState("");
  const [accessForm, setAccessForm] = useState(defaultAccessForm);
  const [membershipSummary, setMembershipSummary] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentOverviewEvents, setPaymentOverviewEvents] = useState(null);
  const [paymentOverviewIssues, setPaymentOverviewIssues] = useState(null);
  const [paymentOverviewReceipts, setPaymentOverviewReceipts] = useState(null);
  const [paymentOverviewLoading, setPaymentOverviewLoading] = useState(false);
  const [exportingPaymentEvents, setExportingPaymentEvents] = useState(false);
  const [exportingPaymentReconciliation, setExportingPaymentReconciliation] = useState(false);
  const [exportingPaymentReceipts, setExportingPaymentReceipts] = useState(false);
  const [refundingPaymentReceiptId, setRefundingPaymentReceiptId] = useState("");
  const [reconcilingPaymentReceiptId, setReconcilingPaymentReceiptId] = useState("");
  const [syncingPaymentReconciliation, setSyncingPaymentReconciliation] = useState(false);
  const [closingPaymentReconciliation, setClosingPaymentReconciliation] = useState(false);
  const [paymentEventFilters, setPaymentEventFilters] = useState(defaultPaymentEventFilters);
  const [paymentReceiptFilters, setPaymentReceiptFilters] = useState(defaultPaymentReceiptFilters);
  const [startingStripeConnect, setStartingStripeConnect] = useState(false);
  const [stripeConnectSession, setStripeConnectSession] = useState(null);
  const stripeConnectSessionRef = useRef(null);
  const stripeConnectSessionRangeRef = useRef("");
  const [refreshingStripeConnect, setRefreshingStripeConnect] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberFilters, setMemberFilters] = useState(defaultMemberFilters);
  const [chapters, setChapters] = useState([]);
  const [publicChapterOptions, setPublicChapterOptions] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");
  const [serviceSearchFocused, setServiceSearchFocused] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [exportingMembers, setExportingMembers] = useState(false);
  const [exportingMemberFlags, setExportingMemberFlags] = useState(false);
  const [exportingMemberGuestPasses, setExportingMemberGuestPasses] = useState(false);
  const [exportingMemberHousehold, setExportingMemberHousehold] = useState(false);
  const [exportingMemberQualifications, setExportingMemberQualifications] = useState(false);
  const [memberImportPreview, setMemberImportPreview] = useState(null);
  const [memberImportLoading, setMemberImportLoading] = useState(false);
  const [memberImportCommitting, setMemberImportCommitting] = useState(false);
  const [memberDetail, setMemberDetail] = useState(null);
  const [memberDetailLoading, setMemberDetailLoading] = useState(false);
  const [memberTimeline, setMemberTimeline] = useState(null);
  const [memberTimelineLoading, setMemberTimelineLoading] = useState(false);
  const [memberPaymentEvents, setMemberPaymentEvents] = useState(null);
  const [memberPaymentEventsLoading, setMemberPaymentEventsLoading] = useState(false);
  const [memberPaymentReceipts, setMemberPaymentReceipts] = useState(null);
  const [memberPaymentReceiptsLoading, setMemberPaymentReceiptsLoading] = useState(false);
  const [selectedMemberDetailId, setSelectedMemberDetailId] = useState("");
  const [savingMember, setSavingMember] = useState(false);
  const [changingMemberPlanId, setChangingMemberPlanId] = useState("");
  const [addingHouseholdMemberId, setAddingHouseholdMemberId] = useState("");
  const [addingMemberDocumentId, setAddingMemberDocumentId] = useState("");
  const [addingMemberFlagId, setAddingMemberFlagId] = useState("");
  const [addingMemberQualificationId, setAddingMemberQualificationId] = useState("");
  const [renewingMemberId, setRenewingMemberId] = useState("");
  const [updatingGuestPassMemberId, setUpdatingGuestPassMemberId] = useState("");
  const [updatingMemberProfileId, setUpdatingMemberProfileId] = useState("");
  const [updatingMemberStatusId, setUpdatingMemberStatusId] = useState("");
  const [updatingMemberId, setUpdatingMemberId] = useState("");
  const [createMemberForm, setCreateMemberForm] = useState({
    fullName: "",
    email: "",
    membershipTypeId: "standard",
    homeChapterId: "",
    status: "active"
  });
  const [savingMembershipType, setSavingMembershipType] = useState(false);
  const [archivingMembershipTypeId, setArchivingMembershipTypeId] = useState("");
  const [editingMembershipTypeId, setEditingMembershipTypeId] = useState("");
  const [membershipTypeForm, setMembershipTypeForm] = useState(defaultMembershipTypeForm);
  const [savingMembershipConfig, setSavingMembershipConfig] = useState(false);
  const [uploadingWaiver, setUploadingWaiver] = useState(false);
  const [membershipConfigForm, setMembershipConfigForm] = useState(defaultMembershipConfigForm);
  const [resourceSummary, setResourceSummary] = useState(null);
  const [resources, setResources] = useState([]);
  const resourcesRef = useRef([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourceDetail, setResourceDetail] = useState(null);
  const [resourceDetailLoading, setResourceDetailLoading] = useState(false);
  const [savingResource, setSavingResource] = useState(false);
  const [updatingResourceId, setUpdatingResourceId] = useState("");
  const [resourceFilters, setResourceFilters] = useState(defaultResourceFilters);
  const [exportingResources, setExportingResources] = useState(false);
  const [resourceForm, setResourceForm] = useState(defaultResourceForm);
  const [resourceBlocks, setResourceBlocks] = useState([]);
  const [resourceBlocksSummary, setResourceBlocksSummary] = useState(null);
  const [savingResourceBlock, setSavingResourceBlock] = useState(false);
  const [exportingResourceBlocks, setExportingResourceBlocks] = useState(false);
  const [updatingResourceBlockId, setUpdatingResourceBlockId] = useState("");
  const [resourceBlockForm, setResourceBlockForm] = useState(defaultResourceBlockForm);
  const [availabilityForm, setAvailabilityForm] = useState(defaultAvailabilityForm);
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [resourceReservations, setResourceReservations] = useState([]);
  const [resourceReservationsSummary, setResourceReservationsSummary] = useState(null);
  const [resourceReservationConflicts, setResourceReservationConflicts] = useState(null);
  const [checkingReservationConflicts, setCheckingReservationConflicts] = useState(false);
  const [reservationForm, setReservationForm] = useState(defaultReservationForm);
  const [savingReservation, setSavingReservation] = useState(false);
  const [updatingReservationId, setUpdatingReservationId] = useState("");
  const [exportingResourceReservations, setExportingResourceReservations] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsSummary, setEventsSummary] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [updatingEventId, setUpdatingEventId] = useState("");
  const [eventFilters, setEventFilters] = useState(defaultEventFilters);
  const [exportingEvents, setExportingEvents] = useState(false);
  const [eventForm, setEventForm] = useState(defaultEventForm);
  const [editingEventId, setEditingEventId] = useState("");
  const [selectedEventId, setSelectedEventId] = useState("");
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [eventRegistrationsSummary, setEventRegistrationsSummary] = useState(null);
  const [eventRegistrationsLoading, setEventRegistrationsLoading] = useState(false);
  const [eventRegistrationFilters, setEventRegistrationFilters] = useState(defaultEventRegistrationFilters);
  const [savingEventRegistration, setSavingEventRegistration] = useState(false);
  const [updatingEventRegistrationKey, setUpdatingEventRegistrationKey] = useState("");
  const [exportingEventRegistrations, setExportingEventRegistrations] = useState(false);
  const [eventRegistrationForm, setEventRegistrationForm] = useState(defaultEventRegistrationForm);
  const [eventAttendance, setEventAttendance] = useState([]);
  const [eventAttendanceSummary, setEventAttendanceSummary] = useState(null);
  const [eventAttendanceLoading, setEventAttendanceLoading] = useState(false);
  const [eventAttendanceFilters, setEventAttendanceFilters] = useState(defaultEventAttendanceFilters);
  const [recordingAttendanceKey, setRecordingAttendanceKey] = useState("");
  const [exportingEventAttendance, setExportingEventAttendance] = useState(false);
  const [nonMemberAttendees, setNonMemberAttendees] = useState([]);
  const [nonMemberAttendeesSummary, setNonMemberAttendeesSummary] = useState(null);
  const [nonMemberAttendeesLoading, setNonMemberAttendeesLoading] = useState(false);
  const [donations, setDonations] = useState([]);
  const [donationsSummary, setDonationsSummary] = useState(null);
  const [donationSettings, setDonationSettings] = useState(null);
  const [donationsLoading, setDonationsLoading] = useState(false);
  const [donationForm, setDonationForm] = useState(defaultDonationForm);
  const [donationFilters, setDonationFilters] = useState(defaultDonationFilters);
  const [donationSettingsForm, setDonationSettingsForm] = useState(defaultDonationSettingsForm);
  const [exportingDonations, setExportingDonations] = useState(false);
  const [loadingDonationReceiptId, setLoadingDonationReceiptId] = useState("");
  const [recordingDonationRefundId, setRecordingDonationRefundId] = useState("");
  const [savingDonation, setSavingDonation] = useState(false);
  const [savingDonationSettings, setSavingDonationSettings] = useState(false);
  const [publicAuthPrompt, setPublicAuthPrompt] = useState(false);
  const [publicDomainContext, setPublicDomainContext] = useState(null);
  const [publicDomainLoading, setPublicDomainLoading] = useState(false);
  const [publicDomainError, setPublicDomainError] = useState("");
  const previousConsolePathRef = useRef("");

  const currentHostname = currentBrowserHostname();
  const isCustomPublicHost = Boolean(
    currentHostname &&
    !isRangeGridPlatformHost(currentHostname) &&
    /^\/(?:membership|about)?$/.test(location.pathname)
  );
  const isPlatformLandingRoute = location.pathname === "/" && isRangeGridPlatformHost(currentHostname);
  const isPublicRangeRoute = /^\/r\/[^/]+(?:\/(?:membership|about|unsubscribe))?$/.test(location.pathname) || isCustomPublicHost;
  const isMemberConsoleRoute = /^\/member\/r\/[^/]+$/.test(location.pathname);
  const isLegacyMemberPortalRoute = location.pathname === "/member/portal";
  const publicDonationReceiptMatch = location.pathname.match(/^\/r\/([^/]+)\/donations\/([^/]+)\/receipt$/);
  const publicReservationCompleteMatch = location.pathname.match(/^\/r\/([^/]+)\/reservations\/([^/]+)\/complete$/);
  const publicEventRegistrationCompleteMatch = location.pathname.match(/^\/r\/([^/]+)\/events\/([^/]+)\/registrations\/([^/]+)\/complete$/);
  const isPublicDonationReceiptRoute = Boolean(publicDonationReceiptMatch);
  const isPublicReservationCompleteRoute = Boolean(publicReservationCompleteMatch);
  const isPublicEventRegistrationCompleteRoute = Boolean(publicEventRegistrationCompleteMatch);
  const publicRangeSlug = useMemo(() => {
    if (isCustomPublicHost) {
      return publicDomainContext?.range?.slug || "";
    }
    const match = location.pathname.match(/^\/(?:member\/)?r\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }, [isCustomPublicHost, location.pathname, publicDomainContext?.range?.slug]);
  const activePublicRangeForAuth = publicRangeSlug
    ? ranges.find((range) => range.slug === publicRangeSlug)
    : null;
  const publicAuthFallback = readPublicAuthContextFallback();
  const publicAuthContextName = activePublicRangeForAuth?.profile?.displayName || activePublicRangeForAuth?.name || publicAuthFallback.name || "";
  const publicAuthContextShortName = activePublicRangeForAuth?.branding?.shortName || publicAuthFallback.shortName || "";
  const publicAuthContextLabel = new URLSearchParams(location.search).get("signup_plan")
    ? "Membership signup"
    : "Member access";

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousConsolePathRef.current;
    previousConsolePathRef.current = currentPath;
    if (
      previousPath === currentPath ||
      location.hash ||
      !/^\/(?:console|workspace)(?:\/|$)/.test(currentPath) ||
      typeof window === "undefined"
    ) {
      return;
    }

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [location.hash, location.pathname]);

  useEffect(() => {
    if (!isCustomPublicHost || !currentHostname) {
      setPublicDomainContext(null);
      setPublicDomainLoading(false);
      setPublicDomainError("");
      return undefined;
    }
    let cancelled = false;
    setPublicDomainLoading(true);
    setPublicDomainError("");
    getPublicDomainContext(currentHostname)
      .then((result) => {
        if (!cancelled) {
          setPublicDomainContext(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPublicDomainContext(null);
          setPublicDomainError(err.message || "RangeGrid could not find this website domain.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPublicDomainLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentHostname, isCustomPublicHost]);

  const publicDonationReceiptContext = useMemo(() => {
    const match = location.pathname.match(/^\/r\/([^/]+)\/donations\/([^/]+)\/receipt$/);
    return match
      ? {
          slug: decodeURIComponent(match[1]),
          donationId: decodeURIComponent(match[2])
        }
      : { slug: "", donationId: "" };
  }, [location.pathname]);
  const publicReservationCompleteContext = useMemo(() => {
    const match = location.pathname.match(/^\/r\/([^/]+)\/reservations\/([^/]+)\/complete$/);
    return match
      ? {
          slug: decodeURIComponent(match[1]),
          reservationId: decodeURIComponent(match[2])
        }
      : { slug: "", reservationId: "" };
  }, [location.pathname]);
  const publicEventRegistrationCompleteContext = useMemo(() => {
    const match = location.pathname.match(/^\/r\/([^/]+)\/events\/([^/]+)\/registrations\/([^/]+)\/complete$/);
    return match
      ? {
          slug: decodeURIComponent(match[1]),
          eventId: decodeURIComponent(match[2]),
          registrationId: decodeURIComponent(match[3])
        }
      : { slug: "", eventId: "", registrationId: "" };
  }, [location.pathname]);
  const suggestedSlug = useMemo(() => slugify(form.name), [form.name]);
  const isTeamWorkspaceRoute = /^\/workspace\/ranges\/[^/]+(?:\/apps\/[^/]+)?$/.test(location.pathname);
  const routeWorkspaceApp = useMemo(() => {
    const match = location.pathname.match(/^\/workspace\/ranges\/[^/]+\/apps\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }, [location.pathname]);
  const isWorkspaceProgramsRoute = routeWorkspaceApp === "classes" || routeWorkspaceApp === "events";
  const isWorkspaceNotificationsRoute = routeWorkspaceApp === "notifications";
  const routeWorkspaceModule = routeWorkspaceApp === "members"
    ? "members"
    : isWorkspaceProgramsRoute
      ? "programs"
      : isWorkspaceNotificationsRoute
        ? "notifications"
        : "home";
  const routeRangeId = useMemo(() => {
    const match = location.pathname.match(/^\/(?:console|workspace)\/ranges\/([^/]+)/);
    if (!match || match[1] === "new") {
      return "";
    }
    return decodeURIComponent(match[1]);
  }, [location.pathname]);
  const routeModuleKey = useMemo(() => {
    if (/^\/workspace\/ranges\/[^/]+(?:\/apps\/[^/]+)?$/.test(location.pathname)) {
      return "staff-portal";
    }
    const match = location.pathname.match(/^\/console\/ranges\/[^/]+\/apps\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }, [location.pathname]);
  const isRangeDashboardRoute = useMemo(() => (
    location.pathname === "/console" || /^\/console\/ranges\/[^/]+$/.test(location.pathname)
  ), [location.pathname]);
  const isReportsWorkspace = routeModuleKey === "reports";
  const isAccountingWorkspace = routeModuleKey === "accounting";
  const isCommunicationsWorkspace = routeModuleKey === "communications";
  const routeResourceId = useMemo(() => {
    const match = location.pathname.match(/^\/console\/ranges\/[^/]+\/apps\/resources\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }, [location.pathname]);
  const selectedRangeId = routeRangeId || activeRangeId;
  const matchedSelectedRange = ranges.find((range) => range.rangeId === selectedRangeId) || null;
  const selectedRange = routeRangeId ? matchedSelectedRange : (matchedSelectedRange || ranges[0] || null);
  const selectedRangePath = selectedRange
    ? `${isTeamWorkspaceRoute ? "/workspace" : "/console"}/ranges/${selectedRange.rangeId}`
    : (isTeamWorkspaceRoute ? "/workspace" : "/console");
  const workspaceVocabulary = selectedWorkspaceVocabulary(selectedRange || {}, rangeApplications);
  const isAuthenticated = auth.status === "authenticated";
  const rangeRouteNotFound = Boolean(routeRangeId) && !loading && !selectedRange;
  const selectedLifecycleStatus = selectedRange?.lifecycle?.status || selectedRange?.status || "active";
  const selectedRangeOperational = ["trial", "active"].includes(selectedLifecycleStatus);
  const ownerLabel =
    auth.attributes?.email ||
    selectedRange?.access?.email ||
    auth.attributes?.preferred_username ||
    auth.user?.username ||
    auth.user?.userId ||
    "Signed in";
  const chapterOrganizationWorkspace = (
    selectedRange?.config?.templateKey === "chapter_organization" ||
    selectedRange?.creationTemplate?.templateKey === "chapter_organization" ||
    String(selectedRange?.config?.vocabulary?.unit || selectedRange?.creationTemplate?.vocabulary?.unit || "").toLowerCase() === "chapter"
  );
  const selectedWorkspaceTemplateKey = workspaceTemplateKeyForRange(selectedRange || {});
  const membershipEnabled = rangeApplications.some(
    (item) => item.moduleKey === "membership" && item.status === "enabled"
  );
  const isRangeApplicationEnabled = (moduleKey) => rangeApplications.some(
    (item) => item.moduleKey === moduleKey && item.status === "enabled"
  );
  const resourcesEnabled = rangeApplications.some(
    (item) => item.moduleKey === "resources" && item.status === "enabled"
  );
  const eventsEnabled = rangeApplications.some(
    (item) => item.moduleKey === "events" && item.status === "enabled"
  );
  const insightsEnabled = rangeApplications.some(
    (item) => item.moduleKey === "insights" && item.status === "enabled"
  );
  const leadsAvailable = rangeGridPublicWebsiteLive(selectedRange || {});
  const leadsEnabled = leadsAvailable && rangeApplications.some(
    (item) => item.moduleKey === "leads" && item.status === "enabled"
  );
  const applicationGateLoading = loading || rangeApplicationsLoading;
  const chaptersApplicationEnabled = rangeApplications.some(
    (item) => item.moduleKey === "chapters" && item.status === "enabled"
  );
  const chaptersEnabled = Boolean(chapterOrganizationWorkspace && chaptersApplicationEnabled);
  const staffPortalEnabled = rangeApplications.some(
    (item) => item.moduleKey === "staff-portal" && item.status === "enabled"
  );
  const teamWorkspaceAvailable = Boolean(staffPortalEnabled && chaptersEnabled && chapterOrganizationWorkspace);
  const donationsEnabled = rangeApplications.some(
    (item) => item.moduleKey === "donations" && item.status === "enabled"
  );
  const eventChapterOptions = useMemo(() => {
    const byId = new Map();
    chapters.forEach((chapter) => {
      if (chapter?.chapterId) {
        byId.set(chapter.chapterId, chapter);
      }
    });
    publicChapterOptions.forEach((chapter) => {
      if (chapter?.chapterId && !byId.has(chapter.chapterId)) {
        byId.set(chapter.chapterId, chapter);
      }
    });
    events.forEach((event) => {
      const snapshot = event?.chapterSnapshot || {};
      eventChapterIdsFromRecord(event).forEach((chapterId) => {
        if (!byId.has(chapterId)) {
          byId.set(chapterId, {
            chapterId,
            name: snapshot.chapterId === chapterId ? snapshot.name : snapshot.name || "Chapter",
            city: snapshot.city,
            region: snapshot.region,
            slug: snapshot.slug,
            meetingArea: event.location || snapshot.meetingArea || ""
          });
        }
      });
    });
    return Array.from(byId.values())
      .sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")));
  }, [chapters, events, publicChapterOptions]);
  const selectedAccessPermissions = new Set(selectedRange?.access?.permissions || []);
  const selectedAccessFeaturePermissions = normalizeAccessFeaturePermissions(selectedRange?.access?.featurePermissions || {});
  const canManageAccess = selectedAccessPermissions.has("range:manage_access");
  const canReadMembers = selectedAccessPermissions.has("members:manage");
  const canReadEvents = selectedAccessPermissions.has("events:manage") || selectedAccessPermissions.has("attendance:manage");
  const workspaceOnlyAccess = Boolean(selectedRange) && !canManageAccess;
  const selectedAccessChapterIds = useMemo(() => (
    Array.isArray(selectedRange?.access?.chapterIds)
      ? selectedRange.access.chapterIds.filter(Boolean)
      : []
  ), [selectedRange?.access?.chapterIds]);
  const selectedAccessApplicationScopes = useMemo(() => (
    Array.isArray(selectedRange?.access?.applicationScopes)
      ? normalizeAccessApplicationScopes(selectedRange.access.applicationScopes)
      : []
  ), [selectedRange?.access?.applicationScopes]);
  function workspaceAppAllowed(scopeKey) {
    return !workspaceOnlyAccess || selectedAccessApplicationScopes.length === 0 || selectedAccessApplicationScopes.includes(scopeKey);
  }
  const workspaceEventChapterOptions = useMemo(() => {
    if (!workspaceOnlyAccess || selectedAccessChapterIds.length === 0) {
      return eventChapterOptions;
    }
    const byId = new Map(eventChapterOptions.map((chapter) => [chapter.chapterId, chapter]));
    return selectedAccessChapterIds.map((chapterId) => byId.get(chapterId) || { chapterId, name: "Assigned chapter" });
  }, [eventChapterOptions, selectedAccessChapterIds, workspaceOnlyAccess]);
  const workspaceRosterChapterId = workspaceEventChapterOptions[0]?.chapterId || "";
  const workspaceScopedNotifications = useMemo(() => {
    if (!workspaceOnlyAccess || selectedAccessChapterIds.length === 0) {
      return notifications;
    }
    const scopedIds = new Set(selectedAccessChapterIds);
    return notifications.filter((notification) => {
      const chapterId = notification?.related?.chapterId || notification?.metadata?.chapterId || "";
      const audience = String(notification?.audience || "").toLowerCase();
      return (
        audience === "chapter_leader" ||
        audience === "chapter_leader_test" ||
        (chapterId && scopedIds.has(chapterId))
      );
    });
  }, [notifications, selectedAccessChapterIds, workspaceOnlyAccess]);

  async function refreshAuth() {
    if (DEV_USER_ID.trim()) {
      setAuth({
        status: "authenticated",
        user: { username: DEV_USER_ID, userId: DEV_USER_ID },
        attributes: { email: `${DEV_USER_ID}@dev.rangegrid.local` }
      });
      return;
    }

    try {
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes().catch(() => ({}));
      setAuth({ status: "authenticated", user: currentUser, attributes });
    } catch {
      setAuth({ status: "signed-out", user: null, attributes: null });
    }
  }

  async function handlePublicAuthenticated() {
    await refreshAuth();
    setPublicAuthPrompt(false);
    const params = new URLSearchParams(location.search);
    const memberIntent = params.get("member_intent");
    if (isPublicRangeRoute && publicRangeSlug && (
      params.get("checkout") === "success" ||
      params.get("signup_plan") ||
      memberIntent
    )) {
      const hashByIntent = {
        events: "#events",
        reservations: "#reservations",
        membership: "#member-status",
        payments: "#payments",
        profile: "#profile"
      };
      const hash = location.hash || hashByIntent[memberIntent] || "#member-dashboard";
      navigate(`/member/r/${encodeURIComponent(publicRangeSlug)}${location.search}${hash}`, { replace: true });
    }
  }

  function handlePublicRequireAuth(intent = {}) {
    if (isPublicRangeRoute && publicRangeSlug) {
      const params = new URLSearchParams(location.search);
      if (intent.signupPlanId) {
        params.set("signup_plan", intent.signupPlanId);
      }
      if (intent.chapterId) {
        params.set("chapterId", intent.chapterId);
      }
      if (intent.memberIntent) {
        params.set("member_intent", intent.memberIntent);
      }
      if (intent.eventId) {
        params.set("event_id", intent.eventId);
      }
      if (intent.resourceId) {
        params.set("resource_id", intent.resourceId);
      }
      if (intent.contextName || intent.contextShortName) {
        try {
          window.sessionStorage.setItem("rangegrid.publicAuthContext", JSON.stringify({
            name: intent.contextName || "",
            shortName: intent.contextShortName || ""
          }));
        } catch {
          // Session storage is only a convenience for public auth copy.
        }
      }
      const hash = intent.hash || location.hash || (intent.signupPlanId ? "#membership" : "#member-dashboard");
      navigate(`/r/${encodeURIComponent(publicRangeSlug)}?${params.toString()}${hash}`, { replace: true });
    }
    setPublicAuthPrompt(true);
  }

  useEffect(() => {
    refreshAuth();
  }, []);

  useEffect(() => {
    resourcesRef.current = resources;
  }, [resources]);

  useEffect(() => {
    if (routeRangeId && routeRangeId !== activeRangeId) {
      setActiveRangeId(routeRangeId);
    }
  }, [activeRangeId, routeRangeId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      if (activeRangeId) {
        window.localStorage.setItem(ACTIVE_RANGE_STORAGE_KEY, activeRangeId);
      } else {
        window.localStorage.removeItem(ACTIVE_RANGE_STORAGE_KEY);
      }
    } catch {
      // Remembering the active range should never block the console.
    }
  }, [activeRangeId]);

  useEffect(() => {
    setProfileForm(rangeProfileFormFromRange(selectedRange));
    setBrandingForm(rangeBrandingFormFromRange(selectedRange));
    setBusinessHoursForm(defaultBusinessHoursForm(selectedRange?.profile?.timezone || selectedRange?.timezone));
  }, [selectedRange]);

  useEffect(() => {
    const email = auth.attributes?.email || "";
    if (auth.status !== "authenticated" || !email) {
      return;
    }

    setForm((current) => (
      current.contactEmail ? current : { ...current, contactEmail: email }
    ));
  }, [auth.attributes?.email, auth.status]);

  useEffect(() => {
    const shouldLoadBusinessHours = location.pathname.endsWith("/settings") ||
      location.pathname === "/console" ||
      /^\/console\/ranges\/[^/]+$/.test(location.pathname) ||
      routeModuleKey === "launch";
    if (!selectedRange?.rangeId || !shouldLoadBusinessHours) {
      setBusinessHoursLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadBusinessHours() {
      setBusinessHoursLoading(true);
      try {
        const result = await getBusinessHours(selectedRange.rangeId);
        if (!cancelled) {
          setBusinessHoursForm(businessHoursFormFromConfig(result, selectedRange));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setBusinessHoursLoading(false);
        }
      }
    }

    loadBusinessHours();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, routeModuleKey, selectedRange]);

  useEffect(() => {
    const shouldLoadReadiness = location.pathname === "/console" ||
      /^\/console\/ranges\/[^/]+$/.test(location.pathname) ||
      routeModuleKey === "launch";
    if (!selectedRange?.rangeId || !shouldLoadReadiness) {
      setRangeReadiness(null);
      setRangeReadinessLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadReadiness() {
      setRangeReadinessLoading(true);
      try {
        const result = await getRangeReadiness(selectedRange.rangeId);
        if (!cancelled) {
          setRangeReadiness(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setRangeReadinessLoading(false);
        }
      }
    }

    loadReadiness();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let cancelled = false;
    async function loadRanges() {
      setLoading(true);
      setError("");
      try {
        const result = await listMyRanges();
        if (!cancelled) {
          setRanges(result);
          setActiveRangeId((current) => preferredActiveRangeId(result, current));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadRanges();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let cancelled = false;
    async function loadApplications() {
      setCatalogLoading(true);
      try {
        const result = await listApplications();
        if (!cancelled) {
          setApplications(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    }

    loadApplications();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    let cancelled = false;
    async function loadRangeApplications() {
      if (!selectedRange?.rangeId) {
        setRangeApplications([]);
        setRangeApplicationsLoading(false);
        return;
      }

      setRangeApplicationsLoading(true);
      try {
        const result = await listRangeApplications(selectedRange.rangeId);
        if (!cancelled) {
          setRangeApplications(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setRangeApplicationsLoading(false);
        }
      }
    }

    loadRangeApplications();
    return () => {
      cancelled = true;
    };
  }, [selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId || !canManageAccess || !location.pathname.endsWith("/access")) {
      setRangeAccessRecords([]);
      setRangeAccessLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadRangeAccess() {
      setRangeAccessLoading(true);
      try {
        const result = await listRangeAccess(selectedRange.rangeId, rangeAccessFilters);
        if (!cancelled) {
          setRangeAccessRecords(result.access || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setRangeAccessLoading(false);
        }
      }
    }

    loadRangeAccess();
    return () => {
      cancelled = true;
    };
  }, [canManageAccess, location.pathname, rangeAccessFilters, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId || !location.pathname.endsWith("/audit")) {
      setAuditEvents([]);
      setAuditLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadAuditEvents() {
      setAuditLoading(true);
      try {
        const result = await listAuditEvents(selectedRange.rangeId, auditFilters);
        if (!cancelled) {
          setAuditEvents(result.events || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setAuditLoading(false);
        }
      }
    }

    loadAuditEvents();
    return () => {
      cancelled = true;
    };
  }, [auditFilters, location.pathname, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId || routeModuleKey !== "daily-ops") {
      setOperationNotes([]);
      setOperationNotesLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadOperationNotes() {
      setOperationNotesLoading(true);
      try {
        const result = await listOperationNotes(selectedRange.rangeId);
        if (!cancelled) {
          setOperationNotes(result.notes || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setOperationNotesLoading(false);
        }
      }
    }

    loadOperationNotes();
    return () => {
      cancelled = true;
    };
  }, [routeModuleKey, selectedRange?.rangeId]);

		  useEffect(() => {
		    const shouldLoadNotifications = location.pathname.endsWith("/notifications") ||
		      isCommunicationsWorkspace ||
		      routeModuleKey === "non-members" ||
		      routeModuleKey === "leads" ||
		      routeModuleKey === "staff-portal" ||
		      location.pathname === "/console" ||
		      /^\/console\/ranges\/[^/]+$/.test(location.pathname);
    if (!selectedRange?.rangeId || !shouldLoadNotifications) {
      setNotifications([]);
      setNotificationSummary(null);
      setNotificationPreferences(null);
      setNotificationsLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadNotifications() {
      setNotificationsLoading(true);
      try {
        const [result, preferencesResult] = await Promise.all([
          listNotifications(selectedRange.rangeId, notificationFilters),
          getNotificationPreferences(selectedRange.rangeId)
        ]);
        if (!cancelled) {
          setNotifications(result.notifications || []);
          setNotificationSummary(result.summary || null);
          setNotificationPreferences(preferencesResult.preferences || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setNotificationsLoading(false);
        }
      }
    }

    loadNotifications();
    return () => {
      cancelled = true;
    };
		  }, [isCommunicationsWorkspace, location.pathname, notificationFilters, routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    setStripeConnectSession(null);
    stripeConnectSessionRef.current = null;
    stripeConnectSessionRangeRef.current = "";
  }, [selectedRange?.rangeId]);

  useEffect(() => {
    const shouldLoadNonMembers = routeModuleKey === "non-members";
    if (!selectedRange?.rangeId || !shouldLoadNonMembers) {
      setNonMemberAttendees([]);
      setNonMemberAttendeesSummary(null);
      setNonMemberAttendeesLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadNonMemberAttendees() {
      setNonMemberAttendeesLoading(true);
      try {
        const result = await listNonMemberAttendees(selectedRange.rangeId);
        if (!cancelled) {
          setNonMemberAttendees(result.attendees || []);
          setNonMemberAttendeesSummary(result.summary || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setNonMemberAttendeesLoading(false);
        }
      }
    }

    loadNonMemberAttendees();
    return () => {
      cancelled = true;
    };
  }, [routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    if (!paymentSettings?.stripe?.connectedAccountId) {
      setStripeConnectSession(null);
      stripeConnectSessionRef.current = null;
      stripeConnectSessionRangeRef.current = "";
    }
  }, [paymentSettings?.stripe?.connectedAccountId]);

  useEffect(() => {
    stripeConnectSessionRef.current = stripeConnectSession;
    stripeConnectSessionRangeRef.current = stripeConnectSession?.clientSecret && selectedRange?.rangeId
      ? selectedRange.rangeId
      : "";
  }, [selectedRange?.rangeId, stripeConnectSession]);

  useEffect(() => {
    const shouldLoadPaymentOverview = location.pathname.endsWith("/payments") || isRangeDashboardRoute || isReportsWorkspace || isAccountingWorkspace || routeModuleKey === "launch";
    if (!selectedRange?.rangeId || !shouldLoadPaymentOverview) {
      setPaymentOverviewEvents(null);
      setPaymentOverviewIssues(null);
      setPaymentOverviewReceipts(null);
      setPaymentOverviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadPaymentOverview() {
      setPaymentOverviewLoading(true);
      try {
        const [settingsResult, eventsResult, receiptsResult, issuesResult] = await Promise.allSettled([
          getRangePayments(selectedRange.rangeId),
          listPaymentEvents(selectedRange.rangeId, { ...paymentEventFilters, limit: 25 }),
          listPaymentReceipts(selectedRange.rangeId, { ...paymentReceiptFilters, limit: 25 }),
          listPaymentIssues(selectedRange.rangeId, { limit: 10 })
        ]);
        if (!cancelled) {
          if (settingsResult.status === "fulfilled") {
            setPaymentSettings(settingsResult.value);
          }
          if (eventsResult.status === "fulfilled") {
            setPaymentOverviewEvents(eventsResult.value);
          }
          if (receiptsResult.status === "fulfilled") {
            setPaymentOverviewReceipts(receiptsResult.value);
          }
          if (issuesResult.status === "fulfilled") {
            setPaymentOverviewIssues(issuesResult.value);
          }
          const firstFailure = [settingsResult, eventsResult, receiptsResult, issuesResult].find((result) => result.status === "rejected");
          if (firstFailure && settingsResult.status === "rejected") {
            setError(firstFailure.reason?.message || "Unable to load payment overview.");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setPaymentOverviewLoading(false);
        }
      }
    }

    loadPaymentOverview();
    return () => {
      cancelled = true;
    };
  }, [isAccountingWorkspace, isRangeDashboardRoute, isReportsWorkspace, location.pathname, paymentEventFilters, paymentReceiptFilters, routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId) {
      setMembershipSummary(null);
      setPaymentSettings(null);
      setMembers([]);
      setMemberDetail(null);
      setMemberTimeline(null);
      setMemberPaymentEvents(null);
      setSelectedMemberDetailId("");
      setMembershipConfigForm(defaultMembershipConfigForm());
      setMembershipLoading(false);
      setPaymentsLoading(false);
      setMembersLoading(false);
      return undefined;
    }

    if (!membershipEnabled || !(isRangeDashboardRoute || ["membership", "resources", "events", "daily-ops", "compliance", "reports", "communications", "accounting", "armory", "staff-portal"].includes(routeModuleKey))) {
      setMembershipSummary(null);
      if (!location.pathname.endsWith("/payments")) {
        setPaymentSettings(null);
      }
      setMembers([]);
      setMemberDetail(null);
      setMemberTimeline(null);
      setMemberPaymentEvents(null);
      setSelectedMemberDetailId("");
      setMembershipConfigForm(defaultMembershipConfigForm());
      setMembershipLoading(false);
      setPaymentsLoading(false);
      setMembersLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadMembershipWorkspace() {
      setMembershipLoading(true);
      setPaymentsLoading(true);
      try {
        const shouldLoadMembershipPayments = routeModuleKey !== "staff-portal";
        const [summaryResult, paymentsResult] = await Promise.all([
          getMembershipSummary(selectedRange.rangeId),
          shouldLoadMembershipPayments ? getRangePayments(selectedRange.rangeId) : Promise.resolve(null)
        ]);
        if (!cancelled) {
          setMembershipSummary(summaryResult);
          if (paymentsResult) {
            setPaymentSettings(paymentsResult);
          }
          setMemberDetail(null);
          setMemberTimeline(null);
          setMemberPaymentEvents(null);
          setSelectedMemberDetailId("");
          setMembershipConfigForm(membershipConfigFormFromSummary(summaryResult));
          setCreateMemberForm((current) => ({
            ...current,
            membershipTypeId: summaryResult.config?.defaultMembershipTypeId || "standard"
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setMembershipLoading(false);
          setPaymentsLoading(false);
        }
      }
    }

    loadMembershipWorkspace();
    return () => {
      cancelled = true;
    };
  }, [isRangeDashboardRoute, location.pathname, membershipEnabled, routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId || !chaptersEnabled || !(membershipEnabled || eventsEnabled)) {
      setChapters([]);
      setChaptersLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadWorkspaceChapters() {
      setChaptersLoading(true);
      try {
        const result = await listChapters(selectedRange.rangeId);
        if (!cancelled) {
          setChapters(result?.chapters || []);
        }
      } catch {
        if (!cancelled) {
          setChapters([]);
        }
      } finally {
        if (!cancelled) {
          setChaptersLoading(false);
        }
      }
    }

    loadWorkspaceChapters();
    return () => {
      cancelled = true;
    };
  }, [chaptersEnabled, eventsEnabled, membershipEnabled, selectedRange?.rangeId]);

  useEffect(() => {
    const slug = String(selectedRange?.slug || "").trim();
    if (!chapterOrganizationWorkspace || !slug) {
      setPublicChapterOptions([]);
      return undefined;
    }

    let cancelled = false;
    async function loadPublicChapterOptions() {
      try {
        const result = await getPublicChapters(slug);
        if (!cancelled) {
          setPublicChapterOptions((result?.chapters || []).map((chapter) => ({
            ...chapter,
            status: chapter.status || "active",
            publicVisible: chapter.publicVisible !== false,
            ownerDataSource: "public-directory"
          })));
        }
      } catch {
        if (!cancelled) {
          setPublicChapterOptions([]);
        }
      }
    }

    loadPublicChapterOptions();
    return () => {
      cancelled = true;
    };
  }, [chapterOrganizationWorkspace, selectedRange?.slug]);

  useEffect(() => {
    const shouldLoadRosterMembers = ["events", "staff-portal"].includes(routeModuleKey) && canReadEvents;
    const shouldLoadFullMembers = canReadMembers && (
      isRangeDashboardRoute || ["membership", "resources", "chapters", "daily-ops", "compliance", "reports", "communications", "accounting", "armory"].includes(routeModuleKey)
    );
    if (!selectedRange?.rangeId || !membershipEnabled || (!shouldLoadFullMembers && !shouldLoadRosterMembers)) {
      if (!shouldLoadFullMembers && routeModuleKey === "staff-portal") {
        setMembers([]);
        setMembersLoading(false);
      }
      return undefined;
    }

    let cancelled = false;
    async function loadFilteredMembers() {
      setMembersLoading(true);
      try {
        const rosterChapterId = shouldLoadRosterMembers
          ? eventFilters.chapterId || workspaceRosterChapterId
          : "";
        const filters = shouldLoadRosterMembers
          ? {
              status: "active",
              membershipTypeId: "",
              chapterId: rosterChapterId,
              source: "",
              q: ""
            }
          : memberFilters;
        const membersResult = await listMembers(selectedRange.rangeId, filters);
        if (!cancelled) {
          setMembers(membersResult);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setMembersLoading(false);
        }
      }
    }

    loadFilteredMembers();
    return () => {
      cancelled = true;
    };
  }, [
    canReadEvents,
    canReadMembers,
    eventFilters.chapterId,
    isRangeDashboardRoute,
    memberFilters,
    membershipEnabled,
    routeModuleKey,
    selectedRange?.rangeId,
    workspaceRosterChapterId
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeReturn = params.get("stripe");
    if (!selectedRange?.rangeId || routeModuleKey !== "membership" || !["return", "refresh"].includes(stripeReturn)) {
      return;
    }

    let cancelled = false;
    async function refreshReturnedStripeAccount() {
      setRefreshingStripeConnect(true);
      setError("");
      try {
        const result = await refreshStripeConnectAccount(selectedRange.rangeId);
        if (!cancelled) {
          setPaymentSettings(result);
          navigate(location.pathname, { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setRefreshingStripeConnect(false);
        }
      }
    }

    refreshReturnedStripeAccount();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate, routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId || !resourcesEnabled || !["membership", "resources", "events", "daily-ops", "equipment", "launch", "reports", "accounting", "armory"].includes(routeModuleKey)) {
      setResourceSummary(null);
      setResources([]);
      setResourceDetail(null);
      setResourceBlocks([]);
      setResourceBlocksSummary(null);
      setResourceReservations([]);
      setResourceReservationsSummary(null);
      setAvailabilityResult(null);
      setResourcesLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadResourcesWorkspace() {
        setResourcesLoading(true);
      try {
        const [resourceResult, blockResult, reservationResult] = await Promise.all([
          getResourceSummary(selectedRange.rangeId),
          listResourceBlocks(selectedRange.rangeId),
          listResourceReservations(selectedRange.rangeId)
        ]);
        if (!cancelled) {
          setResourceSummary(resourceResult);
          setResources(sortResourcesByName(resourceResult.resources || []));
          setResourceBlocks(blockResult.blocks || []);
          setResourceBlocksSummary(blockResult.summary || null);
          setResourceReservations(reservationResult.reservations || []);
          setResourceReservationsSummary(reservationResult.summary || null);
          setResourceBlockForm((current) => ({
            ...current,
            resourceId: current.resourceId || resourceResult.resources?.[0]?.resourceId || ""
          }));
          setAvailabilityForm((current) => ({
            ...current,
            resourceId: current.resourceId || resourceResult.resources?.[0]?.resourceId || ""
          }));
          setReservationForm((current) => ({
            ...current,
            resourceId: current.resourceId || resourceResult.resources?.[0]?.resourceId || ""
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setResourcesLoading(false);
        }
      }
    }

    loadResourcesWorkspace();
    return () => {
      cancelled = true;
    };
  }, [resourcesEnabled, routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId || !resourcesEnabled || routeModuleKey !== "resources" || !routeResourceId) {
      setResourceDetail(null);
      setResourceDetailLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadResourceDetail() {
      setResourceDetailLoading(true);
      try {
        const result = await getResourceDetail(selectedRange.rangeId, routeResourceId);
        if (!cancelled) {
          setResourceDetail(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setResourceDetailLoading(false);
        }
      }
    }

    loadResourceDetail();
    return () => {
      cancelled = true;
    };
  }, [resourcesEnabled, routeModuleKey, routeResourceId, selectedRange?.rangeId]);

  useEffect(() => {
    if (!isTeamWorkspaceRoute || !isWorkspaceProgramsRoute || !workspaceOnlyAccess || workspaceEventChapterOptions.length === 0) {
      return;
    }
    const allowedChapterIds = workspaceEventChapterOptions.map((chapter) => chapter.chapterId).filter(Boolean);
    setEventFilters((current) => (
      allowedChapterIds.includes(current.chapterId)
        ? current
        : { ...current, chapterId: allowedChapterIds[0] || "" }
    ));
  }, [isTeamWorkspaceRoute, isWorkspaceProgramsRoute, workspaceEventChapterOptions, workspaceOnlyAccess]);

  useEffect(() => {
    if (!selectedRange?.rangeId || !eventsEnabled || !canReadEvents || !(isRangeDashboardRoute || ["events", "chapters", "daily-ops", "reports", "accounting", "staff-portal"].includes(routeModuleKey))) {
      setEvents([]);
      setEventsSummary(null);
      setEventsLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadEventsWorkspace() {
      setEventsLoading(true);
      if (routeModuleKey === "events") {
        setPaymentsLoading(true);
      }
      try {
        const [result, paymentsResult] = await Promise.all([
          listEvents(selectedRange.rangeId, apiEventFilters(eventFilters)),
          routeModuleKey === "events" ? getRangePayments(selectedRange.rangeId) : Promise.resolve(null)
        ]);
        if (!cancelled) {
          setEvents(result.events || []);
          setEventsSummary(result.summary || null);
          if (paymentsResult) {
            setPaymentSettings(paymentsResult);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setEventsLoading(false);
          if (routeModuleKey === "events") {
            setPaymentsLoading(false);
          }
        }
      }
    }

    loadEventsWorkspace();
    return () => {
      cancelled = true;
    };
  }, [canReadEvents, eventFilters, eventsEnabled, isRangeDashboardRoute, routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId || !donationsEnabled || !["donations", "reports", "accounting"].includes(routeModuleKey)) {
      setDonations([]);
      setDonationsSummary(null);
      setDonationSettings(null);
      setDonationSettingsForm(defaultDonationSettingsForm());
      setDonationsLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadDonationsWorkspace() {
      setDonationsLoading(true);
      try {
        const [result, settingsResult] = await Promise.all([
          listDonations(selectedRange.rangeId, donationFilters),
          getDonationSettings(selectedRange.rangeId)
        ]);
        if (!cancelled) {
          setDonations(result.donations || []);
          setDonationsSummary(result.summary || null);
          setDonationSettings(settingsResult);
          setDonationSettingsForm(donationSettingsFormFromSettings(settingsResult));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setDonationsLoading(false);
        }
      }
    }

    loadDonationsWorkspace();
    return () => {
      cancelled = true;
    };
  }, [donationFilters, donationsEnabled, routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId || !donationsEnabled || routeModuleKey === "donations") {
      return undefined;
    }

    let cancelled = false;
    async function loadDonationSummary() {
      try {
        const result = await listDonations(selectedRange.rangeId, { limit: 10 });
        if (!cancelled) {
          setDonationsSummary({
            ...(result.summary || {}),
            pending: (result.donations || []).filter((donation) => ["pending", "checkout_started"].includes(donation.status)).length
          });
        }
      } catch {
        if (!cancelled) {
          setDonationsSummary(null);
        }
      }
    }

    loadDonationSummary();
    return () => {
      cancelled = true;
    };
  }, [donationsEnabled, routeModuleKey, selectedRange?.rangeId]);

  useEffect(() => {
    if (!selectedRange?.rangeId || !eventsEnabled || !["events", "staff-portal"].includes(routeModuleKey) || !selectedEventId) {
      setEventRegistrations([]);
      setEventRegistrationsSummary(null);
      setEventRegistrationsLoading(false);
      setEventAttendance([]);
      setEventAttendanceSummary(null);
      setEventAttendanceLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadEventRoster() {
      setEventRegistrationsLoading(true);
      setEventAttendanceLoading(true);
      try {
        const [registrationResult, attendanceResult] = await Promise.all([
          listEventRegistrations(selectedRange.rangeId, selectedEventId, eventRegistrationFilters),
          listEventAttendance(selectedRange.rangeId, selectedEventId, eventAttendanceFilters)
        ]);
        if (!cancelled) {
          setEventRegistrations(registrationResult.registrations || []);
          setEventRegistrationsSummary(registrationResult.summary || null);
          setEventAttendance(attendanceResult.attendance || []);
          setEventAttendanceSummary(attendanceResult.summary || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setEventRegistrationsLoading(false);
          setEventAttendanceLoading(false);
        }
      }
    }

    loadEventRoster();
    return () => {
      cancelled = true;
    };
  }, [eventAttendanceFilters, eventRegistrationFilters, eventsEnabled, routeModuleKey, selectedEventId, selectedRange?.rangeId]);

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const created = await createRange({
        name: form.name,
        slug: form.slug || suggestedSlug,
        timezone: form.timezone,
        displayName: form.name,
        legalName: form.legalName,
        description: form.description,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        website: form.website,
        address: form.address,
        templateKey: form.templateKey,
        selectedApplicationKeys: form.selectedApplicationKeys,
        source: "self_serve"
      });
      setRanges((current) => [created, ...current.filter((range) => range.rangeId !== created.rangeId)]);
      setActiveRangeId(created.rangeId);
      setForm(defaultCreateRangeForm(auth.attributes?.email || ""));
      navigate(`/console/ranges/${created.rangeId}/apps/launch`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveProfile(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingProfile(true);
    setError("");

    try {
      const updated = await updateRangeProfile(selectedRange.rangeId, profileForm);
      setRanges((current) => current.map((range) => (
        range.rangeId === updated.rangeId ? updated : range
      )));
      setProfileForm(rangeProfileFormFromRange(updated));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleSaveBranding(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingBranding(true);
    setError("");

    try {
      const updated = await updateRangeBranding(selectedRange.rangeId, brandingForm);
      setRanges((current) => current.map((range) => (
        range.rangeId === updated.rangeId ? updated : range
      )));
      setBrandingForm(rangeBrandingFormFromRange(updated));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingBranding(false);
    }
  }

  async function handleUpdateProductionMenuApp(appKey, visible) {
    if (!selectedRange?.rangeId || !appKey) {
      return null;
    }

    setUpdatingProductionMenuKey(appKey);
    setError("");

    try {
      const currentOptionalApps = selectedRange.config?.productionMenu?.optionalApps || {};
      const updated = await updateRangeProductionMenu(selectedRange.rangeId, {
        optionalApps: {
          ...currentOptionalApps,
          [appKey]: visible
        }
      });
      setRanges((current) => current.map((range) => (
        range.rangeId === updated.rangeId ? updated : range
      )));
      return updated;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setUpdatingProductionMenuKey("");
    }
  }

  async function handleSaveBusinessHours(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingBusinessHours(true);
    setError("");

    try {
      const updated = await updateBusinessHours(selectedRange.rangeId, businessHoursForm);
      setBusinessHoursForm(businessHoursFormFromConfig(updated, selectedRange));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingBusinessHours(false);
    }
  }

  async function handleEnableApplication(applicationId) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setEnablingApplicationId(applicationId);
    setError("");

    try {
      const enabled = await enableRangeApplication(selectedRange.rangeId, applicationId);
      setRangeApplications((current) => [
        enabled,
        ...current.filter((item) => item.applicationId !== enabled.applicationId)
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setEnablingApplicationId("");
    }
  }

  async function handleDisableApplication(applicationId) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setDisablingApplicationId(applicationId);
    setError("");

    try {
      const disabled = await disableRangeApplication(selectedRange.rangeId, applicationId);
      setRangeApplications((current) => [
        disabled,
        ...current.filter((item) => item.applicationId !== disabled.applicationId)
      ]);
      if (routeModuleKey === disabled.moduleKey) {
        navigate(`/console/ranges/${selectedRange.rangeId}/applications`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDisablingApplicationId("");
    }
  }

  async function handleCreateDonation(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingDonation(true);
    setError("");

    try {
      const created = await createDonation(selectedRange.rangeId, {
        donorName: donationForm.donorName,
        donorEmail: donationForm.donorEmail,
        amountCents: Math.round(Number.parseFloat(donationForm.amount || "0") * 100),
        status: donationForm.status,
        note: donationForm.note
      });
      setDonations((current) => [created, ...current.filter((item) => item.donationId !== created.donationId)]);
      setDonationsSummary((current) => ({
        ...(current || {}),
        returned: Number(current?.returned || donations.length) + 1,
        paidTotalCents: Number(current?.paidTotalCents || 0) + (created.status === "paid" ? Number(created.amountCents || 0) : 0)
      }));
      setDonationForm(defaultDonationForm());
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDonation(false);
    }
  }

  async function handleSaveDonationSettings(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingDonationSettings(true);
    setError("");

    try {
      const suggestedAmountsCents = donationSettingsForm.suggestedAmounts
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .map((amount) => Math.round(Number.parseFloat(amount) * 100))
        .filter((amount) => Number.isFinite(amount) && amount > 0);
      const minimumAmountCents = Math.round(Number.parseFloat(donationSettingsForm.minimumAmount || "1") * 100);
      if (!Number.isFinite(minimumAmountCents) || minimumAmountCents <= 0) {
        throw new Error("Minimum donation amount must be greater than zero.");
      }
      if (suggestedAmountsCents.length === 0) {
        throw new Error("Add at least one suggested donation amount.");
      }
      const updated = await updateDonationSettings(selectedRange.rangeId, {
        publicDonationsEnabled: donationSettingsForm.publicDonationsEnabled,
        customAmountEnabled: donationSettingsForm.customAmountEnabled,
        donorNotesEnabled: donationSettingsForm.donorNotesEnabled,
        currency: donationSettingsForm.currency,
        minimumAmountCents,
        suggestedAmountsCents
      });
      setDonationSettings(updated);
      setDonationSettingsForm(donationSettingsFormFromSettings(updated));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDonationSettings(false);
    }
  }

  async function handleExportDonations() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingDonations(true);
    setError("");

    try {
      const result = await downloadDonationsCsv(selectedRange.rangeId, donationFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingDonations(false);
    }
  }

  async function handleDownloadDonationReceipt(donation) {
    if (!selectedRange?.rangeId || !donation?.donationId) {
      return;
    }

    setLoadingDonationReceiptId(donation.donationId);
    setError("");

    try {
      const receipt = await getDonationReceipt(selectedRange.rangeId, donation.donationId);
      downloadReceiptDocument(receipt, {
        range: selectedRange,
        title: "Donation receipt"
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingDonationReceiptId("");
    }
  }

  async function handleRecordDonationRefund(donation) {
    if (!selectedRange?.rangeId || !donation?.donationId) {
      return;
    }

    const amount = window.prompt(
      "Refund amount",
      (Number(donation.amountCents || 0) / 100).toFixed(2)
    );
    if (amount === null) {
      return;
    }
    const amountCents = Math.round(Number.parseFloat(amount || "0") * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setError("Refund amount must be greater than zero.");
      return;
    }
    const reason = window.prompt("Refund reason", donation.refund?.reason || "Owner recorded refund");
    if (reason === null) {
      return;
    }

    setRecordingDonationRefundId(donation.donationId);
    setError("");

    try {
      const updated = await recordDonationRefund(selectedRange.rangeId, donation.donationId, {
        amountCents,
        reason
      });
      setDonations((current) => current.map((item) => (
        item.donationId === updated.donationId ? updated : item
      )));
      setDonationsSummary((current) => ({
        ...(current || {}),
        paidTotalCents: Math.max(
          0,
          Number(current?.paidTotalCents || 0) - (donation.status === "paid" ? Number(donation.amountCents || 0) : 0)
        )
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setRecordingDonationRefundId("");
    }
  }

  function handleDownloadPaymentReceipt(receipt) {
    downloadReceiptDocument(receipt, {
      range: selectedRange,
      title: "Payment receipt"
    });
  }

  async function handleRefundPaymentReceipt(receipt, refund) {
    if (!selectedRange?.rangeId || !receipt?.receiptId) {
      return;
    }

    setRefundingPaymentReceiptId(receipt.receiptId);
    setError("");

    try {
      const result = await refundPaymentReceipt(selectedRange.rangeId, receipt.receiptId, refund);
      if (result?.receipt) {
        setPaymentOverviewReceipts((current) => ({
          ...(current || {}),
          receipts: (current?.receipts || []).map((item) => (
            item.receiptId === result.receipt.receiptId ? result.receipt : item
          ))
        }));
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setRefundingPaymentReceiptId("");
    }
  }

  async function handleUpdatePaymentReceiptReconciliation(receipt, reconciliation) {
    if (!selectedRange?.rangeId || !receipt?.receiptId) {
      return;
    }

    setReconcilingPaymentReceiptId(receipt.receiptId);
    setError("");

    try {
      const result = await updatePaymentReceiptReconciliation(selectedRange.rangeId, receipt.receiptId, reconciliation);
      if (result?.receipt) {
        setPaymentOverviewReceipts((current) => ({
          ...(current || {}),
          receipts: (current?.receipts || []).map((item) => (
            item.receiptId === result.receipt.receiptId ? result.receipt : item
          ))
        }));
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setReconcilingPaymentReceiptId("");
    }
  }

  async function handleSyncPaymentReconciliation(syncFilters = {}) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setSyncingPaymentReconciliation(true);
    setError("");

    try {
      const result = await syncPaymentReconciliation(selectedRange.rangeId, {
        ...syncFilters,
        limit: syncFilters.limit || 25
      });
      if (Array.isArray(result?.receipts) && result.receipts.length > 0) {
        setPaymentOverviewReceipts((current) => {
          const nextById = new Map(result.receipts.map((receipt) => [receipt.receiptId, receipt]));
          return {
            ...(current || {}),
            receipts: (current?.receipts || []).map((item) => nextById.get(item.receiptId) || item)
          };
        });
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSyncingPaymentReconciliation(false);
    }
  }

  async function handleClosePaymentReconciliation(close) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setClosingPaymentReconciliation(true);
    setError("");

    try {
      const result = await closePaymentReconciliationPeriod(selectedRange.rangeId, close);
      if (result?.close) {
        setPaymentSettings((current) => {
          const accounting = current?.accounting || {};
          return {
            ...(current || {}),
            accounting: {
              ...accounting,
              lastPeriodCloseAt: result.close.closedAt,
              periodCloses: [
                result.close,
                ...(accounting.periodCloses || []).filter((item) => (
                  item.closeId !== result.close.closeId &&
                  !(item.startDate === result.close.startDate && item.endDate === result.close.endDate)
                ))
              ].slice(0, 24)
            }
          };
        });
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setClosingPaymentReconciliation(false);
    }
  }

  async function handleExportPaymentReceipts(exportFilters = {}) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingPaymentReceipts(true);
    setError("");

    try {
      const result = await downloadPaymentReceiptsCsv(selectedRange.rangeId, {
        ...paymentReceiptFilters,
        ...exportFilters
      });
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingPaymentReceipts(false);
    }
  }

  async function handleExportPaymentReconciliation(exportFilters = {}) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingPaymentReconciliation(true);
    setError("");

    try {
      const result = await downloadPaymentReconciliationCsv(selectedRange.rangeId, {
        ...paymentReceiptFilters,
        ...exportFilters
      });
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingPaymentReconciliation(false);
    }
  }

  async function handleExportPaymentPeriodCloses() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingPaymentReconciliation(true);
    setError("");

    try {
      const result = await downloadPaymentPeriodClosesCsv(selectedRange.rangeId);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingPaymentReconciliation(false);
    }
  }

  async function handleExportPaymentEvents() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingPaymentEvents(true);
    setError("");

    try {
      const result = await downloadPaymentEventsCsv(selectedRange.rangeId, paymentEventFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingPaymentEvents(false);
    }
  }

  async function handleExportMembers() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingMembers(true);
    setError("");

    try {
      const result = await downloadMembersCsv(selectedRange.rangeId, memberFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingMembers(false);
    }
  }

  async function handleExportMemberQualifications() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingMemberQualifications(true);
    setError("");

    try {
      const result = await downloadMemberQualificationsCsv(selectedRange.rangeId, memberFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingMemberQualifications(false);
    }
  }

  async function handleExportMemberFlags() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingMemberFlags(true);
    setError("");

    try {
      const result = await downloadMemberFlagsCsv(selectedRange.rangeId, memberFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingMemberFlags(false);
    }
  }

  async function handleExportMemberHousehold() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingMemberHousehold(true);
    setError("");

    try {
      const result = await downloadMemberHouseholdCsv(selectedRange.rangeId, memberFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingMemberHousehold(false);
    }
  }

  async function handleExportMemberGuestPasses() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingMemberGuestPasses(true);
    setError("");

    try {
      const result = await downloadMemberGuestPassesCsv(selectedRange.rangeId, memberFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingMemberGuestPasses(false);
    }
  }

  async function handleGrantAccess(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setGrantingAccess(true);
    setError("");

    try {
      const created = await grantRangeAccess(selectedRange.rangeId, buildAccessGrantPayload(accessForm));
      setRangeAccessRecords((current) => [created, ...current.filter((item) => item.accessUserId !== created.accessUserId)]);
      setAccessForm(defaultAccessForm());
    } catch (err) {
      setError(err.message);
    } finally {
      setGrantingAccess(false);
    }
  }

  async function handleExportRangeAccess() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingRangeAccess(true);
    setError("");

    try {
      const result = await downloadRangeAccessCsv(selectedRange.rangeId, rangeAccessFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingRangeAccess(false);
    }
  }

  async function handleExportAudit() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingAudit(true);
    setError("");

    try {
      const result = await downloadAuditCsv(selectedRange.rangeId, auditFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingAudit(false);
    }
  }

  async function handleCreateOperationNote(note) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setSavingOperationNote(true);
    setError("");

    try {
      const saved = await createOperationNote(selectedRange.rangeId, note);
      setOperationNotes((current) => [saved, ...current.filter((item) => item.noteId !== saved.noteId)].slice(0, 24));
      return saved;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSavingOperationNote(false);
    }
  }

  async function handleUpdateAccess(accessUserId, patch) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingAccessUserId(accessUserId);
    setError("");

    try {
      const updated = await updateRangeAccess(selectedRange.rangeId, accessUserId, patch);
      setRangeAccessRecords((current) => current.map((item) => (
        item.accessUserId === accessUserId ? updated : item
      )));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingAccessUserId("");
    }
  }

  async function handleUpdateNotificationStatus(notificationId, status) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingNotificationId(notificationId);
    setError("");

    try {
      const updated = await updateNotificationStatus(selectedRange.rangeId, notificationId, status);
      setNotifications((current) => {
        const next = current.map((item) => (
          item.notificationId === updated.notificationId ? updated : item
        ));
        setNotificationSummary((summary) => ({
          ...(summary || {}),
          queued: next.filter((item) => item.status === "queued").length,
          failed: next.filter((item) => item.status === "failed" || item.delivery?.status === "failed").length
        }));
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingNotificationId("");
    }
  }

  async function handleDeliverNotification(notificationId) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingNotificationId(notificationId);
    setError("");

    try {
      const updated = await deliverNotification(selectedRange.rangeId, notificationId);
      setNotifications((current) => {
        const next = current.map((item) => (
          item.notificationId === updated.notificationId ? updated : item
        ));
        setNotificationSummary((summary) => ({
          ...(summary || {}),
          queued: next.filter((item) => item.status === "queued").length,
          failed: next.filter((item) => item.status === "failed" || item.delivery?.status === "failed").length
        }));
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingNotificationId("");
    }
  }

  async function handleQueueCustomNotification(notification) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setQueueingCustomNotification(true);
    setError("");

    try {
      const queued = await queueCustomNotification(selectedRange.rangeId, notification);
      setNotifications((current) => [queued, ...current]);
      setNotificationSummary((summary) => ({
        ...(summary || {}),
        queued: (summary?.queued || 0) + 1,
        totalReturned: (summary?.totalReturned || notifications.length) + 1
      }));
      return queued;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setQueueingCustomNotification(false);
    }
  }

  async function handlePreviewCommunicationAudience(params) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    try {
      return await previewCommunicationAudience(selectedRange.rangeId, params);
    } catch (err) {
      setError(err.message);
      return null;
    }
  }

  async function handleSendCommunicationBroadcast(payload) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    try {
      return await sendCommunicationBroadcast(selectedRange.rangeId, payload);
    } catch (err) {
      setError(err.message);
      return null;
    }
  }

  async function handleSaveNotificationPreferences(preferences) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingNotificationPreferences(true);
    setError("");

    try {
      const result = await updateNotificationPreferences(selectedRange.rangeId, preferences);
      setNotificationPreferences(result.preferences || null);
      return result.preferences || null;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSavingNotificationPreferences(false);
    }
  }

  async function handleStartSenderEmailVerification(email) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setUpdatingSenderEmail(true);
    setError("");

    try {
      const result = await startSenderEmailVerification(selectedRange.rangeId, email);
      setNotificationPreferences(result.preferences || null);
      return result.preferences || null;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUpdatingSenderEmail(false);
    }
  }

  async function handleRefreshSenderEmailVerification(email) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setUpdatingSenderEmail(true);
    setError("");

    try {
      const result = await refreshSenderEmailVerification(selectedRange.rangeId, email);
      setNotificationPreferences(result.preferences || null);
      return result.preferences || null;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUpdatingSenderEmail(false);
    }
  }

  async function handleSendGuestLeadEmail(attendanceId, emailPayload) {
    if (!selectedRange?.rangeId || !attendanceId) {
      return null;
    }

    setSendingGuestLeadEmailId(attendanceId);
    setError("");

    try {
      const result = await sendNonMemberAttendeeEmail(selectedRange.rangeId, attendanceId, emailPayload);
      if (result.attendee) {
        setNonMemberAttendees((current) => current.map((attendee) => (
          attendee.attendanceId === result.attendee.attendanceId
            ? {
                ...attendee,
                ...result.attendee,
                event: attendee.event
              }
            : attendee
        )));
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSendingGuestLeadEmailId("");
    }
  }

  async function handleSendRangeLeadEmail(leadId, emailPayload) {
    if (!selectedRange?.rangeId || !leadId) {
      return null;
    }

    setSendingGuestLeadEmailId(leadId);
    setError("");

    try {
      return await sendRangeLeadEmail(selectedRange.rangeId, leadId, emailPayload);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSendingGuestLeadEmailId("");
    }
  }

  async function handleExportNotifications() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingNotifications(true);
    setError("");

    try {
      const result = await downloadNotificationsCsv(selectedRange.rangeId, notificationFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingNotifications(false);
    }
  }

  async function handleCreateMember(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingMember(true);
    setError("");

    try {
      const member = await createMember(selectedRange.rangeId, createMemberForm);
      setMembers((current) => [member, ...current]);
      setMembershipSummary((current) => current
        ? { ...current, memberCount: (current.memberCount || 0) + 1 }
        : current);
      setCreateMemberForm((current) => ({
        fullName: "",
        email: "",
        membershipTypeId: current.membershipTypeId,
        homeChapterId: current.homeChapterId,
        status: "active"
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMember(false);
    }
  }

  async function handleCreateMemberFromPayload(memberInput) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setSavingMember(true);
    setError("");

    try {
      const member = await createMember(selectedRange.rangeId, memberInput);
      setMembers((current) => [member, ...current.filter((item) => item.memberId !== member.memberId)]);
      setMembershipSummary((current) => current
        ? { ...current, memberCount: (current.memberCount || 0) + 1 }
        : current);
      return member;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSavingMember(false);
    }
  }

  async function handlePreviewMemberImport(csv) {
    if (!selectedRange?.rangeId) {
      return null;
    }
    setMemberImportLoading(true);
    setError("");
    try {
      const preview = await previewMemberImport(selectedRange.rangeId, csv);
      setMemberImportPreview(preview);
      return preview;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setMemberImportLoading(false);
    }
  }

  async function handleCommitMemberImport() {
    if (!selectedRange?.rangeId || !memberImportPreview?.rows?.length) {
      return;
    }
    const validRows = memberImportPreview.rows.filter((row) => row.valid);
    if (validRows.length === 0) {
      return;
    }
    setMemberImportCommitting(true);
    setError("");
    try {
      const created = [];
      for (const row of validRows) {
        const member = await createMember(selectedRange.rangeId, row.member);
        created.push(member);
      }
      setMembers((current) => [...created, ...current]);
      setMembershipSummary((current) => current
        ? { ...current, memberCount: (current.memberCount || 0) + created.length }
        : current);
      setMemberImportPreview(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setMemberImportCommitting(false);
    }
  }

  async function handleUpdateMemberReview(memberId, patch) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingMemberId(memberId);
    setError("");

    try {
      const updated = await updateMemberReview(selectedRange.rangeId, memberId, patch);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingMemberId("");
    }
  }

  async function handleUpdateMemberStatus(memberId, patch) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingMemberStatusId(memberId);
    setError("");

    try {
      const updated = await updateMemberStatus(selectedRange.rangeId, memberId, patch);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingMemberStatusId("");
    }
  }

  async function handleUpdateMemberProfile(memberId, patch) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingMemberProfileId(memberId);
    setError("");

    try {
      const updated = await updateMemberProfile(selectedRange.rangeId, memberId, patch);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingMemberProfileId("");
    }
  }

  async function handleUpdateMemberStatusFromChapter(memberId, patch) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setUpdatingMemberStatusId(memberId);
    setError("");

    try {
      const updated = await updateMemberStatus(selectedRange.rangeId, memberId, patch);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUpdatingMemberStatusId("");
    }
  }

  async function handleUpdateMemberProfileFromChapter(memberId, patch) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setUpdatingMemberProfileId(memberId);
    setError("");

    try {
      const updated = await updateMemberProfile(selectedRange.rangeId, memberId, patch);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUpdatingMemberProfileId("");
    }
  }

  async function handleOpenMemberDetail(memberId) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setSelectedMemberDetailId(memberId);
    setMemberDetailLoading(true);
    setMemberTimelineLoading(true);
    setMemberPaymentEventsLoading(true);
    setMemberPaymentReceiptsLoading(true);
    setError("");

    try {
      const [detail, timeline, paymentEvents, paymentReceipts] = await Promise.all([
        getMemberDetail(selectedRange.rangeId, memberId),
        getMemberTimeline(selectedRange.rangeId, memberId),
        listPaymentEvents(selectedRange.rangeId, { memberId }),
        listPaymentReceipts(selectedRange.rangeId, { memberId })
      ]);
      setMemberDetail(detail);
      setMemberTimeline(timeline);
      setMemberPaymentEvents(paymentEvents);
      setMemberPaymentReceipts(paymentReceipts);
    } catch (err) {
      setError(err.message);
    } finally {
      setMemberDetailLoading(false);
      setMemberTimelineLoading(false);
      setMemberPaymentEventsLoading(false);
      setMemberPaymentReceiptsLoading(false);
    }
  }

  async function handleRenewMember(memberId, renewal) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setRenewingMemberId(memberId);
    setError("");

    try {
      const updated = await renewMember(selectedRange.rangeId, memberId, renewal);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setRenewingMemberId("");
    }
  }

  async function handleChangeMemberPlan(memberId, planChange) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setChangingMemberPlanId(memberId);
    setError("");

    try {
      const updated = await changeMemberPlan(selectedRange.rangeId, memberId, planChange);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setChangingMemberPlanId("");
    }
  }

  async function handleUpdateMemberGuestPasses(memberId, patch) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingGuestPassMemberId(memberId);
    setError("");

    try {
      const updated = await updateMemberGuestPasses(selectedRange.rangeId, memberId, patch);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingGuestPassMemberId("");
    }
  }

  async function handleAddHouseholdMember(memberId, householdMember) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setAddingHouseholdMemberId(memberId);
    setError("");

    try {
      const updated = await addHouseholdMember(selectedRange.rangeId, memberId, householdMember);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingHouseholdMemberId("");
    }
  }

  async function handleAddMemberFlag(memberId, flag) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setAddingMemberFlagId(memberId);
    setError("");

    try {
      const updated = await addMemberFlag(selectedRange.rangeId, memberId, flag);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingMemberFlagId("");
    }
  }

  async function handleAddMemberDocument(memberId, document) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setAddingMemberDocumentId(memberId);
    setError("");

    try {
      const updated = await addMemberDocument(selectedRange.rangeId, memberId, document);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingMemberDocumentId("");
    }
  }

  async function handleAddMemberQualification(memberId, qualification) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setAddingMemberQualificationId(memberId);
    setError("");

    try {
      const updated = await addMemberQualification(selectedRange.rangeId, memberId, qualification);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingMemberQualificationId("");
    }
  }

  async function handleUpdateMemberQualification(memberId, qualificationId, patch) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setAddingMemberQualificationId(memberId);
    setError("");

    try {
      const updated = await updateMemberQualification(selectedRange.rangeId, memberId, qualificationId, patch);
      setMembers((current) => current.map((member) => (
        member.memberId === memberId ? updated : member
      )));
      setMemberDetail((current) => (
        current?.member?.memberId === memberId
          ? { ...current, member: updated }
          : current
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingMemberQualificationId("");
    }
  }

  function handleCloseMemberDetail() {
    setSelectedMemberDetailId("");
    setMemberDetail(null);
    setMemberTimeline(null);
    setMemberPaymentEvents(null);
    setMemberDetailLoading(false);
    setMemberTimelineLoading(false);
    setMemberPaymentEventsLoading(false);
  }

  function resetMembershipTypeForm() {
    setEditingMembershipTypeId("");
    setMembershipTypeForm(defaultMembershipTypeForm());
  }

  function handleEditMembershipType(type) {
    setEditingMembershipTypeId(type.membershipTypeId);
    setMembershipTypeForm({
      name: type.name || "",
      description: type.description || "",
      category: type.category || "membership",
      billingModel: type.billing?.model || (type.lifetime ? "one_time" : "subscription"),
      billingInterval: type.billing?.interval || type.term?.unit || "year",
      termCount: String(type.term?.unit === "lifetime" ? 1 : type.term?.count || 1),
      termUnit: type.term?.unit || "year",
      price: String(((type.pricing?.amountCents || 0) / 100).toFixed(2)),
      paymentMode: type.paymentPolicy?.mode || "full_payment",
      prescreeningFee: type.paymentPolicy?.prescreeningFeeCents
        ? String((Number(type.paymentPolicy.prescreeningFeeCents || 0) / 100).toFixed(2))
        : "",
      prescreeningLabel: type.paymentPolicy?.prescreeningLabel || "Prescreening fee",
      prescreeningNonRefundable: type.paymentPolicy?.nonRefundablePrescreeningFee !== false,
      balanceDueDays: String(type.paymentPolicy?.balanceDueDays || 14),
      status: type.status || "active",
      publicSignupEnabled: Boolean(type.publicSignupEnabled),
      campaignEnabled: Boolean(type.campaign?.enabled),
      campaignLabel: type.campaign?.label || "",
      campaignMaxSignups: type.campaign?.maxSignups ? String(type.campaign.maxSignups) : "",
      campaignShowRemaining: type.campaign?.showRemaining !== false,
      benefitsItemsText: linesFromValue(type.benefits?.items),
      benefitsNotes: type.benefits?.notes || "",
      requiredQualificationKeysText: linesFromValue(type.accessRules?.requiredQualificationKeys),
      guestPasses: String(type.guestPassPolicy?.passes || 0),
      guestPassPeriod: type.guestPassPolicy?.period || "month",
      guestPassRollover: Boolean(type.guestPassPolicy?.rollover),
      laneReservationAllowed: Boolean(type.accessRules?.laneReservation?.allowed),
      advanceBookingDays: String(type.accessRules?.laneReservation?.advanceBookingDays || 0),
      includedHoursPerMonth: String(type.accessRules?.laneReservation?.includedHoursPerMonth || 0),
      maxActiveReservations: String(type.accessRules?.laneReservation?.maxActiveReservations || 0),
      gunRentalAllowed: Boolean(type.accessRules?.gunRental?.allowed),
      gunRentalDiscountPercent: String(type.accessRules?.gunRental?.discountPercent || 0),
      membersOnlyEventsAllowed: Boolean(type.accessRules?.membersOnlyEvents?.allowed),
      chapterScopeMode: type.chapterScope?.mode || "all",
      chapterScopeIds: linesFromValue(type.chapterScope?.chapterIds),
      primaryChapterId: type.chapterScope?.primaryChapterId || ""
    });
  }

  function buildMembershipTypePayload() {
    const term = membershipTypeForm.termUnit === "lifetime"
      ? { unit: "lifetime", count: 1 }
      : {
        count: Number.parseInt(membershipTypeForm.termCount || "1", 10),
        unit: membershipTypeForm.termUnit
      };
    const billing = membershipTypeForm.termUnit === "lifetime" || membershipTypeForm.billingModel === "one_time"
      ? { model: "one_time", interval: null }
      : {
        model: "subscription",
        interval: membershipTypeForm.billingInterval
      };

    return {
      name: membershipTypeForm.name,
      description: membershipTypeForm.description,
      category: membershipTypeForm.category,
      status: membershipTypeForm.status,
      term,
      billing,
      pricing: {
        amountCents: Math.round(Number.parseFloat(membershipTypeForm.price || "0") * 100),
        currency: "usd"
      },
      paymentPolicy: {
        mode: membershipTypeForm.paymentMode,
        prescreeningFeeCents: Math.round(Number.parseFloat(membershipTypeForm.prescreeningFee || "0") * 100),
        prescreeningLabel: membershipTypeForm.prescreeningLabel,
        nonRefundablePrescreeningFee: membershipTypeForm.prescreeningNonRefundable,
        balanceDueDays: Number.parseInt(membershipTypeForm.balanceDueDays || "0", 10)
      },
      campaign: {
        enabled: Boolean(membershipTypeForm.campaignEnabled),
        label: membershipTypeForm.campaignLabel,
        maxSignups: Number.parseInt(membershipTypeForm.campaignMaxSignups || "0", 10),
        showRemaining: Boolean(membershipTypeForm.campaignShowRemaining)
      },
      benefits: {
        notes: membershipTypeForm.benefitsNotes,
        items: benefitItemsFromText(membershipTypeForm.benefitsItemsText)
      },
      guestPassPolicy: {
        enabled: Number.parseInt(membershipTypeForm.guestPasses || "0", 10) > 0,
        passes: Number.parseInt(membershipTypeForm.guestPasses || "0", 10),
        period: membershipTypeForm.guestPassPeriod,
        rollover: membershipTypeForm.guestPassRollover
      },
      accessRules: {
        requiredQualificationKeys: linesToArray(membershipTypeForm.requiredQualificationKeysText),
        laneReservation: {
          allowed: membershipTypeForm.laneReservationAllowed,
          advanceBookingDays: Number.parseInt(membershipTypeForm.advanceBookingDays || "0", 10),
          includedHoursPerMonth: Number.parseInt(membershipTypeForm.includedHoursPerMonth || "0", 10),
          maxActiveReservations: Number.parseInt(membershipTypeForm.maxActiveReservations || "0", 10)
        },
        gunRental: {
          allowed: membershipTypeForm.gunRentalAllowed,
          discountPercent: Number.parseInt(membershipTypeForm.gunRentalDiscountPercent || "0", 10)
        },
        membersOnlyEvents: {
          allowed: membershipTypeForm.membersOnlyEventsAllowed
        }
      },
      publicSignupEnabled: membershipTypeForm.publicSignupEnabled,
      chapterScope: {
        mode: membershipTypeForm.chapterScopeMode,
        chapterIds: linesToArray(membershipTypeForm.chapterScopeIds),
        primaryChapterId: membershipTypeForm.primaryChapterId
      }
    };
  }

  function buildMembershipConfigPayload(formOverride = membershipConfigForm) {
    return {
      settings: {
        qualificationCatalog: parseQualificationCatalogText(formOverride.qualificationCatalogText),
        guestPolicy: {
          waiverRequired: formOverride.guestWaiverRequired,
          waiverInstructions: formOverride.guestWaiverInstructions
        },
        chapterMemberVisibility: {
          phone: formOverride.chapterMemberVisibilityPhone,
          address: formOverride.chapterMemberVisibilityAddress,
          membershipStatus: formOverride.chapterMemberVisibilityStatus,
          joinDate: formOverride.chapterMemberVisibilityJoinDate,
          memberNumber: formOverride.chapterMemberVisibilityMemberNumber
        }
      },
      reviewPolicy: {
        publicSignup: {
          waiver: {
            required: formOverride.waiverRequired,
            instructions: formOverride.waiverInstructions,
            expiresInDays: Number.parseInt(formOverride.waiverExpiresInDays || "0", 10),
            document: formOverride.waiverDocument || undefined,
            documentHistory: formOverride.waiverDocumentHistory || []
          },
          prequalification: {
            required: formOverride.prequalificationRequired || Boolean(formOverride.applicationForm?.enabled && formOverride.applicationForm?.status === "published"),
            instructions: formOverride.prequalificationInstructions,
            checklist: parseChecklistText(formOverride.prequalificationChecklistText)
          },
          applicationForm: normalizeApplicationFormForClient(formOverride.applicationForm),
          backgroundCheck: {
            required: formOverride.backgroundCheckRequired,
            instructions: formOverride.backgroundCheckInstructions,
            checklist: parseChecklistText(formOverride.backgroundCheckChecklistText),
            provider: formOverride.backgroundCheckRequired ? "checkr" : formOverride.backgroundCheckProvider,
            checkr: {
              environment: formOverride.backgroundCheckEnvironment || "test",
              packageId: formOverride.backgroundCheckPackageId,
              packageName: formOverride.backgroundCheckPackageName,
              workLocationId: formOverride.backgroundCheckWorkLocationId,
              accountMode: "rangegrid_managed",
              autoStartAfterPrescreen: formOverride.backgroundCheckAutoStartAfterPrescreen !== false,
              mockMode: formOverride.backgroundCheckMockMode !== false
            }
          },
          approvalMode: formOverride.approvalMode
        }
      }
    };
  }

  async function handleSaveMembershipConfig(event, formOverride) {
    event?.preventDefault?.();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingMembershipConfig(true);
    setError("");

    try {
      const config = await updateMembershipConfig(selectedRange.rangeId, buildMembershipConfigPayload(formOverride || membershipConfigForm));
      setMembershipSummary((current) => current ? { ...current, config } : current);
      setMembershipConfigForm(membershipConfigFormFromSummary({ config }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMembershipConfig(false);
    }
  }

  const openStripeConnectSession = useCallback(async () => {
    if (!selectedRange?.rangeId) {
      throw new Error("Select a range before opening Stripe.");
    }

    const cachedSession = stripeConnectSessionRef.current;
    if (
      cachedSession?.clientSecret &&
      stripeConnectSessionRangeRef.current === selectedRange.rangeId
    ) {
      return {
        ...(paymentSettings || {}),
        accountSession: cachedSession
      };
    }

    const result = await createStripeConnectAccountSession(selectedRange.rangeId);
    setPaymentSettings(result);
    setStripeConnectSession(result.accountSession || null);
    stripeConnectSessionRef.current = result.accountSession || null;
    stripeConnectSessionRangeRef.current = result.accountSession?.clientSecret ? selectedRange.rangeId : "";
    return result;
  }, [paymentSettings, selectedRange?.rangeId]);

  async function handleOpenStripeConnectSession() {
    setStartingStripeConnect(true);
    setError("");

    try {
      await openStripeConnectSession();
    } catch (err) {
      setError(err.message);
    } finally {
      setStartingStripeConnect(false);
    }
  }

  async function handleStartStripeConnect() {
    setStartingStripeConnect(true);
    setError("");

    try {
      await openStripeConnectSession();
      navigate(`${selectedRangePath}/payments`);
    } catch (err) {
      setError(err.message);
    } finally {
      setStartingStripeConnect(false);
    }
  }

  const fetchStripeConnectClientSecret = useCallback(async () => {
    if (!selectedRange?.rangeId) {
      throw new Error("Select a range before opening Stripe.");
    }
    const cachedSession = stripeConnectSessionRef.current;
    if (
      cachedSession?.clientSecret &&
      stripeConnectSessionRangeRef.current === selectedRange.rangeId
    ) {
      return cachedSession.clientSecret;
    }

    const result = await openStripeConnectSession();
    const clientSecret = result?.accountSession?.clientSecret || "";
    if (!clientSecret) {
      throw new Error("Stripe did not return an embedded workspace session.");
    }
    return clientSecret;
  }, [openStripeConnectSession, selectedRange?.rangeId]);

  async function handleRefreshStripeConnect() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setRefreshingStripeConnect(true);
    setError("");

    try {
      const result = await refreshStripeConnectAccount(selectedRange.rangeId);
      setPaymentSettings(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshingStripeConnect(false);
    }
  }

  async function handleUploadWaiver(file) {
    if (!selectedRange?.rangeId || !file) {
      return;
    }

    setUploadingWaiver(true);
    setError("");

    try {
      const upload = await createMembershipDocumentUploadUrl(selectedRange.rangeId, {
        documentType: "waiver",
        fileName: file.name,
        contentType: file.type || "application/pdf",
        sizeBytes: file.size
      });
      const response = await fetch(upload.uploadUrl, {
        method: upload.method || "PUT",
        headers: upload.headers || { "content-type": file.type || "application/pdf" },
        body: file
      });
      if (!response.ok) {
        throw new Error(`Waiver upload failed (${response.status})`);
      }
      setMembershipConfigForm((current) => ({
        ...current,
        waiverDocument: upload.document,
        waiverDocumentHistory: current.waiverDocumentHistory || [],
        waiverInstructions: current.waiverInstructions || "Review the waiver document before approving membership."
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingWaiver(false);
    }
  }

  async function handleSaveMembershipType(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingMembershipType(true);
    setError("");

    try {
      const payload = buildMembershipTypePayload();
      const saved = editingMembershipTypeId
        ? await updateMembershipType(selectedRange.rangeId, editingMembershipTypeId, payload)
        : await createMembershipType(selectedRange.rangeId, payload);

      setMembershipSummary((current) => {
        if (!current) {
          return current;
        }
        const existingTypes = current.membershipTypes || [];
        const nextTypes = [
          saved,
          ...existingTypes.filter((type) => type.membershipTypeId !== saved.membershipTypeId)
        ].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        return { ...current, membershipTypes: nextTypes };
      });
      resetMembershipTypeForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMembershipType(false);
    }
  }

  async function handleSetDefaultMembershipType(membershipTypeId) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setError("");
    try {
      const config = await setDefaultMembershipType(selectedRange.rangeId, membershipTypeId);
      setMembershipSummary((current) => current ? { ...current, config } : current);
      setCreateMemberForm((current) => ({ ...current, membershipTypeId }));
    } catch (err) {
      setError(err.message);
    }
  }

  function buildMembershipTypePayloadFromType(type, overrides = {}) {
    return {
      name: type.name,
      description: type.description || "",
      category: type.category || "membership",
      status: type.status || "active",
      term: type.term || { unit: "year", count: 1 },
      billing: type.billing || { model: "subscription", interval: "year" },
      pricing: {
        amountCents: Number.parseInt(type.pricing?.amountCents || 0, 10),
        currency: type.pricing?.currency || "usd"
      },
      paymentPolicy: type.paymentPolicy || {
        mode: "full_payment",
        prescreeningFeeCents: 0,
        prescreeningLabel: "",
        nonRefundablePrescreeningFee: false,
        balanceDueDays: 0
      },
      benefits: type.benefits || { notes: "", items: [] },
      guestPassPolicy: type.guestPassPolicy || {
        enabled: false,
        passes: 0,
        period: "month",
        rollover: false
      },
      accessRules: type.accessRules || {
        laneReservation: {
          allowed: false,
          advanceBookingDays: 0,
          includedHoursPerMonth: 0,
          maxActiveReservations: 0
        },
        gunRental: {
          allowed: false,
          discountPercent: 0
        },
        membersOnlyEvents: {
          allowed: false
        }
      },
      publicSignupEnabled: Boolean(type.publicSignupEnabled),
      ...overrides
    };
  }

  async function handleArchiveMembershipType(type) {
    if (!selectedRange?.rangeId || !type?.membershipTypeId) {
      return;
    }

    if (!window.confirm(`Deactivate ${type.name}? Existing member history stays intact, but the plan will no longer be available for sign-up or Stripe checkout.`)) {
      return;
    }

    setArchivingMembershipTypeId(type.membershipTypeId);
    setError("");

    try {
      const saved = await updateMembershipType(
        selectedRange.rangeId,
        type.membershipTypeId,
        buildMembershipTypePayloadFromType(type, {
          status: "inactive",
          publicSignupEnabled: false
        })
      );
      setMembershipSummary((current) => {
        if (!current) {
          return current;
        }
        const nextTypes = [
          saved,
          ...(current.membershipTypes || []).filter((item) => item.membershipTypeId !== saved.membershipTypeId)
        ].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        return { ...current, membershipTypes: nextTypes };
      });
      if (editingMembershipTypeId === type.membershipTypeId) {
        resetMembershipTypeForm();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setArchivingMembershipTypeId("");
    }
  }

  function summarizeResources(nextResources) {
    return {
      total: nextResources.length,
      bays: nextResources.filter((resource) => resource.type === "bay").length,
      lanes: nextResources.filter((resource) => resource.type === "lane").length,
      open: nextResources.filter((resource) => resource.status === "open").length,
      targetChecks: nextResources.filter((resource) => resource.targetControl?.status === "target_check").length
    };
  }

  function summarizeResourceBlocks(nextBlocks) {
    const now = Date.now();
    return {
      total: nextBlocks.length,
      active: nextBlocks.filter((block) => block.status === "active").length,
      upcoming: nextBlocks.filter((block) => (
        block.status === "active" && Date.parse(block.endsAt) >= now
      )).length
    };
  }

  function summarizeResourceReservations(nextReservations) {
    const now = Date.now();
    const byResource = Array.from(nextReservations.reduce((map, reservation) => {
      const current = map.get(reservation.resourceId) || {
        resourceId: reservation.resourceId,
        reservations: 0,
        walkIns: 0,
        reservedHours: 0
      };
      const starts = Date.parse(reservation.startsAt);
      const ends = Date.parse(reservation.endsAt);
      const hours = Number.isFinite(starts) && Number.isFinite(ends) && ends > starts
        ? (ends - starts) / (60 * 60 * 1000)
        : 0;
      current.reservations += 1;
      current.walkIns += reservation.source === "walk_in" ? 1 : 0;
      if (["confirmed", "completed"].includes(reservation.status) || reservation.attendance?.status === "checked_in") {
        current.reservedHours += hours;
      }
      map.set(reservation.resourceId, current);
      return map;
    }, new Map()).values())
      .sort((left, right) => right.reservedHours - left.reservedHours || right.reservations - left.reservations)
      .slice(0, 20);
    return {
      total: nextReservations.length,
      confirmed: nextReservations.filter((reservation) => reservation.status === "confirmed").length,
      standby: nextReservations.filter((reservation) => reservation.status === "standby").length,
      walkIns: nextReservations.filter((reservation) => reservation.source === "walk_in").length,
      checkedIn: nextReservations.filter((reservation) => reservation.attendance?.status === "checked_in").length,
      completed: nextReservations.filter((reservation) => reservation.status === "completed").length,
      noShow: nextReservations.filter((reservation) => reservation.status === "no_show").length,
      upcoming: nextReservations.filter((reservation) => (
        reservation.status === "confirmed" && Date.parse(reservation.endsAt) >= now
      )).length,
      utilization: {
        byResource
      }
    };
  }

  function summarizeEvents(nextEvents) {
    return {
      total: nextEvents.length,
      published: nextEvents.filter((item) => item.status === "published").length,
      draft: nextEvents.filter((item) => item.status === "draft").length
    };
  }

  function defaultChapterLeadInstructor() {
    const role = String(selectedRange?.access?.role || "").toLowerCase();
    if (role !== "chapter_leader") {
      return null;
    }
    const chapter = eventChapterOptions.find((item) => item.chapterId === eventForm.primaryChapterId) ||
      eventChapterOptions.find((item) => item.chapterId === linesToArray(eventForm.chapterScopeIds)[0]) ||
      workspaceEventChapterOptions[0] ||
      null;
    const name = [
      chapter?.leaderFirstName,
      chapter?.leaderLastName
    ].filter(Boolean).join(" ").trim() ||
      auth.attributes?.name ||
      auth.attributes?.preferred_username ||
      selectedRange?.access?.email ||
      auth.attributes?.email ||
      "";
    const email = (chapter?.leaderEmails || []).find(Boolean) ||
      selectedRange?.access?.email ||
      auth.attributes?.email ||
      "";
    if (!name && !email) {
      return null;
    }
    return {
      name,
      email,
      phone: chapter?.leaderPhone || "",
      role: "Chapter lead"
    };
  }

  function buildEventPayload() {
    const instructors = String(eventForm.instructorLines || "")
      .split("\n")
      .map((line) => {
        const [name, email, role] = line.split("|").map((item) => item.trim());
        return { name, email, role };
      })
      .filter((item) => item.name || item.email);
    if (instructors.length === 0) {
      const defaultInstructor = defaultChapterLeadInstructor();
      if (defaultInstructor) {
        instructors.push(defaultInstructor);
      }
    }
    return {
      title: eventForm.title,
      description: eventForm.description,
      type: eventForm.type,
      status: eventForm.status,
      visibility: eventForm.visibility,
      startsAt: new Date(eventForm.startsAt).toISOString(),
      endsAt: new Date(eventForm.endsAt).toISOString(),
      timezone: eventForm.timezone || selectedRange?.timezone || "America/Los_Angeles",
      location: eventForm.location,
      capacity: {
        maxAttendees: Number.parseInt(eventForm.maxAttendees || "0", 10),
        waitlistLimit: Number.parseInt(eventForm.waitlistLimit || "0", 10)
      },
      registrationPolicy: {
        publicRegistrationEnabled: eventForm.publicRegistrationEnabled,
        opensAt: eventForm.registrationOpensAt ? new Date(eventForm.registrationOpensAt).toISOString() : undefined,
        closesAt: eventForm.registrationClosesAt ? new Date(eventForm.registrationClosesAt).toISOString() : undefined,
        instructions: eventForm.registrationInstructions
      },
      pricing: {
        amountCents: Math.round(Number.parseFloat(eventForm.price || "0") * 100),
        currency: eventForm.currency || "usd",
        label: Number.parseFloat(eventForm.price || "0") > 0 ? "Paid event" : "Free"
      },
      chapterId: eventForm.primaryChapterId || undefined,
      chapterIds: eventForm.chapterScopeMode === "selected" ? linesToArray(eventForm.chapterScopeIds) : [],
      chapterSnapshot: eventForm.primaryChapterId
        ? (() => {
            const chapter = eventChapterOptions.find((item) => item.chapterId === eventForm.primaryChapterId);
            return chapter ? {
              chapterId: chapter.chapterId,
              name: chapter.name,
              city: chapter.city,
              region: chapter.region,
              slug: chapter.slug
            } : undefined;
          })()
        : undefined,
      includedForMembers: Boolean(eventForm.includedForMembers),
      memberPricing: {
        included: Boolean(eventForm.includedForMembers),
        amountCents: 0,
        currency: eventForm.currency || "usd",
        label: Boolean(eventForm.includedForMembers) ? "Included with membership" : "Member price"
      },
      instructors,
      accessPolicy: {
        requiredQualificationKeys: String(eventForm.requiredQualificationKeys || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      },
      resourceIds: eventForm.resourceIds || []
    };
  }

  function formFromEvent(item) {
    return {
      title: displayProgramTitle(item.title) || item.title || "",
      description: item.description || "",
      type: item.type || "class",
      status: item.status || "draft",
      visibility: item.visibility || "private",
      startsAt: item.startsAt ? toDateTimeLocalInput(new Date(item.startsAt)) : defaultEventForm().startsAt,
      endsAt: item.endsAt ? toDateTimeLocalInput(new Date(item.endsAt)) : defaultEventForm().endsAt,
      timezone: item.timezone || selectedRange?.timezone || "America/Los_Angeles",
      location: item.location || "",
      maxAttendees: String(item.capacity?.maxAttendees || 0),
      waitlistLimit: String(item.capacity?.waitlistLimit || 0),
      price: String((Number(item.pricing?.amountCents || 0) / 100).toFixed(2)),
      currency: item.pricing?.currency || "usd",
      publicRegistrationEnabled: item.registrationPolicy?.publicRegistrationEnabled !== false,
      registrationOpensAt: item.registrationPolicy?.opensAt ? toDateTimeLocalInput(new Date(item.registrationPolicy.opensAt)) : "",
      registrationClosesAt: item.registrationPolicy?.closesAt ? toDateTimeLocalInput(new Date(item.registrationPolicy.closesAt)) : "",
      registrationInstructions: item.registrationPolicy?.instructions || "",
      chapterScopeMode: (item.chapterIds || []).length > 0 ? "selected" : "all",
      chapterScopeIds: linesFromValue(item.chapterIds || []),
      primaryChapterId: item.chapterId || item.chapterIds?.[0] || "",
      includedForMembers: Boolean(item.includedForMembers || item.memberPricing?.included),
      instructorLines: (item.instructors || [])
        .map((instructor) => [displayInstructorName(instructor), instructor.email, instructor.role].filter(Boolean).join(" | "))
        .join("\n"),
      requiredQualificationKeys: (item.accessPolicy?.requiredQualificationKeys || []).join(", "),
      resourceIds: item.resourceIds || []
    };
  }

  function handleEditEvent(item) {
    setEditingEventId(item.eventId);
    setEventForm(formFromEvent(item));
    setError("");
  }

  function handleCancelEventEdit() {
    setEditingEventId("");
    setEventForm(defaultEventForm());
  }

  async function handleCreateEvent(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return null;
    }

    setSavingEvent(true);
    setError("");

    try {
      const saved = editingEventId
        ? await updateEvent(selectedRange.rangeId, editingEventId, buildEventPayload())
        : await createEvent(selectedRange.rangeId, buildEventPayload());
      const nextEvents = (editingEventId
        ? events.map((item) => (item.eventId === editingEventId ? saved : item))
        : [...events, saved])
        .sort((left, right) => String(left.startsAt).localeCompare(String(right.startsAt)));
      setEvents(nextEvents);
      setEventsSummary(summarizeEvents(nextEvents));
      setEventForm(defaultEventForm());
      setEditingEventId("");
      return saved;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSavingEvent(false);
    }
  }

  async function handleExportEvents() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingEvents(true);
    setError("");

    try {
      const result = await downloadEventsCsv(selectedRange.rangeId, eventFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingEvents(false);
    }
  }

  async function handleUpdateEvent(eventId, patch) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingEventId(eventId);
    setError("");

    try {
      const current = events.find((item) => item.eventId === eventId) || {};
      const updated = await updateEvent(selectedRange.rangeId, eventId, { ...current, ...patch });
      const nextEvents = events
        .map((item) => (item.eventId === eventId ? updated : item))
        .sort((left, right) => String(left.startsAt).localeCompare(String(right.startsAt)));
      setEvents(nextEvents);
      setEventsSummary(summarizeEvents(nextEvents));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingEventId("");
    }
  }

  async function handleSignOffEvent(eventId, signoff = {}) {
    if (!selectedRange?.rangeId || !eventId) {
      return;
    }

    setUpdatingEventId(eventId);
    setError("");

    try {
      const updated = await signOffEvent(selectedRange.rangeId, eventId, signoff);
      const nextEvents = events
        .map((item) => (item.eventId === eventId ? updated : item))
        .sort((left, right) => String(left.startsAt).localeCompare(String(right.startsAt)));
      setEvents(nextEvents);
      setEventsSummary(summarizeEvents(nextEvents));
      if (selectedEventId === eventId) {
        await Promise.all([
          listEventRegistrations(selectedRange.rangeId, eventId, eventRegistrationFilters),
          listEventAttendance(selectedRange.rangeId, eventId, eventAttendanceFilters)
        ]).then(([registrationResult, attendanceResult]) => {
          setEventRegistrations(registrationResult.registrations || []);
          setEventRegistrationsSummary(registrationResult.summary || null);
          setEventAttendance(attendanceResult.attendance || []);
          setEventAttendanceSummary(attendanceResult.summary || null);
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingEventId("");
    }
  }

  async function handleReviewEventApproval(eventId, decision, review = {}) {
    if (!selectedRange?.rangeId || !eventId) {
      return;
    }

    setUpdatingEventId(eventId);
    setError("");

    try {
      const updated = await reviewEventApproval(selectedRange.rangeId, eventId, decision, review);
      const nextEvents = events
        .map((item) => (item.eventId === eventId ? updated : item))
        .sort((left, right) => String(left.startsAt).localeCompare(String(right.startsAt)));
      setEvents(nextEvents);
      setEventsSummary(summarizeEvents(nextEvents));
      const notificationResult = await listNotifications(selectedRange.rangeId, notificationFilters);
      setNotifications(notificationResult.notifications || []);
      setNotificationSummary(notificationResult.summary || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingEventId("");
    }
  }

  function summarizeEventRegistrations(nextRegistrations) {
    return {
      total: nextRegistrations.length,
      confirmed: nextRegistrations.filter((item) => item.status === "confirmed").length,
      waitlisted: nextRegistrations.filter((item) => item.status === "waitlisted").length,
      cancelled: nextRegistrations.filter((item) => item.status === "cancelled").length,
      paymentPending: nextRegistrations.filter((item) => item.status === "payment_pending").length,
      checkoutStarted: nextRegistrations.filter((item) => item.status === "checkout_started").length
    };
  }

  function summarizeEventAttendance(nextAttendance) {
    return {
      total: nextAttendance.length,
      attended: nextAttendance.filter((item) => item.status === "attended").length,
      noShow: nextAttendance.filter((item) => item.status === "no_show").length,
      excused: nextAttendance.filter((item) => item.status === "excused").length
    };
  }

  async function handleCreateEventRegistration(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId || !selectedEventId) {
      return;
    }

    setSavingEventRegistration(true);
    setError("");

    try {
      const created = await createEventRegistration(
        selectedRange.rangeId,
        selectedEventId,
        eventRegistrationForm
      );
      const nextRegistrations = [...eventRegistrations, created]
        .sort((left, right) => String(left.createdAt).localeCompare(String(right.createdAt)));
      setEventRegistrations(nextRegistrations);
      setEventRegistrationsSummary(summarizeEventRegistrations(nextRegistrations));
      setEventRegistrationForm(defaultEventRegistrationForm());
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingEventRegistration(false);
    }
  }

  async function handleUpdateEventRegistration(registration, patch) {
    if (!selectedRange?.rangeId || !selectedEventId || !registration?.registrationId) {
      return;
    }

    const key = `${registration.registrationId}#${patch.status || "update"}`;
    setUpdatingEventRegistrationKey(key);
    setError("");

    try {
      const updated = await updateEventRegistration(
        selectedRange.rangeId,
        selectedEventId,
        registration.registrationId,
        patch
      );
      const nextRegistrations = eventRegistrations
        .map((item) => (item.registrationId === updated.registrationId ? updated : item))
        .sort((left, right) => String(left.createdAt).localeCompare(String(right.createdAt)));
      setEventRegistrations(nextRegistrations);
      setEventRegistrationsSummary(summarizeEventRegistrations(nextRegistrations));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingEventRegistrationKey("");
    }
  }

  async function handlePromoteNextEventWaitlist() {
    if (!selectedRange?.rangeId || !selectedEventId) {
      return;
    }

    const reason = window.prompt("Promotion reason (optional)");
    if (reason === null) {
      return;
    }

    setUpdatingEventRegistrationKey("promote-next");
    setError("");

    try {
      const updated = await promoteNextEventWaitlist(selectedRange.rangeId, selectedEventId, reason.trim());
      const nextRegistrations = eventRegistrations
        .map((item) => (item.registrationId === updated.registrationId ? updated : item))
        .sort((left, right) => String(left.createdAt).localeCompare(String(right.createdAt)));
      setEventRegistrations(nextRegistrations);
      setEventRegistrationsSummary(summarizeEventRegistrations(nextRegistrations));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingEventRegistrationKey("");
    }
  }

  async function handleQueueEventReminders() {
    if (!selectedRange?.rangeId || !selectedEventId) {
      return;
    }

    const message = window.prompt("Optional reminder message");
    if (message === null) {
      return;
    }

    setUpdatingEventRegistrationKey("queue-reminders");
    setError("");

    try {
      const result = await queueEventReminders(selectedRange.rangeId, selectedEventId, message.trim());
      window.alert(`Queued ${result.queued || 0} reminder notification${result.queued === 1 ? "" : "s"}.${result.skipped ? ` Skipped ${result.skipped}.` : ""}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingEventRegistrationKey("");
    }
  }

  async function handleExportEventRegistrations() {
    if (!selectedRange?.rangeId || !selectedEventId) {
      return;
    }

    setExportingEventRegistrations(true);
    setError("");

    try {
      const result = await downloadEventRegistrationsCsv(selectedRange.rangeId, selectedEventId, eventRegistrationFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingEventRegistrations(false);
    }
  }

  async function handleExportEventAttendance() {
    if (!selectedRange?.rangeId || !selectedEventId) {
      return;
    }

    setExportingEventAttendance(true);
    setError("");

    try {
      const result = await downloadEventAttendanceCsv(selectedRange.rangeId, selectedEventId, eventAttendanceFilters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingEventAttendance(false);
    }
  }

  async function handleRecordEventAttendance(registration, status = "attended", notes = "") {
    if (!selectedRange?.rangeId || !selectedEventId) {
      return;
    }

    const key = `${registration.registrationId || registration.email}#${status}`;
    setRecordingAttendanceKey(key);
    setError("");

    try {
      const created = await recordEventAttendance(selectedRange.rangeId, selectedEventId, {
        registrationId: registration.registrationId,
        memberId: registration.memberId,
        attendeeType: registration.attendeeType || (registration.memberId ? "member" : "manual"),
        source: registration.source || (registration.memberId ? "member" : "free_trial"),
        email: registration.email,
        fullName: registration.fullName,
        chapterId: registration.chapterId,
        chapterName: registration.chapterName,
        memberSnapshot: registration.memberSnapshot,
        status,
        notes
      });
      const nextAttendance = [...eventAttendance, created]
        .sort((left, right) => String(left.checkedInAt).localeCompare(String(right.checkedInAt)));
      setEventAttendance(nextAttendance);
      setEventAttendanceSummary(summarizeEventAttendance(nextAttendance));
    } catch (err) {
      setError(err.message);
    } finally {
      setRecordingAttendanceKey("");
    }
  }

  function buildResourcePayload() {
    const fixedDistances = resourceForm.fixedDistances
      .split(",")
      .map((item) => Number.parseInt(item.trim(), 10))
      .filter((item) => Number.isFinite(item) && item > 0);

    return {
      type: resourceForm.type,
      name: resourceForm.name,
      parentResourceId: resourceForm.parentResourceId || undefined,
      laneCount: Number.parseInt(resourceForm.laneCount || "0", 10),
      laneNamePrefix: resourceForm.laneNamePrefix,
      status: resourceForm.status,
      targetControl: {
        status: resourceForm.targetControlStatus,
        note: resourceForm.targetControlNote,
        cycle: {
          enabled: resourceForm.targetCycleEnabled,
          firingMinutes: Number.parseInt(resourceForm.targetCycleFiringMinutes || "0", 10),
          targetCheckMinutes: Number.parseInt(resourceForm.targetCycleCheckMinutes || "0", 10),
          startsAt: resourceForm.targetCycleStartsAt || undefined,
          note: resourceForm.targetCycleNote
        }
      },
      reservable: resourceForm.reservable,
      capacity: {
        shooters: Number.parseInt(resourceForm.shooterCapacity || "0", 10),
        lanes: Number.parseInt(resourceForm.laneCapacity || "0", 10)
      },
      distance: {
        unit: "yards",
        min: Number.parseInt(resourceForm.distanceMin || "0", 10),
        max: Number.parseInt(resourceForm.distanceMax || "0", 10),
        fixed: fixedDistances
      },
      reservationRules: {
        enabled: resourceForm.reservable,
        minDurationMinutes: Number.parseInt(resourceForm.minDurationMinutes || "30", 10),
        maxDurationMinutes: Number.parseInt(resourceForm.maxDurationMinutes || "120", 10),
        slotSizeMinutes: Number.parseInt(resourceForm.slotSizeMinutes || "30", 10),
        bufferMinutes: Number.parseInt(resourceForm.bufferMinutes || "0", 10),
        cancellationCutoffHours: Number.parseInt(resourceForm.cancellationCutoffHours || "0", 10),
        payment: {
          enabled: resourceForm.reservationFeeEnabled,
          amountCents: Math.round(Number.parseFloat(resourceForm.reservationFeeAmount || "0") * 100),
          currency: resourceForm.reservationFeeCurrency,
          label: resourceForm.reservationFeeLabel
        }
      },
      accessPolicy: {
        requiredQualificationKeys: resourceForm.requiredQualificationKeys
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      },
      availabilityPolicy: {
        inheritRangeHours: resourceForm.availabilityInheritRangeHours,
        startsOn: resourceForm.availabilityStartsOn || undefined,
        endsOn: resourceForm.availabilityEndsOn || undefined,
        openTime: resourceForm.availabilityOpenTime || undefined,
        closeTime: resourceForm.availabilityCloseTime || undefined,
        notes: resourceForm.availabilityNotes || undefined
      },
      attributes: {
        environment: resourceForm.environment,
        assetClass: resourceForm.assetClass,
        serialNumber: resourceForm.serialNumber,
        rentalStatus: resourceForm.rentalStatus,
        maintenanceDueAt: resourceForm.maintenanceDueAt,
        maintenancePlanName: resourceForm.maintenancePlanName,
        maintenanceIntervalDays: resourceForm.maintenanceIntervalDays,
        maintenanceChecklist: resourceForm.maintenanceChecklist,
        maintenanceAssignedTo: resourceForm.maintenanceAssignedTo,
        notes: resourceForm.notes
      }
    };
  }

  function resourceTemplateRules({ enabled = true, min = 60, max = 120, slot = 60, cutoff = 2 } = {}) {
    return {
      enabled,
      minDurationMinutes: min,
      maxDurationMinutes: max,
      slotSizeMinutes: slot,
      bufferMinutes: 0,
      cancellationCutoffHours: cutoff,
      payment: {
        enabled: false,
        amountCents: 0,
        currency: "usd",
        label: "Reservation fee"
      }
    };
  }

  function resourceTemplateAvailability({ status = "members_only", notes = "" } = {}) {
    return {
      publicationStatus: status,
      publicationNote: notes,
      inheritRangeHours: true,
      notes
    };
  }

  function resourceTemplateTarget({ firingMinutes = 0, targetCheckMinutes = 0, note = "" } = {}) {
    return {
      status: "firing_allowed",
      note,
      cycle: {
        enabled: Boolean(firingMinutes && targetCheckMinutes),
        firingMinutes,
        targetCheckMinutes,
        note
      }
    };
  }

  function buildLibertyFacilityResourceTemplate() {
    const base = ({
      type = "bay",
      name,
      reservable = true,
      shooters = 1,
      lanes = 1,
      minDistance = 0,
      maxDistance = 0,
      fixedDistances = [],
      notes = "",
      laneCount = 0,
      laneNamePrefix = "Lane",
      childNames = [],
      childReservable,
      rules = {},
      availability = {},
      target = {}
    }) => ({
      type,
      name,
      status: "open",
      targetControl: resourceTemplateTarget(target),
      reservable,
      laneCount,
      laneNamePrefix,
      childNames,
      childReservable,
      capacity: { shooters, lanes },
      distance: {
        unit: "yards",
        min: minDistance,
        max: maxDistance,
        fixed: fixedDistances
      },
      reservationRules: resourceTemplateRules({ enabled: reservable, ...rules }),
      accessPolicy: { requiredQualificationKeys: [] },
      availabilityPolicy: resourceTemplateAvailability(availability),
      attributes: {
        environment: "outdoor",
        notes
      }
    });

    return [
      base({
        name: "1000-Yard Range",
        shooters: 16,
        lanes: 16,
        minDistance: 1000,
        maxDistance: 1000,
        fixedDistances: [1000],
        notes: "Sixteen precision long-range lanes: eight camera-equipped lanes and eight standard lanes for reservations, training, and competitions.",
        laneCount: 16,
        laneNamePrefix: "1000-Yard Lane",
        childNames: [
          ...Array.from({ length: 8 }, (_, index) => `1000-Yard Camera Lane ${index + 1}`),
          ...Array.from({ length: 8 }, (_, index) => `1000-Yard Standard Lane ${index + 1}`)
        ],
        rules: { max: 180 },
        availability: { notes: "Precision long-range shooting and competitions." }
      }),
      base({
        name: "600-Yard Unknown Distance Range",
        shooters: 10,
        lanes: 10,
        minDistance: 100,
        maxDistance: 600,
        fixedDistances: [600],
        notes: "Steel animal silhouette targets repositioned weekly for realistic range-estimation practice.",
        laneCount: 10,
        laneNamePrefix: "600-Yard Position",
        rules: { max: 180 },
        availability: { notes: "Unknown-distance steel silhouette practice." }
      }),
      base({
        name: "100-Yard General Range",
        reservable: false,
        childReservable: false,
        shooters: 10,
        lanes: 10,
        minDistance: 100,
        maxDistance: 100,
        fixedDistances: [100],
        notes: "General member-use 100-yard benches. Open to members without reservation.",
        laneCount: 10,
        laneNamePrefix: "100-Yard General Bench",
        availability: { status: "open", notes: "General member-use benches, no reservation required." }
      }),
      base({
        name: "100-Yard Reservable Range",
        shooters: 10,
        lanes: 10,
        minDistance: 100,
        maxDistance: 100,
        fixedDistances: [100],
        notes: "Reservable 100-yard range for member reservations, classes, and competitions.",
        laneCount: 10,
        laneNamePrefix: "100-Yard Reservable Bench",
        rules: { max: 180 },
        availability: { notes: "Reservable 100-yard benches for members, classes, and competitions." }
      }),
      base({
        type: "area",
        name: "180-Degree Range",
        shooters: 12,
        lanes: 1,
        minDistance: 0,
        maxDistance: 50,
        fixedDistances: [50],
        notes: "Arced 50-yard-radius range with a flexible multi-directional layout.",
        rules: { max: 240 },
        availability: { notes: "Flexible range for member reservations, private classes, and competitions." }
      }),
      ...[1, 2, 3, 4].map((number) => base({
        name: `50-Yard Mixed-Use Range ${number}`,
        shooters: 6,
        lanes: 1,
        minDistance: 0,
        maxDistance: 50,
        fixedDistances: [50],
        notes: "Reservable in one-hour increments for structured drills integrated with the app.",
        rules: { min: 60, max: 120, slot: 60 },
        availability: { notes: "One-hour member reservations and structured training drills." }
      })),
      base({
        name: "Shotgun Clay Range",
        shooters: 3,
        lanes: 3,
        minDistance: 0,
        maxDistance: 60,
        notes: "Three shooting positions, each equipped with a clay bird thrower.",
        laneCount: 3,
        laneNamePrefix: "Shotgun Clay Position",
        target: {
          firingMinutes: 40,
          targetCheckMinutes: 20,
          note: "Scheduled 20-minute shutdowns on the hour for coordinated target changes on long-range bays."
        },
        rules: { max: 120 },
        availability: { notes: "Clay thrower positions with coordinated hourly shutdowns." }
      }),
      base({
        name: "Pistol Range",
        reservable: false,
        childReservable: false,
        shooters: 20,
        lanes: 20,
        minDistance: 0,
        maxDistance: 25,
        fixedDistances: [7, 15, 25],
        notes: "Always-open pistol range with steel target positions and silhouette paper positions.",
        laneCount: 20,
        laneNamePrefix: "Pistol Position",
        childNames: [
          ...Array.from({ length: 10 }, (_, index) => `Pistol Steel Position ${index + 1}`),
          ...Array.from({ length: 10 }, (_, index) => `Pistol Paper Position ${index + 1}`)
        ],
        target: {
          firingMinutes: 25,
          targetCheckMinutes: 5,
          note: "Brief 5-minute shutdowns every 30 minutes for safe paper target replacement."
        },
        availability: { status: "open", notes: "Always-open pistol range paper section." }
      })
    ];
  }

  async function handleApplyLibertyFacilityTemplate() {
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingResource(true);
    setError("");

    try {
      const createdResources = [];
      for (const payload of buildLibertyFacilityResourceTemplate()) {
        const result = await createResource(selectedRange.rangeId, payload);
        createdResources.push(result.resource, ...(result.childResources || []));
      }
      const nextResources = sortResourcesByName([...createdResources.filter(Boolean), ...resourcesRef.current]);
      resourcesRef.current = nextResources;
      setResources(nextResources);
      setResourceSummary((summary) => ({
        ...(summary || {}),
        summary: summarizeResources(nextResources)
      }));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSavingResource(false);
    }
  }

  async function handleCreateResource(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingResource(true);
    setError("");

    try {
      const result = await createResource(selectedRange.rangeId, buildResourcePayload());
      const createdResources = [result.resource, ...(result.childResources || [])].filter(Boolean);
      const nextResources = sortResourcesByName([...createdResources, ...resources]);
      resourcesRef.current = nextResources;
      setResources(nextResources);
      setResourceSummary((summary) => ({
        ...(summary || {}),
        summary: summarizeResources(nextResources)
      }));
      setResourceForm(defaultResourceForm());
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingResource(false);
    }
  }

  async function handleCreateArmoryItem(payload) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingResource(true);
    setError("");

    try {
      const result = await createResource(selectedRange.rangeId, payload);
      const createdResources = [result.resource, ...(result.childResources || [])].filter(Boolean);
      const nextResources = sortResourcesByName([...createdResources, ...resourcesRef.current]);
      resourcesRef.current = nextResources;
      setResources(nextResources);
      setResourceSummary((summary) => ({
        ...(summary || {}),
        summary: summarizeResources(nextResources)
      }));
      return result.resource;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSavingResource(false);
    }
  }

  async function handleExportResources(extraFilters = {}) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingResources(true);
    setError("");

    try {
      const safeExtraFilters = extraFilters && !extraFilters.nativeEvent ? extraFilters : {};
      const result = await downloadResourcesCsv(selectedRange.rangeId, { ...resourceFilters, ...safeExtraFilters });
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingResources(false);
    }
  }

  async function handleExportFacilityResources() {
    return handleExportResources({ excludeArmory: "true" });
  }

  async function handleUpdateResource(resourceId, patch) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setUpdatingResourceId(resourceId);
    setError("");

    try {
      const updated = await updateResource(selectedRange.rangeId, resourceId, patch);
      const nextResources = resourcesRef.current.map((resource) => (
        resource.resourceId === resourceId ? updated : resource
      ));
      resourcesRef.current = nextResources;
      setResources(nextResources);
      setResourceSummary((summary) => ({
        ...(summary || {}),
        summary: summarizeResources(nextResources)
      }));
      setResourceDetail((current) => (
        current?.resource?.resourceId === resourceId
          ? { ...current, resource: updated }
          : current
      ));
      return updated;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setUpdatingResourceId("");
    }
  }

  async function handleUpdateResourceSectionCommand(resourceId, command) {
    if (!selectedRange?.rangeId) {
      return null;
    }

    setUpdatingResourceId(resourceId);
    setError("");

    try {
      const result = await updateResourceSectionCommand(selectedRange.rangeId, resourceId, command);
      const updatedResources = result?.resources || [];
      const updatedById = new Map(updatedResources.map((resource) => [resource.resourceId, resource]));
      const nextResources = resourcesRef.current.map((resource) => (
        updatedById.get(resource.resourceId) || resource
      ));
      resourcesRef.current = nextResources;
      setResources(nextResources);
      setResourceSummary((summary) => ({
        ...(summary || {}),
        summary: summarizeResources(nextResources)
      }));
      setResourceDetail((current) => {
        if (!current?.resource?.resourceId) {
          return current;
        }
        const updated = updatedById.get(current.resource.resourceId);
        return updated ? { ...current, resource: updated } : current;
      });
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setUpdatingResourceId("");
    }
  }

  function buildResourceBlockPayload() {
    return {
      resourceId: resourceBlockForm.resourceId,
      type: resourceBlockForm.type,
      title: resourceBlockForm.title,
      reason: resourceBlockForm.reason,
      startsAt: new Date(resourceBlockForm.startsAt).toISOString(),
      endsAt: new Date(resourceBlockForm.endsAt).toISOString(),
      appliesToChildren: resourceBlockForm.appliesToChildren,
      recurrence: {
        frequency: resourceBlockForm.recurrenceFrequency,
        interval: Number.parseInt(resourceBlockForm.recurrenceInterval || "1", 10)
      }
    };
  }

  async function handleCreateResourceBlock(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingResourceBlock(true);
    setError("");

    try {
      const block = await createResourceBlock(selectedRange.rangeId, buildResourceBlockPayload());
      const nextBlocks = [block, ...resourceBlocks]
        .sort((a, b) => String(a.startsAt || "").localeCompare(String(b.startsAt || "")));
      setResourceBlocks(nextBlocks);
      setResourceBlocksSummary(summarizeResourceBlocks(nextBlocks));
      setResourceBlockForm((current) => ({
        ...defaultResourceBlockForm(),
        resourceId: current.resourceId
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingResourceBlock(false);
    }
  }

  async function handleUpdateResourceBlock(blockId, patch) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingResourceBlockId(blockId);
    setError("");

    try {
      const updated = await updateResourceBlock(selectedRange.rangeId, blockId, patch);
      const nextBlocks = resourceBlocks
        .map((block) => (block.blockId === blockId ? updated : block))
        .sort((a, b) => String(a.startsAt || "").localeCompare(String(b.startsAt || "")));
      setResourceBlocks(nextBlocks);
      setResourceBlocksSummary(summarizeResourceBlocks(nextBlocks));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingResourceBlockId("");
    }
  }

  async function handleCheckAvailability(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId || !availabilityForm.resourceId) {
      return;
    }

    setCheckingAvailability(true);
    setError("");

    try {
      const result = await checkResourceAvailability(selectedRange.rangeId, availabilityForm.resourceId, {
        startsAt: new Date(availabilityForm.startsAt).toISOString(),
        endsAt: new Date(availabilityForm.endsAt).toISOString()
      });
      setAvailabilityResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setCheckingAvailability(false);
    }
  }

  function buildReservationPayload() {
    return {
      resourceId: reservationForm.resourceId,
      memberId: reservationForm.memberId || undefined,
      customerName: reservationForm.customerName,
      contactEmail: reservationForm.contactEmail,
      startsAt: new Date(reservationForm.startsAt).toISOString(),
      endsAt: new Date(reservationForm.endsAt).toISOString(),
      notes: reservationForm.notes,
      overrideAvailability: reservationForm.overrideAvailability,
      overrideReason: reservationForm.overrideReason,
      source: reservationForm.source || "owner",
      status: reservationForm.status || "confirmed"
    };
  }

  async function handleCreateReservation(event) {
    event.preventDefault();
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingReservation(true);
    setError("");

    try {
      const reservation = await createResourceReservation(selectedRange.rangeId, buildReservationPayload());
      const nextReservations = [reservation, ...resourceReservations]
        .sort((a, b) => String(a.startsAt || "").localeCompare(String(b.startsAt || "")));
      setResourceReservations(nextReservations);
      setResourceReservationsSummary(summarizeResourceReservations(nextReservations));
      setReservationForm((current) => ({
        ...defaultReservationForm(),
        resourceId: current.resourceId
      }));
      setAvailabilityResult(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingReservation(false);
    }
  }

  async function handleUpdateReservation(reservationId, patch) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setUpdatingReservationId(reservationId);
    setError("");

    try {
      const updated = await updateResourceReservation(selectedRange.rangeId, reservationId, patch);
      const nextReservations = resourceReservations
        .map((reservation) => (reservation.reservationId === reservationId ? updated : reservation))
        .sort((a, b) => String(a.startsAt || "").localeCompare(String(b.startsAt || "")));
      setResourceReservations(nextReservations);
      setResourceReservationsSummary(summarizeResourceReservations(nextReservations));
      setAvailabilityResult(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingReservationId("");
    }
  }

  async function handleExportResourceReservations(filters = {}) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingResourceReservations(true);
    setError("");

    try {
      const result = await downloadResourceReservationsCsv(selectedRange.rangeId, filters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingResourceReservations(false);
    }
  }

  async function handleCheckReservationConflicts(filters = {}) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setCheckingReservationConflicts(true);
    setError("");

    try {
      const result = await getResourceReservationConflicts(selectedRange.rangeId, filters);
      setResourceReservationConflicts(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setCheckingReservationConflicts(false);
    }
  }

  async function handleExportResourceBlocks(filters = {}) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setExportingResourceBlocks(true);
    setError("");

    try {
      const result = await downloadResourceBlocksCsv(selectedRange.rangeId, filters);
      const blob = new Blob([result.body], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingResourceBlocks(false);
    }
  }

  async function handleSignOut() {
    setError("");
    if (!DEV_USER_ID.trim()) {
      await signOut();
    }
    setRanges([]);
    setApplications([]);
    setRangeApplications([]);
    setRangeReadiness(null);
    setBusinessHoursForm(defaultBusinessHoursForm());
    setMembershipSummary(null);
    setPaymentSettings(null);
    setPaymentOverviewEvents(null);
    setPaymentOverviewReceipts(null);
    setMembers([]);
    setRangeAccessRecords([]);
    setAuditEvents([]);
    setResourceSummary(null);
    setResources([]);
    setResourceBlocks([]);
    setResourceBlocksSummary(null);
    setResourceReservations([]);
    setResourceReservationsSummary(null);
    setEvents([]);
    setEventsSummary(null);
    setSelectedEventId("");
    setEventRegistrations([]);
    setEventRegistrationsSummary(null);
    setEventAttendance([]);
    setEventAttendanceSummary(null);
    setDonations([]);
    setDonationsSummary(null);
    setDonationSettings(null);
    setAvailabilityResult(null);
    navigate("/console");
    resetMembershipTypeForm();
    setResourceForm(defaultResourceForm());
    setResourceBlockForm(defaultResourceBlockForm());
    setAvailabilityForm(defaultAvailabilityForm());
    setReservationForm(defaultReservationForm());
    setEventForm(defaultEventForm());
    setEventRegistrationForm(defaultEventRegistrationForm());
    setDonationForm(defaultDonationForm());
    setDonationSettingsForm(defaultDonationSettingsForm());
    setAccessForm(defaultAccessForm());
    setActiveRangeId("");
    setAuth({ status: "signed-out", user: null, attributes: null });
  }

  async function handlePublicSignOut() {
    const targetSlug = publicRangeSlug;
    setPublicAuthPrompt(false);
    await handleSignOut();
    if (targetSlug) {
      navigate(`/r/${encodeURIComponent(targetSlug)}`);
    }
  }

  if (isLegacyMemberPortalRoute) {
    return <Navigate replace to={`/member/r/lox-loaded${location.search || ""}${location.hash || ""}`} />;
  }

  if (isPlatformLandingRoute) {
    return <LandingPage />;
  }

  if (isPublicRangeRoute || isMemberConsoleRoute || isPublicDonationReceiptRoute || isPublicReservationCompleteRoute || isPublicEventRegistrationCompleteRoute) {
    if (isCustomPublicHost && (publicDomainLoading || !publicRangeSlug)) {
      return (
        <main className="auth-shell">
          <div className="empty-state">
            {publicDomainError || "RangeGrid is opening this website..."}
          </div>
        </main>
      );
    }

    if (isMemberConsoleRoute && auth.status === "checking") {
      return (
        <main className="auth-shell">
          <div className="empty-state">Checking member access...</div>
        </main>
      );
    }

    if ((publicAuthPrompt || isMemberConsoleRoute) && !isAuthenticated) {
      return (
        <AuthPanel
          contextLabel={publicAuthContextLabel}
          contextName={publicAuthContextName}
          contextShortName={publicAuthContextShortName}
          surface="public"
          onAuthenticated={handlePublicAuthenticated}
        />
      );
    }

    if (isPublicDonationReceiptRoute) {
      return (
        <PublicDonationReceiptPage
          slug={publicDonationReceiptContext.slug}
          donationId={publicDonationReceiptContext.donationId}
        />
      );
    }

    return (
      <PublicRangePage
        slug={publicRangeSlug}
        isAuthenticated={isAuthenticated}
        memberAccount={{ label: ownerLabel }}
        memberConsoleMode={isMemberConsoleRoute}
        onMemberSignOut={handlePublicSignOut}
        onRequireAuth={handlePublicRequireAuth}
        eventCompletionEventId={publicEventRegistrationCompleteContext.eventId}
        eventRegistrationCompletionId={publicEventRegistrationCompleteContext.registrationId}
        reservationCompletionId={publicReservationCompleteContext.reservationId}
      />
    );
  }

  if (auth.status === "checking") {
    return (
      <main className="auth-shell">
        <div className="empty-state">Checking sign-in status...</div>
      </main>
    );
  }

  if (!isAuthenticated) {
    return <AuthPanel onAuthenticated={refreshAuth} />;
  }

  async function handleUpdateEventAttendance(attendance, updates = {}) {
    if (!selectedRange?.rangeId || !selectedEventId || !attendance?.attendanceId) {
      return;
    }

    const key = `${attendance.registrationId || attendance.email || attendance.attendanceId}#${updates.status || updates.rangeFeeStatus || attendance.status || "attended"}`;
    setRecordingAttendanceKey(key);
    setError("");

    try {
      const updated = await updateEventAttendance(
        selectedRange.rangeId,
        selectedEventId,
        attendance.attendanceId,
        updates
      );
      const nextAttendance = eventAttendance
        .map((entry) => entry.attendanceId === updated.attendanceId ? updated : entry)
        .sort((left, right) => String(left.checkedInAt).localeCompare(String(right.checkedInAt)));
      setEventAttendance(nextAttendance);
      setEventAttendanceSummary(summarizeEventAttendance(nextAttendance));
    } catch (err) {
      setError(err.message);
    } finally {
      setRecordingAttendanceKey("");
    }
  }

  const noOwnerWorkspaceAccess = !loading && ranges.length === 0 && !selectedRange;
  const isOwnerOnboardingRoute = location.pathname === "/console/onboarding" || location.pathname === "/console/ranges/new";
  if (
    workspaceOnlyAccess &&
    !isTeamWorkspaceRoute &&
    (location.pathname === "/console" || isOwnerOnboardingRoute || /^\/console\/ranges\/[^/]+/.test(location.pathname))
  ) {
    return <Navigate replace to={`/workspace/ranges/${selectedRange.rangeId}${location.hash || ""}`} />;
  }

  if (noOwnerWorkspaceAccess && location.pathname === "/console") {
    return <Navigate replace to="/console/onboarding" />;
  }

  const baseViewCopy = buildViewCopy({ location, routeModuleKey });
  const vocabularyViewTitles = {
    "daily-ops": workspaceVocabulary.operationsLabel,
    resources: workspaceVocabulary.facilitiesLabel
  };
  const isHomePathForCopy = location.pathname === "/console" || /^\/console\/ranges\/[^/]+$/.test(location.pathname);
  const noAssignedOwnerWorkspace = isHomePathForCopy && noOwnerWorkspaceAccess;
  const currentViewCopy = {
    ...baseViewCopy,
    ...(vocabularyViewTitles[routeModuleKey] ? { title: vocabularyViewTitles[routeModuleKey] } : {}),
    ...(isHomePathForCopy ? {
      description: chapterOrganizationWorkspace
        ? ""
        : `Monitor daily operations, member flow, ${workspaceVocabulary.workspace.toLowerCase()} readiness, and launch blockers.`
    } : {}),
    ...(noAssignedOwnerWorkspace ? {
      title: "Start RangeGrid",
      iconKey: "launch",
      description: "Create your owner workspace and begin launch setup."
    } : {}),
    ...(location.pathname.endsWith("/settings") ? {
      title: workspaceVocabulary.setupLabel,
      description: `Configure ${workspaceVocabulary.workspace.toLowerCase()} profile, operating schedule, and member portal actions.`
    } : {}),
    ...(location.pathname.endsWith("/notifications") ? {
      description: ""
    } : {}),
    ...(location.pathname.endsWith("/audit") ? {
      description: `Review recent activity for the active ${workspaceVocabulary.workspace.toLowerCase()}.`
    } : {}),
    ...(routeModuleKey === "staff-portal" ? { description: teamWorkspaceViewDescription(selectedRange) } : {}),
	    ...(isTeamWorkspaceRoute ? {
	      title: routeWorkspaceApp === "members"
	        ? "Members"
	        : isWorkspaceProgramsRoute
	          ? "Programs & Events"
	            : isWorkspaceNotificationsRoute
	              ? "Notifications"
	          : "Home",
	      iconKey: routeWorkspaceApp === "members"
	        ? "membership"
	        : isWorkspaceProgramsRoute
	          ? "events"
	          : isWorkspaceNotificationsRoute
	            ? "notifications"
	          : "console-home",
	      description: ""
	    } : {})
  };
  const isApplicationsRoute = location.pathname.endsWith("/applications");
  const workspaceLayoutCollapsed = sidebarCollapsed || noOwnerWorkspaceAccess;
  const communicationsConfig = selectedRange?.config?.communications || {};
  const communicationsProductionReady = Boolean(
    communicationsConfig.enabled === true &&
    communicationsConfig.broadcastDeliveryEnabled === true &&
    communicationsConfig.unsubscribeHandlingEnabled === true &&
    communicationsConfig.bounceComplaintHandlingEnabled === true &&
    communicationsConfig.sendHistoryEnabled === true &&
    notificationPreferences?.emailSender?.activeEmail &&
    notificationPreferences?.emailSender?.activeVerifiedAt
  );
  const accountingProductionReady = Boolean(
    selectedRange?.config?.accounting?.enabled &&
    paymentSettings?.configured !== false &&
    paymentSettings?.publishableKeyConfigured !== false &&
    paymentSettings?.stripe?.connectedAccountId &&
    paymentSettings?.stripe?.chargesEnabled &&
    paymentSettings?.stripe?.payoutsEnabled
  );
  const hideProductionBlockedOwnerApp = (item) => (
    ((item.key || item.moduleKey || item.iconKey) === "communications" && !communicationsProductionReady) ||
    ((item.key || item.moduleKey || item.iconKey) === "accounting" && !accountingProductionReady)
  );
  const productionMenuOptionalApps = selectedRange?.config?.productionMenu?.optionalApps || {};
  const showOptionalOwnerApp = (item) => {
    const key = item.key || item.moduleKey || item.iconKey;
    if (item.enabled === true) {
      return true;
    }
    if (!["armory", "leads", "reports", "audit"].includes(key)) {
      return true;
    }
    return Boolean(
      productionMenuOptionalApps[key] ||
      selectedRange?.config?.applications?.[key]?.showInNavigation ||
      selectedRange?.config?.[key]?.showInNavigation
    );
  };
  const coreOwnerMenuKeys = new Set(["home", "console-home", "application-hub", "launch", "access", "range-setup"]);
  const isCoreOwnerMenuItem = (item) => coreOwnerMenuKeys.has(item.key || item.iconKey || item.moduleKey);
  const showInPrimaryOwnerMenu = (item) => isCoreOwnerMenuItem(item) || item.enabled === true;
  const applicationUnavailableMessage = (name) => {
    if (rangeRouteNotFound) {
      return isTeamWorkspaceRoute
        ? "This workspace could not be found for the signed-in team member."
        : "This range could not be found for the signed-in owner.";
    }
    if (rangeApplicationsLoading) {
      return "Loading application state...";
    }
    return `${name} is not enabled for this range.`;
  };
  const memberProgramGroupLabel = chapterOrganizationWorkspace ? "Chapters and programs" : "Members and programs";
	  const chapterWorkspaceNavItems = [
	    {
	      label: "Home",
	      active: isTeamWorkspaceRoute && routeWorkspaceApp !== "members" && !isWorkspaceProgramsRoute && !isWorkspaceNotificationsRoute,
	      path: `${selectedRangePath}/apps/home`,
	      iconKey: "console-home"
	    },
    {
      label: "Members",
      active: isTeamWorkspaceRoute && routeWorkspaceApp === "members",
      path: `${selectedRangePath}/apps/members`,
      iconKey: "membership",
      scopeKey: "membership"
    },
	    {
	      label: "Programs & Events",
	      active: isTeamWorkspaceRoute && isWorkspaceProgramsRoute,
	      path: `${selectedRangePath}/apps/events`,
	      iconKey: "events",
	      scopeKey: "events"
	    },
	    {
	      label: "Notifications",
	      active: isTeamWorkspaceRoute && isWorkspaceNotificationsRoute,
	      path: `${selectedRangePath}/apps/notifications`,
      iconKey: "notifications",
      scopeKey: "notifications"
    }
  ].filter((item) => !item.scopeKey || workspaceAppAllowed(item.scopeKey));
  const allServiceLauncherItems = [
    {
      key: "home",
      applicationId: "home",
      moduleKey: "home",
      label: "Home",
      description: "Owner command view with chapter links and the monthly class calendar.",
      category: "Command",
      enabled: isRangeApplicationEnabled("home"),
      iconKey: "console-home",
      path: selectedRangePath,
      keywords: "home dashboard calendar command monthly class schedule chapters programs members"
    },
    {
      key: "daily-ops",
      applicationId: "daily-ops",
      moduleKey: "daily-ops",
      label: workspaceVocabulary.operationsLabel,
      description: chapterOrganizationWorkspace
        ? "Chapter program rosters, attendance, participant handoff, and local operating signals."
        : "Daily line control, shooter check-in, safety records, equipment readiness, cease-fire notes, and same-day actions.",
      category: "Daily operations",
      enabled: isRangeApplicationEnabled("daily-ops"),
      iconKey: "daily-ops",
      path: chapterOrganizationWorkspace ? `${selectedRangePath}/apps/staff-portal#staff-programs-roster` : `${selectedRangePath}/apps/daily-ops`,
      keywords: "daily ops operations range control shooter check in cease fire line status compliance safety equipment rentals chapter program roster attendance"
    },
    {
      key: "resources",
      applicationId: "resources",
      moduleKey: "resources",
      label: workspaceVocabulary.facilitiesLabel,
      description: chapterOrganizationWorkspace
        ? "Chapter directory, public locations, leader coverage, and local chapter readiness."
        : "Physical zones, ranges, and lanes for the range map.",
      category: "Daily operations",
      enabled: chapterOrganizationWorkspace ? chaptersEnabled : resourcesEnabled,
      iconKey: "resources",
      path: chapterOrganizationWorkspace ? `${selectedRangePath}/apps/chapters#chapters-directory` : `${selectedRangePath}/apps/resources`,
      keywords: "facilities resources chapters locations lanes bays availability maintenance access restriction owned range assets"
    },
    {
      key: "armory",
      applicationId: "armory",
      moduleKey: "armory",
      label: "Armory",
      description: "Range-owned firearms, serials, round counts, checkout, return, malfunction notes, and firearm maintenance.",
      category: "Daily operations",
      enabled: isRangeApplicationEnabled("armory"),
      iconKey: "armory",
      path: `${selectedRangePath}/apps/armory`,
      keywords: "armory firearms guns rentals serial caliber round count checkout return maintenance"
    },
    {
      key: "membership",
      applicationId: "membership",
      moduleKey: "membership",
      label: "Membership",
      description: "Members, applications, waivers, plans, reviews, and guest passes.",
      category: memberProgramGroupLabel,
      enabled: membershipEnabled,
      iconKey: "membership",
      path: `${selectedRangePath}/apps/membership`,
      keywords: "member membership waiver plan applicant review guest pass qualification"
    },
    {
      key: "insights",
      applicationId: "insights",
      moduleKey: "insights",
      label: "Insights",
      description: chapterOrganizationWorkspace
        ? "Chapter growth, member movement, class coverage, and participation signals."
        : "Retention, range usage, member journeys, and resource activity trends.",
      category: memberProgramGroupLabel,
      enabled: insightsEnabled,
      iconKey: "insights",
      path: `${selectedRangePath}/apps/insights`,
      keywords: "insights dashboard growth members participation frequency chapter health journey retention reservations resources usage"
    },
    {
      key: "events",
      applicationId: "events",
      moduleKey: "events",
      label: "Programs & Events",
      description: "Classes, matches, open shoots, rosters, attendance, registration windows, and waitlists.",
      category: memberProgramGroupLabel,
      enabled: eventsEnabled,
      iconKey: "events",
      path: `${selectedRangePath}/apps/events`,
      keywords: "programs events classes matches roster attendance registration waitlist"
    },
    {
      key: "non-members",
      applicationId: "non-members",
      moduleKey: "non-members",
      label: "Guest Leads",
      description: "Guest leads, attendance context, visit history, and follow-up templates.",
      category: memberProgramGroupLabel,
      enabled: isRangeApplicationEnabled("non-members"),
      iconKey: "non-members",
      path: `${selectedRangePath}/apps/non-members`,
      keywords: "non member attendees guest leads outreach visit history follow up"
    },
    {
      key: "chapters",
      applicationId: "chapters",
      moduleKey: "chapters",
      label: "Chapters",
      description: "Chapter directory, city groups, leader assignment, and public chapter visibility.",
      category: memberProgramGroupLabel,
      enabled: chaptersEnabled,
      iconKey: "chapters",
      path: `${selectedRangePath}/apps/chapters`,
      keywords: "chapters city groups leaders instructors organization chapter directory"
    },
    {
      key: "staff-portal",
      applicationId: "staff-portal",
      moduleKey: "staff-portal",
      label: "Team Workspace",
      description: "Chapter lead and instructor workspace for scoped programs, rosters, attendance, messages, tasks, and local insight.",
      category: memberProgramGroupLabel,
      enabled: teamWorkspaceAvailable,
      iconKey: "staff-portal",
      path: `${selectedRangePath}/apps/staff-portal`,
      keywords: "team workspace chapter leader instructor roster attendance check in class participant task message insight"
    },
    {
      key: "leads",
      applicationId: "leads",
      moduleKey: "leads",
      label: "Leads",
      description: "Public update requests, presale interest, launch campaigns, and visitor follow-up.",
      category: "Business",
      enabled: leadsEnabled,
      iconKey: "leads",
      path: `${selectedRangePath}/apps/leads`,
      keywords: "leads public updates presale launch visitors contact requests gun show campaign"
    },
    {
      key: "payments",
      applicationId: "payments",
      moduleKey: "payments",
      label: "Payments",
      description: "Stripe workspace, receipts, accounting, payouts, revenue, and reconciliation.",
      category: "Business",
      enabled: isRangeApplicationEnabled("payments"),
      iconKey: "payments",
      path: `${selectedRangePath}/payments`,
      keywords: "payments stripe accounting finance revenue receipt payout reconciliation"
    },
    {
      key: "notifications",
      applicationId: "notifications",
      moduleKey: "notifications",
      label: "Notifications",
      description: chapterOrganizationWorkspace
        ? "Chapter updates, class signoffs, and member-change notices."
        : "Review message delivery, approval, failed sends, and audience settings.",
      category: "Business",
      enabled: isRangeApplicationEnabled("notifications"),
      iconKey: "notifications",
      path: `${selectedRangePath}/notifications`,
      keywords: "notifications messages delivery review approval failed sends audience email"
    },
    {
      key: "communications",
      applicationId: "communications",
      moduleKey: "communications",
      label: "Communications",
      description: "Draft member messages, preview copy, and queue review messages.",
      category: "Business",
      enabled: isRangeApplicationEnabled("communications"),
      iconKey: "communications",
      path: `${selectedRangePath}/apps/communications`,
      keywords: "communications messages email member update chapter announcement review copy"
    },
    {
      key: "reports",
      applicationId: "reports",
      moduleKey: "reports",
      label: "Reports",
      description: "Membership, program, booking, export, and operating summaries.",
      category: "Business",
      enabled: isRangeApplicationEnabled("reports"),
      iconKey: "reports",
      path: `${selectedRangePath}/apps/reports`,
      keywords: "reports exports summaries active members programs bookings operations"
    },
    {
      key: "accounting",
      applicationId: "accounting",
      moduleKey: "accounting",
      label: "Accounting",
      description: "Payment, donation, program, and reservation reconciliation.",
      category: "Business",
      enabled: isRangeApplicationEnabled("accounting"),
      iconKey: "accounting",
      path: `${selectedRangePath}/apps/accounting`,
      keywords: "accounting reconciliation payments donations receipts finance ledger"
    },
    {
      key: "launch",
      applicationId: "launch",
      moduleKey: "launch",
      label: "Launch",
      description: "Launch readiness checklist and remaining public-flow blockers.",
      category: "Setup",
      enabled: isRangeApplicationEnabled("launch"),
      iconKey: "launch",
      path: `${selectedRangePath}/apps/launch`,
      keywords: "launch readiness blockers checklist publish public signup"
    },
    {
      key: "access",
      applicationId: "access",
      moduleKey: "access",
      label: "Access",
      description: "Invite owners, admins, chapter leaders, instructors, and staff with the right chapter and application scope.",
      category: "Setup",
      enabled: isRangeApplicationEnabled("access") && Boolean(canManageAccess),
      iconKey: "access",
      path: `${selectedRangePath}/access`,
      keywords: "access invite role permission chapter leader instructor staff scope"
    },
    {
      key: "audit",
      applicationId: "audit",
      moduleKey: "audit",
      label: "Audit",
      description: "Recent owner, staff, member, and system activity for the active workspace.",
      category: "Setup",
      enabled: isRangeApplicationEnabled("audit"),
      iconKey: "audit",
      path: `${selectedRangePath}/audit`,
      keywords: "audit activity history log owner staff member changes"
    },
    {
      key: "range-setup",
      applicationId: "range-setup",
      moduleKey: "range-setup",
      label: workspaceVocabulary.setupLabel,
      description: `${workspaceVocabulary.workspace} profile, operating schedule, portal appearance, and member-facing actions.`,
      category: "Setup",
      enabled: isRangeApplicationEnabled("range-setup"),
      iconKey: "range-setup",
      path: `${selectedRangePath}/settings`,
      keywords: "settings setup range profile hours schedule branding logo member portal actions"
    }
  ].filter((item) => (
    applicationAvailableForWorkspace(item, selectedRange || {}, selectedWorkspaceTemplateKey) &&
    !hideProductionBlockedOwnerApp(item)
  ));
  const serviceLauncherItems = allServiceLauncherItems.filter((item) => (
    showOptionalOwnerApp(item) &&
    showInPrimaryOwnerMenu(item)
  ));
  const ownerNavGroupOrder = ["Command", "Daily operations", memberProgramGroupLabel, "Business", "Setup"];
  const ownerNavItems = [
    {
      label: "Workspace Menu",
      active: isApplicationsRoute,
      disabled: !selectedRange,
      enabled: Boolean(selectedRange),
      path: selectedRange ? `${selectedRangePath}/applications` : "/console",
      iconKey: "application-hub",
      group: "Command"
    },
    ...serviceLauncherItems
      .filter((item) => item.enabled === true)
      .map((item) => {
        const pathWithoutHash = String(item.path || "").split("#")[0].split("?")[0];
        const homeActive = item.key === "home" && isRangeDashboardRoute;
        const exactActive = pathWithoutHash && location.pathname === pathWithoutHash;
        const childActive = pathWithoutHash && pathWithoutHash !== selectedRangePath && location.pathname.startsWith(`${pathWithoutHash}/`);

        return {
          label: item.label,
          active: Boolean(homeActive || exactActive || childActive),
          disabled: false,
          enabled: true,
          path: item.path,
          iconKey: item.iconKey,
          group: item.category || "Applications"
        };
      })
  ];
  const ownerNavGroups = ownerNavGroupOrder
    .map((group) => ({
      group,
      items: ownerNavItems.filter((item) => item.group === group)
    }))
    .filter((group) => group.items.length > 0);
  const serviceSearchNeedle = serviceSearchQuery.trim().toLowerCase();
  const serviceSearchResults = (serviceSearchNeedle
    ? serviceLauncherItems.filter((item) => [
      item.label,
      item.description,
      item.category,
      item.keywords
    ].join(" ").toLowerCase().includes(serviceSearchNeedle))
    : serviceLauncherItems
  ).slice(0, 8);
  const chapterProgramsRouteElement = (eventsEnabled || applicationGateLoading) ? (
    <EventsPage
      eventAttendance={eventAttendance}
      eventAttendanceFilters={eventAttendanceFilters}
      eventAttendanceLoading={eventAttendanceLoading}
      eventAttendanceSummary={eventAttendanceSummary}
      eventFilters={eventFilters}
      eventForm={eventForm}
      editingEventId={editingEventId}
      exportingEventAttendance={exportingEventAttendance}
      exportingEventRegistrations={exportingEventRegistrations}
      exportingEvents={exportingEvents}
      events={events}
      eventRegistrationFilters={eventRegistrationFilters}
      eventRegistrationForm={eventRegistrationForm}
      eventRegistrations={eventRegistrations}
      eventRegistrationsLoading={eventRegistrationsLoading}
      eventRegistrationsSummary={eventRegistrationsSummary}
      chapters={workspaceEventChapterOptions}
      chaptersEnabled={workspaceEventChapterOptions.length > 0}
      chaptersLoading={chaptersLoading}
      chapterScopeRequired={workspaceOnlyAccess && workspaceEventChapterOptions.length > 0}
      canCreateEvent={!workspaceOnlyAccess || selectedAccessFeaturePermissions.events.createClasses}
      canRequestClassChanges={!workspaceOnlyAccess || selectedAccessFeaturePermissions.events.requestClassChanges}
      canRequestClassCancellation={!workspaceOnlyAccess || selectedAccessFeaturePermissions.events.requestClassCancellation}
      canSignOffEvent
      canReviewEventApproval={false}
      loading={eventsLoading || applicationGateLoading}
      members={members}
      membersLoading={membersLoading}
      onCreateEvent={handleCreateEvent}
      onCancelEventEdit={handleCancelEventEdit}
      onEditEvent={handleEditEvent}
      onCreateEventRegistration={handleCreateEventRegistration}
      onExportEvents={handleExportEvents}
      onExportEventAttendance={handleExportEventAttendance}
      onExportEventRegistrations={handleExportEventRegistrations}
      onPromoteNextEventWaitlist={handlePromoteNextEventWaitlist}
      onQueueEventReminders={handleQueueEventReminders}
      onRecordEventAttendance={handleRecordEventAttendance}
      onReviewEventApproval={handleReviewEventApproval}
      onSignOffEvent={handleSignOffEvent}
      onUpdateEventAttendance={handleUpdateEventAttendance}
      onUpdateEvent={handleUpdateEvent}
      onUpdateEventRegistration={handleUpdateEventRegistration}
      onStartStripeConnect={handleStartStripeConnect}
      paymentSettings={paymentSettings}
      paymentsLoading={paymentsLoading}
      recordingAttendanceKey={recordingAttendanceKey}
      resources={resources}
      savingEvent={savingEvent}
      savingEventRegistration={savingEventRegistration}
      selectedEventId={selectedEventId}
      selectedRange={selectedRange}
      startingStripeConnect={startingStripeConnect}
      setEventAttendanceFilters={setEventAttendanceFilters}
      setEventFilters={setEventFilters}
      setEventForm={setEventForm}
      setEventRegistrationFilters={setEventRegistrationFilters}
      setEventRegistrationForm={setEventRegistrationForm}
      setSelectedEventId={setSelectedEventId}
      summary={eventsSummary}
      membershipSummary={membershipSummary}
      updatingEventId={updatingEventId}
      updatingEventRegistrationKey={updatingEventRegistrationKey}
    />
  ) : (
    <EmptyPanel
      title="Programs & Events unavailable"
      message={applicationUnavailableMessage("Programs & Events")}
      actionLabel={selectedRange && !isTeamWorkspaceRoute ? "Open applications" : "Open home"}
      onAction={() => navigate(selectedRange && !isTeamWorkspaceRoute ? `${selectedRangePath}/applications` : selectedRangePath)}
    />
  );
  const chapterNotificationsRouteElement = (
    <NotificationsPage
      exportingNotifications={exportingNotifications}
      loading={notificationsLoading}
      members={members}
      notificationFilters={notificationFilters}
      notifications={workspaceScopedNotifications}
      notificationPreferences={notificationPreferences}
      chapters={workspaceEventChapterOptions}
      onExportNotifications={handleExportNotifications}
      onSaveNotificationPreferences={handleSaveNotificationPreferences}
      onDeliverNotification={handleDeliverNotification}
      onReviewEventApproval={handleReviewEventApproval}
      onUpdateNotificationStatus={handleUpdateNotificationStatus}
      savingNotificationPreferences={savingNotificationPreferences}
      selectedRange={selectedRange}
      setNotificationFilters={setNotificationFilters}
      summary={notificationSummary}
      updatingNotificationId={updatingNotificationId}
      workspaceMode="chapter"
    />
  );
  const nonMemberAttendeesRouteElement = (
    <NonMemberAttendeesPage
      attendees={nonMemberAttendees}
      loading={nonMemberAttendeesLoading}
      notificationPreferences={notificationPreferences}
      onSaveNotificationPreferences={handleSaveNotificationPreferences}
      onRefreshSenderEmailVerification={handleRefreshSenderEmailVerification}
      onSendGuestLeadEmail={handleSendGuestLeadEmail}
      onStartSenderEmailVerification={handleStartSenderEmailVerification}
      savingNotificationPreferences={savingNotificationPreferences}
      selectedRange={selectedRange}
      sendingGuestLeadEmailId={sendingGuestLeadEmailId}
      summary={nonMemberAttendeesSummary}
      updatingSenderEmail={updatingSenderEmail}
    />
  );
  const teamWorkspaceUnavailableElement = (
    <EmptyPanel
      title="Team Workspace unavailable"
      message={chapterOrganizationWorkspace
        ? applicationUnavailableMessage("Team Workspace")
        : "This workspace does not use chapter or scoped team workspaces yet. Use Access to manage owners, admins, staff, and instructors for this range."}
      actionLabel={selectedRange && !isTeamWorkspaceRoute ? "Open workspace menu" : "Open home"}
      onAction={() => navigate(selectedRange && !isTeamWorkspaceRoute ? `${selectedRangePath}/applications` : selectedRangePath)}
    />
  );
	  const teamWorkspaceRouteElement = !teamWorkspaceAvailable
      ? teamWorkspaceUnavailableElement
      : isWorkspaceProgramsRoute
	    ? workspaceAppAllowed("events")
        ? chapterProgramsRouteElement
        : (
          <EmptyPanel
            title="Programs & Events unavailable"
            message="The owner has not enabled this workspace for your chapter lead access."
            actionLabel="Open home"
            onAction={() => navigate(selectedRangePath)}
          />
        )
	    : isWorkspaceNotificationsRoute
	      ? workspaceAppAllowed("notifications")
          ? chapterNotificationsRouteElement
          : (
            <EmptyPanel
              title="Notifications unavailable"
              message="The owner has not enabled this workspace for your chapter lead access."
              actionLabel="Open home"
              onAction={() => navigate(selectedRangePath)}
            />
          )
	        : (teamWorkspaceAvailable || rangeApplicationsLoading) ? (
    <StaffPortalPage
      chapters={chapters}
      eventAttendance={eventAttendance}
      eventAttendanceFilters={eventAttendanceFilters}
      eventAttendanceLoading={eventAttendanceLoading}
      eventAttendanceSummary={eventAttendanceSummary}
      eventFilters={eventFilters}
      eventRegistrations={eventRegistrations}
      eventRegistrationFilters={eventRegistrationFilters}
      eventRegistrationsLoading={eventRegistrationsLoading}
      eventRegistrationsSummary={eventRegistrationsSummary}
      events={events}
      loading={eventsLoading || rangeApplicationsLoading}
      members={members}
      membersLoading={membersLoading}
      membershipSummary={membershipSummary}
      notifications={notifications}
      notificationsLoading={notificationsLoading}
      onExportEventAttendance={handleExportEventAttendance}
      onExportEventRegistrations={handleExportEventRegistrations}
      onRecordEventAttendance={handleRecordEventAttendance}
      onSignOffEvent={handleSignOffEvent}
      onUpdateEventAttendance={handleUpdateEventAttendance}
      onUpdateEventRegistration={handleUpdateEventRegistration}
      recordingAttendanceKey={recordingAttendanceKey}
      selectedEventId={selectedEventId}
      selectedRange={selectedRange}
      setEventAttendanceFilters={setEventAttendanceFilters}
      setEventFilters={setEventFilters}
      setEventRegistrationFilters={setEventRegistrationFilters}
      setSelectedEventId={setSelectedEventId}
      updatingEventId={updatingEventId}
		      updatingEventRegistrationKey={updatingEventRegistrationKey}
	      workspaceAppModule={!isTeamWorkspaceRoute && chapterOrganizationWorkspace ? "programs" : routeWorkspaceModule}
	      workspaceAppBasePath={isTeamWorkspaceRoute ? selectedRangePath : ""}
	      workspaceRoleOverride={!isTeamWorkspaceRoute && chapterOrganizationWorkspace ? "chapter_leader" : ""}
	    />
  ) : (
    <EmptyPanel
      title="Chapter lead portal unavailable"
      message={applicationUnavailableMessage("Chapter lead portal")}
      actionLabel={selectedRange && !isTeamWorkspaceRoute ? "Open applications" : "Open home"}
      onAction={() => navigate(selectedRange && !isTeamWorkspaceRoute ? `${selectedRangePath}/applications` : selectedRangePath)}
    />
  );

  function openServiceLauncherItem(item) {
    if (!item || !item.enabled) {
      return;
    }
    navigate(item.path);
    setServiceSearchQuery("");
    setServiceSearchFocused(false);
  }

  async function handleCreateReservationFromPayload(payload) {
    if (!selectedRange?.rangeId) {
      return;
    }

    setSavingReservation(true);
    setError("");

    try {
      const reservation = await createResourceReservation(selectedRange.rangeId, payload);
      const nextReservations = [reservation, ...resourceReservations]
        .sort((a, b) => String(a.startsAt || "").localeCompare(String(b.startsAt || "")));
      setResourceReservations(nextReservations);
      setResourceReservationsSummary(summarizeResourceReservations(nextReservations));
      setAvailabilityResult(null);
      return reservation;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSavingReservation(false);
    }
  }

  function handleActiveRangeChange(event) {
    const nextRangeId = event.target.value;
    if (!nextRangeId || nextRangeId === selectedRange?.rangeId) {
      return;
    }

    if (!ranges.some((range) => range.rangeId === nextRangeId)) {
      return;
    }

    setActiveRangeId(nextRangeId);

    const rangeRouteMatch = location.pathname.match(/^\/(?<surface>console|workspace)\/ranges\/[^/]+(?<suffix>.*)$/);
    if (rangeRouteMatch?.groups) {
      navigate(`/${rangeRouteMatch.groups.surface}/ranges/${nextRangeId}${rangeRouteMatch.groups.suffix || ""}${location.search}${location.hash}`);
      return;
    }

    navigate(`/console/ranges/${nextRangeId}`);
  }

  const workspaceChapterContextLabel = workspaceEventChapterOptions.length === 1
    ? workspaceEventChapterOptions[0]?.name
    : workspaceEventChapterOptions.length > 1
      ? `${workspaceEventChapterOptions.length} assigned chapters`
      : "Assigned chapter";

  return (
    <div className={`console-shell ${isTeamWorkspaceRoute ? "is-team-workspace-route" : ""}`}>
      <header className="console-header">
        <button
          type="button"
          className="console-brand"
          onClick={() => navigate(selectedRangePath)}
          aria-label={isTeamWorkspaceRoute ? "Open chapter lead home" : "Open RangeGrid console home"}
        >
          <span
            className={`console-service-icon ${selectedRange?.branding?.consoleIconUrl || selectedRange?.branding?.logoUrl ? "has-image" : ""}`}
            aria-hidden="true"
          >
            {selectedRange?.branding?.consoleIconUrl || selectedRange?.branding?.logoUrl ? (
              <img src={selectedRange.branding.consoleIconUrl || selectedRange.branding.logoUrl} alt="" />
            ) : (
              isTeamWorkspaceRoute ? (selectedRange?.branding?.shortName || "CL") : "RG"
            )}
          </span>
          <div>
            <strong>{isTeamWorkspaceRoute ? (selectedRange?.displayName || selectedRange?.name || "Chapter Lead") : "RangeGrid"}</strong>
            {isTeamWorkspaceRoute && <span>Chapter workspace</span>}
          </div>
        </button>
        {isTeamWorkspaceRoute ? (
          <div className="console-operator-context" aria-label="Chapter lead context">
            <span>Chapter Lead</span>
            <strong>{currentViewCopy.title}</strong>
          </div>
        ) : noOwnerWorkspaceAccess ? (
          <div className="console-operator-context" aria-label="Owner onboarding context">
            <span>Owner onboarding</span>
            <strong>{isOwnerOnboardingRoute ? "Create workspace" : "Start RangeGrid"}</strong>
          </div>
        ) : (
          <div className="console-service-search">
            <label htmlFor="console-service-search">Search applications</label>
            <input
              id="console-service-search"
              value={serviceSearchQuery}
              onBlur={() => window.setTimeout(() => setServiceSearchFocused(false), 120)}
              onChange={(event) => setServiceSearchQuery(event.target.value)}
              onFocus={() => setServiceSearchFocused(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  openServiceLauncherItem(serviceSearchResults.find((item) => item.enabled));
                }
                if (event.key === "Escape") {
                  setServiceSearchQuery("");
                  setServiceSearchFocused(false);
                }
              }}
              placeholder="Search apps, workflows, reports..."
              type="search"
            />
            {serviceSearchFocused && (
              <div className="console-service-results" role="listbox">
                {serviceSearchResults.map((item) => (
                  <button
                    type="button"
                    disabled={!item.enabled}
                    key={item.label}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => openServiceLauncherItem(item)}
                  >
                    <ApplicationIcon appKey={item.iconKey || "default"} />
                    <span className="console-service-result-copy">
                      <span>{item.category}</span>
                      <strong>{item.label}</strong>
                      <small>{item.enabled ? item.description : "Enable this application before opening it."}</small>
                    </span>
                  </button>
                ))}
                {serviceSearchResults.length === 0 && (
                  <div className="console-service-empty">No matching applications.</div>
                )}
              </div>
            )}
          </div>
        )}
        {selectedRange && (
          <label className={`console-range-context${isTeamWorkspaceRoute ? " is-workspace-context" : ""}`}>
            <span
              className={`console-range-context-icon ${selectedRange.branding?.consoleIconUrl ? "has-image" : ""}`}
              aria-hidden="true"
            >
              {selectedRange.branding?.consoleIconUrl && (
                <img src={selectedRange.branding.consoleIconUrl} alt="" />
              )}
            </span>
            <span className="console-range-context-copy">
              <span>{isTeamWorkspaceRoute ? "Chapter" : workspaceVocabulary.workspace}</span>
              <strong>{isTeamWorkspaceRoute ? workspaceChapterContextLabel : selectedRange.name}</strong>
            </span>
            {!isTeamWorkspaceRoute && (
              <select
                aria-label="Switch active range"
                value={selectedRange.rangeId}
                onChange={handleActiveRangeChange}
              >
                {ranges.map((range) => (
                  <option key={range.rangeId} value={range.rangeId}>
                    {range.name}
                  </option>
                ))}
              </select>
            )}
          </label>
        )}
        <div className="console-user">
          <strong>{ownerLabel}</strong>
          <button type="button" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <div className="app-shell">
        <main className="workspace">
          <header className="workspace-header">
            <div className="workspace-title-row">
              {currentViewCopy.iconKey && (
                <ApplicationIcon appKey={currentViewCopy.iconKey} />
              )}
              <div>
                <h1>{currentViewCopy.title}</h1>
                {currentViewCopy.description && <p>{currentViewCopy.description}</p>}
              </div>
            </div>
            {!noOwnerWorkspaceAccess && (
              <button
                type="button"
                className="workspace-nav-toggle"
                onClick={() => setSidebarCollapsed((current) => !current)}
                aria-expanded={!sidebarCollapsed}
                aria-label={sidebarCollapsed ? "Show navigation" : "Hide navigation"}
              >
                <span className="workspace-nav-toggle-icon" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                <strong>{sidebarCollapsed ? "Menu" : "Hide menu"}</strong>
              </button>
            )}
          </header>

          <div className={`workspace-layout ${workspaceLayoutCollapsed ? "is-sidebar-collapsed" : ""}`}>
            {!isTeamWorkspaceRoute && !noOwnerWorkspaceAccess && !sidebarCollapsed && (
              <aside className="service-sidebar" aria-label="RangeGrid owner navigation">
                <div className="service-sidebar-label">Owner workspace</div>
                <nav className="service-nav" aria-label="RangeGrid sections">
                  {ownerNavGroups.map((group) => (
                    <div className="service-nav-group" key={group.group}>
                      <span className="service-nav-group-label">{group.group}</span>
                      {group.items.map((item) => (
                        <button
                          type="button"
                          className={item.active ? "is-active" : ""}
                          disabled={item.disabled}
                          key={item.label}
                          onClick={() => navigate(item.path)}
                        >
                          {item.iconKey && <ApplicationIcon appKey={item.iconKey} />}
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </nav>
              </aside>
            )}

            {isTeamWorkspaceRoute && !noOwnerWorkspaceAccess && !sidebarCollapsed && (
              <aside className="service-sidebar chapter-workspace-sidebar" aria-label="Chapter lead applications">
                <div className="service-sidebar-label">Chapter lead</div>
                <nav className="service-nav" aria-label="Chapter lead applications">
                  <div className="service-nav-group">
                    <span className="service-nav-group-label">Applications</span>
                    {chapterWorkspaceNavItems.map((item) => (
                      <button
                        type="button"
                        className={item.active ? "is-active" : ""}
                        key={item.label}
                        onClick={() => navigate(item.path)}
                      >
                        <ApplicationIcon appKey={item.iconKey} />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </nav>
              </aside>
            )}

            <section className="workspace-content">
              {loading && (
                <div className="status-strip" role="status">
                  Loading owner console data...
                </div>
              )}
              {rangeRouteNotFound && (
                <div className="alert alert-warning" role="status">
                  <span>
                    <strong>Range unavailable.</strong> The requested range is not available to this signed-in owner.
                  </span>
                  <button type="button" onClick={() => navigate("/console")}>
                    Open home
                  </button>
                </div>
              )}
              {selectedRange && !selectedRangeOperational && (
                <div className="lifecycle-banner">
                  <strong>{selectedRange.name} is {selectedLifecycleStatus}</strong>
                  <span>Read-only pages remain available, but operational changes and public signup are blocked.</span>
                </div>
              )}
              {error && !isTransientBackgroundError(error) && (
                <div className="alert alert-error" role="alert">
                  <span>{error}</span>
                  <button type="button" onClick={() => setError("")}>
                    Dismiss
                  </button>
                </div>
              )}

              <Routes>
                <Route path="/" element={<Navigate to="/console" replace />} />
                <Route
                  path="/workspace"
                  element={<Navigate to={selectedRange ? selectedRangePath : "/console"} replace />}
                />
                <Route
                  path="/workspace/ranges/:rangeId"
                  element={<Navigate to={`${selectedRangePath}/apps/home`} replace />}
                />
                <Route
                  path="/workspace/ranges/:rangeId/apps/:workspaceApp"
                  element={teamWorkspaceRouteElement}
                />
                <Route
                  path="/console"
                  element={(
                    <HomePage
                      loading={loading}
                      membershipEnabled={membershipEnabled}
                      membershipLoading={membershipLoading}
                      membershipSummary={membershipSummary}
                      members={members}
                      membersLoading={membersLoading}
                      donationsEnabled={donationsEnabled}
                      donationsSummary={donationsSummary}
                      eventsEnabled={eventsEnabled}
                      navigate={navigate}
                      notificationSummary={notificationSummary}
                      onFetchStripeConnectClientSecret={fetchStripeConnectClientSecret}
                      onRefreshStripeConnect={handleRefreshStripeConnect}
                      onStartStripeConnect={handleOpenStripeConnectSession}
                      onStripeConnectExit={handleRefreshStripeConnect}
                      paymentEvents={paymentOverviewEvents}
                      paymentIssues={paymentOverviewIssues}
                      paymentOverviewLoading={paymentOverviewLoading}
                      paymentReceipts={paymentOverviewReceipts}
                      paymentSettings={paymentSettings}
                      readiness={rangeReadiness}
                      readinessLoading={rangeReadinessLoading}
                      ranges={ranges}
                      refreshingStripeConnect={refreshingStripeConnect}
                      resourcesEnabled={resourcesEnabled}
                      startingStripeConnect={startingStripeConnect}
                      stripeConnectSession={stripeConnectSession}
                      canManageAccess={canManageAccess}
                      chapters={chapters}
                      businessHoursConfigured={Boolean(businessHoursForm.updatedAt)}
                      businessHoursForm={businessHoursForm}
                      events={events}
                      eventsLoading={eventsLoading}
                      selectedRange={selectedRange}
                      setActiveRangeId={setActiveRangeId}
                    />
                  )}
                />
                <Route
                  path="/console/platform"
                  element={(
                    <PlatformAdminPage
                      loading={loading}
                      navigate={navigate}
                      ranges={ranges}
                      setActiveRangeId={setActiveRangeId}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/new"
                  element={(
                    <CreateRangePage
                      form={form}
                      handleSubmit={handleSubmit}
                      saving={saving}
                      setForm={setForm}
                      suggestedSlug={suggestedSlug}
                    />
                  )}
                />
                <Route
                  path="/console/onboarding"
                  element={(
                    <CreateRangePage
                      form={form}
                      handleSubmit={handleSubmit}
                      saving={saving}
                      setForm={setForm}
                      suggestedSlug={suggestedSlug}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId"
                  element={(
                    <HomePage
                      loading={loading}
                      membershipEnabled={membershipEnabled}
                      membershipLoading={membershipLoading}
                      membershipSummary={membershipSummary}
                      members={members}
                      membersLoading={membersLoading}
                      donationsEnabled={donationsEnabled}
                      donationsSummary={donationsSummary}
                      eventsEnabled={eventsEnabled}
                      navigate={navigate}
                      notificationSummary={notificationSummary}
                      onFetchStripeConnectClientSecret={fetchStripeConnectClientSecret}
                      onRefreshStripeConnect={handleRefreshStripeConnect}
                      onStartStripeConnect={handleOpenStripeConnectSession}
                      onStripeConnectExit={handleRefreshStripeConnect}
                      paymentEvents={paymentOverviewEvents}
                      paymentOverviewLoading={paymentOverviewLoading}
                      paymentReceipts={paymentOverviewReceipts}
                      paymentSettings={paymentSettings}
                      readiness={rangeReadiness}
                      readinessLoading={rangeReadinessLoading}
                      ranges={ranges}
                      refreshingStripeConnect={refreshingStripeConnect}
                      resourcesEnabled={resourcesEnabled}
                      startingStripeConnect={startingStripeConnect}
                      stripeConnectSession={stripeConnectSession}
                      canManageAccess={canManageAccess}
                      chapters={chapters}
                      businessHoursConfigured={Boolean(businessHoursForm.updatedAt)}
                      businessHoursForm={businessHoursForm}
                      events={events}
                      eventsLoading={eventsLoading}
                      selectedRange={selectedRange}
                      setActiveRangeId={setActiveRangeId}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/applications"
                  element={(
                    <ApplicationsPage
                      applications={applications.filter((application) => (
                        REWORKED_APPLICATION_MODULE_KEYS.has(application.moduleKey)
                      ))}
                      catalogLoading={catalogLoading}
                      disableApplication={handleDisableApplication}
                      disablingApplicationId={disablingApplicationId}
                      enableApplication={handleEnableApplication}
                      enablingApplicationId={enablingApplicationId}
                      launcherItems={allServiceLauncherItems}
                      openModule={(moduleKey, path) => navigate(path || `${selectedRangePath}/apps/${moduleKey}`)}
                      ownerLoading={loading}
                      rangeApplications={rangeApplications}
                      rangeApplicationsLoading={rangeApplicationsLoading}
                      selectedRange={selectedRange}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/settings"
                  element={(
                    <SettingsPage
                      brandingForm={brandingForm}
                      businessHoursForm={businessHoursForm}
                      businessHoursLoading={businessHoursLoading}
                      form={profileForm}
                      onSetOptionalAppVisibility={handleUpdateProductionMenuApp}
                      onSaveBranding={handleSaveBranding}
                      onSaveBusinessHours={handleSaveBusinessHours}
                      onSaveProfile={handleSaveProfile}
                      savingBranding={savingBranding}
                      savingBusinessHours={savingBusinessHours}
                      saving={savingProfile}
                      selectedRange={selectedRange}
                      setBrandingForm={setBrandingForm}
                      setBusinessHoursForm={setBusinessHoursForm}
                      setForm={setProfileForm}
                      updatingProductionMenuKey={updatingProductionMenuKey}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/access"
                  element={canManageAccess ? (
                    <AccessPage
                      accessForm={accessForm}
                      accessLoading={rangeAccessLoading}
                      accessFilters={rangeAccessFilters}
                      accessRecords={rangeAccessRecords}
                      chapters={chapters}
                      exportingAccess={exportingRangeAccess}
                      grantingAccess={grantingAccess}
                      onExportAccess={handleExportRangeAccess}
                      onGrantAccess={handleGrantAccess}
                      onUpdateAccess={handleUpdateAccess}
                      selectedRange={selectedRange}
                      setAccessForm={setAccessForm}
                      setAccessFilters={setRangeAccessFilters}
                      updatingAccessUserId={updatingAccessUserId}
                    />
                  ) : (
                    <EmptyPanel
                      title="Access unavailable"
                      message={rangeRouteNotFound ? "This range could not be found for the signed-in owner." : "Access management is available to owners."}
                      actionLabel="Open home"
                      onAction={() => navigate("/console")}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/audit"
                  element={(
                    <AuditPage
                      auditEvents={auditEvents}
                      auditLoading={auditLoading}
                      auditFilters={auditFilters}
                      exportingAudit={exportingAudit}
                      onExportAudit={handleExportAudit}
                      selectedRange={selectedRange}
                      setAuditFilters={setAuditFilters}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/notifications"
                  element={(
                    <NotificationsPage
                      exportingNotifications={exportingNotifications}
                      loading={notificationsLoading}
                      members={members}
                      notificationFilters={notificationFilters}
                      notifications={notifications}
                      notificationPreferences={notificationPreferences}
                      chapters={chapters}
                      onExportNotifications={handleExportNotifications}
                      onSaveNotificationPreferences={handleSaveNotificationPreferences}
                      onDeliverNotification={handleDeliverNotification}
                      onReviewEventApproval={handleReviewEventApproval}
                      onUpdateNotificationStatus={handleUpdateNotificationStatus}
                      savingNotificationPreferences={savingNotificationPreferences}
                      selectedRange={selectedRange}
                      setNotificationFilters={setNotificationFilters}
                      summary={notificationSummary}
                      updatingNotificationId={updatingNotificationId}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/payments"
                  element={(
                    <PaymentsPage
                      exportingEvents={exportingPaymentEvents}
                      exportingReconciliation={exportingPaymentReconciliation}
                      exportingReceipts={exportingPaymentReceipts}
                      loading={paymentOverviewLoading}
                      onRefreshStripeConnect={handleRefreshStripeConnect}
                      onStartStripeConnect={handleOpenStripeConnectSession}
                      onFetchStripeConnectClientSecret={fetchStripeConnectClientSecret}
                      onExportEvents={handleExportPaymentEvents}
                      onExportPeriodCloses={handleExportPaymentPeriodCloses}
                      onExportReconciliation={handleExportPaymentReconciliation}
                      onExportReceipts={handleExportPaymentReceipts}
                      onCloseReconciliation={handleClosePaymentReconciliation}
                      onDownloadReceipt={handleDownloadPaymentReceipt}
                      onRefundReceipt={handleRefundPaymentReceipt}
                      onSyncReconciliation={handleSyncPaymentReconciliation}
                      onUpdateReceiptReconciliation={handleUpdatePaymentReceiptReconciliation}
                      paymentEventFilters={paymentEventFilters}
                      paymentEvents={paymentOverviewEvents}
                      paymentIssues={paymentOverviewIssues}
                      paymentReceiptFilters={paymentReceiptFilters}
                      paymentReceipts={paymentOverviewReceipts}
                      paymentSettings={paymentSettings}
                      closingReconciliation={closingPaymentReconciliation}
                      refreshingStripeConnect={refreshingStripeConnect}
                      reconcilingReceiptId={reconcilingPaymentReceiptId}
                      refundingReceiptId={refundingPaymentReceiptId}
                      selectedRange={selectedRange}
                      syncingReconciliation={syncingPaymentReconciliation}
                      startingStripeConnect={startingStripeConnect}
                      stripeConnectSession={stripeConnectSession}
                      onStripeConnectExit={handleRefreshStripeConnect}
                      setPaymentEventFilters={setPaymentEventFilters}
                      setPaymentReceiptFilters={setPaymentReceiptFilters}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/build-e2e"
                  element={(
                    <BuildE2EDashboardPage selectedRange={selectedRange} />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/membership"
                  element={(membershipEnabled || rangeApplicationsLoading) ? (
                    <MembershipPage
                      createMemberForm={createMemberForm}
                      chapters={chapters}
                      chaptersEnabled={chaptersEnabled}
                      chaptersLoading={chaptersLoading}
                      editingMembershipTypeId={editingMembershipTypeId}
                      loading={membershipLoading || rangeApplicationsLoading}
                      membershipConfigForm={membershipConfigForm}
                      memberDetail={memberDetail}
                      memberDetailLoading={memberDetailLoading}
                      memberTimeline={memberTimeline}
                      memberTimelineLoading={memberTimelineLoading}
                      memberPaymentEvents={memberPaymentEvents}
                      memberPaymentEventsLoading={memberPaymentEventsLoading}
                      memberPaymentReceipts={memberPaymentReceipts}
                      memberPaymentReceiptsLoading={memberPaymentReceiptsLoading}
                      memberImportCommitting={memberImportCommitting}
                      memberImportLoading={memberImportLoading}
                      memberImportPreview={memberImportPreview}
                      memberFilters={memberFilters}
                      members={members}
                      membersLoading={membersLoading}
                      onAddHouseholdMember={handleAddHouseholdMember}
                      onAddMemberDocument={handleAddMemberDocument}
                      onAddMemberFlag={handleAddMemberFlag}
                      onAddMemberQualification={handleAddMemberQualification}
                      onCloseMemberDetail={handleCloseMemberDetail}
                      onChangeMemberPlan={handleChangeMemberPlan}
                      onCommitMemberImport={handleCommitMemberImport}
                      onCreateMember={handleCreateMember}
                      onArchiveMembershipType={handleArchiveMembershipType}
                      onEditMembershipType={handleEditMembershipType}
                      onExportMemberFlags={handleExportMemberFlags}
                      onExportMemberGuestPasses={handleExportMemberGuestPasses}
                      onExportMemberHousehold={handleExportMemberHousehold}
                      onExportMemberQualifications={handleExportMemberQualifications}
                      onExportMembers={handleExportMembers}
                      onOpenMemberDetail={handleOpenMemberDetail}
                      onPreviewMemberImport={handlePreviewMemberImport}
                      onRenewMember={handleRenewMember}
                      onUpdateMemberGuestPasses={handleUpdateMemberGuestPasses}
                      onUpdateMemberQualification={handleUpdateMemberQualification}
                      onUpdateMemberProfile={handleUpdateMemberProfile}
                      onResetMembershipTypeForm={resetMembershipTypeForm}
                      onSaveMembershipConfig={handleSaveMembershipConfig}
                      onSaveMembershipType={handleSaveMembershipType}
                      onSetDefaultMembershipType={handleSetDefaultMembershipType}
                      onRefreshStripeConnect={handleRefreshStripeConnect}
                      onStartStripeConnect={handleStartStripeConnect}
                      onUpdateResource={handleUpdateResource}
                      onUploadWaiver={handleUploadWaiver}
                      onUpdateMemberReview={handleUpdateMemberReview}
                      onUpdateMemberStatus={handleUpdateMemberStatus}
                      paymentSettings={paymentSettings}
                      paymentsLoading={paymentsLoading}
                      resources={resources}
                      resourcesEnabled={resourcesEnabled}
                      selectedRange={selectedRange}
                      setCreateMemberForm={setCreateMemberForm}
                      setMemberFilters={setMemberFilters}
                      setMembershipConfigForm={setMembershipConfigForm}
                      setMembershipTypeForm={setMembershipTypeForm}
                      summary={membershipSummary}
                      membershipTypeForm={membershipTypeForm}
                      savingMembershipConfig={savingMembershipConfig}
                      savingMembershipType={savingMembershipType}
                      savingMember={savingMember}
                      exportingMemberFlags={exportingMemberFlags}
                      exportingMemberGuestPasses={exportingMemberGuestPasses}
                      exportingMemberHousehold={exportingMemberHousehold}
                      exportingMemberQualifications={exportingMemberQualifications}
                      exportingMembers={exportingMembers}
                      addingHouseholdMemberId={addingHouseholdMemberId}
                      addingMemberDocumentId={addingMemberDocumentId}
                      addingMemberFlagId={addingMemberFlagId}
                      addingMemberQualificationId={addingMemberQualificationId}
                      archivingMembershipTypeId={archivingMembershipTypeId}
                      changingMemberPlanId={changingMemberPlanId}
                      renewingMemberId={renewingMemberId}
                      refreshingStripeConnect={refreshingStripeConnect}
                      startingStripeConnect={startingStripeConnect}
                      uploadingWaiver={uploadingWaiver}
                      selectedMemberDetailId={selectedMemberDetailId}
                      updatingGuestPassMemberId={updatingGuestPassMemberId}
                      updatingMemberProfileId={updatingMemberProfileId}
                      updatingMemberStatusId={updatingMemberStatusId}
                      updatingMemberId={updatingMemberId}
                      updatingResourceId={updatingResourceId}
                    />
                  ) : (
                    <EmptyPanel
                      title="Membership unavailable"
                      message={applicationUnavailableMessage("Membership")}
                      actionLabel={selectedRange ? "Open workspace menu" : "Open home"}
                      onAction={() => navigate(selectedRange ? `${selectedRangePath}/applications` : "/console")}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/resources/:resourceId"
                  element={chapterOrganizationWorkspace ? (
                    <Navigate replace to={`${selectedRangePath}/applications`} />
                  ) : (resourcesEnabled || rangeApplicationsLoading) ? (
                    <ResourcesPage
                      availabilityForm={availabilityForm}
                      availabilityResult={availabilityResult}
                      checkingAvailability={checkingAvailability}
                      checkingReservationConflicts={checkingReservationConflicts}
                      loading={resourcesLoading || rangeApplicationsLoading}
                      onCheckAvailability={handleCheckAvailability}
                      onCheckReservationConflicts={handleCheckReservationConflicts}
                      onCloseResourceDetail={() => navigate(`${selectedRangePath}/apps/resources`)}
                      onCreateResourceBlock={handleCreateResourceBlock}
                      onCreateResource={handleCreateResource}
                      onApplyLibertyFacilityTemplate={handleApplyLibertyFacilityTemplate}
                      onExportResources={handleExportFacilityResources}
                      onCreateReservation={handleCreateReservation}
                      onExportBlocks={handleExportResourceBlocks}
                      onExportReservations={handleExportResourceReservations}
                      onOpenResourceDetail={(resourceId) => navigate(`${selectedRangePath}/apps/resources/${resourceId}`)}
                      onUpdateResourceBlock={handleUpdateResourceBlock}
                      onUpdateResource={handleUpdateResource}
                      onUpdateReservation={handleUpdateReservation}
                      resourceBlockForm={resourceBlockForm}
                      resourceBlocks={resourceBlocks}
                      resourceBlocksSummary={resourceBlocksSummary}
                      resourceDetail={resourceDetail}
                      resourceDetailLoading={resourceDetailLoading}
                      resourceFilters={resourceFilters}
                      resourceReservationConflicts={resourceReservationConflicts}
                      resourceReservations={resourceReservations}
                      resourceReservationsSummary={resourceReservationsSummary}
                      resources={resources}
                      members={members}
                      resourceForm={resourceForm}
                      reservationForm={reservationForm}
                      exportingResourceBlocks={exportingResourceBlocks}
                      exportingResources={exportingResources}
                      exportingReservations={exportingResourceReservations}
                      savingResourceBlock={savingResourceBlock}
                      savingResource={savingResource}
                      savingReservation={savingReservation}
                      selectedRange={selectedRange}
                      selectedResourceDetailId={routeResourceId}
                      setAvailabilityForm={setAvailabilityForm}
                      setResourceFilters={setResourceFilters}
                      setResourceBlockForm={setResourceBlockForm}
                      setResourceForm={setResourceForm}
                      setReservationForm={setReservationForm}
                      summary={resourceSummary?.summary || {}}
                      updatingResourceBlockId={updatingResourceBlockId}
                      updatingResourceId={updatingResourceId}
                      updatingReservationId={updatingReservationId}
                    />
                  ) : (
                    <EmptyPanel
                      title="Resources unavailable"
                      message={applicationUnavailableMessage("Resources")}
                      actionLabel={selectedRange ? "Open workspace menu" : "Open home"}
                      onAction={() => navigate(selectedRange ? `${selectedRangePath}/applications` : "/console")}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/resources"
                  element={chapterOrganizationWorkspace ? (
                    <Navigate replace to={`${selectedRangePath}/applications`} />
                  ) : (resourcesEnabled || rangeApplicationsLoading) ? (
                    <ResourcesPage
                      availabilityForm={availabilityForm}
                      availabilityResult={availabilityResult}
                      checkingAvailability={checkingAvailability}
                      checkingReservationConflicts={checkingReservationConflicts}
                      loading={resourcesLoading || rangeApplicationsLoading}
                      onCheckAvailability={handleCheckAvailability}
                      onCheckReservationConflicts={handleCheckReservationConflicts}
                      onCloseResourceDetail={() => navigate(`${selectedRangePath}/apps/resources`)}
                      onCreateResourceBlock={handleCreateResourceBlock}
                      onCreateResource={handleCreateResource}
                      onApplyLibertyFacilityTemplate={handleApplyLibertyFacilityTemplate}
                      onCreateReservation={handleCreateReservation}
                      onExportResources={handleExportFacilityResources}
                      onExportBlocks={handleExportResourceBlocks}
                      onExportReservations={handleExportResourceReservations}
                      onOpenResourceDetail={(resourceId) => navigate(`${selectedRangePath}/apps/resources/${resourceId}`)}
                      onUpdateResourceBlock={handleUpdateResourceBlock}
                      onUpdateResource={handleUpdateResource}
                      onUpdateReservation={handleUpdateReservation}
                      resourceBlockForm={resourceBlockForm}
                      resourceBlocks={resourceBlocks}
                      resourceBlocksSummary={resourceBlocksSummary}
                      resourceDetail={resourceDetail}
                      resourceDetailLoading={resourceDetailLoading}
                      resourceFilters={resourceFilters}
                      resourceReservationConflicts={resourceReservationConflicts}
                      resourceReservations={resourceReservations}
                      resourceReservationsSummary={resourceReservationsSummary}
                      resources={resources}
                      members={members}
                      resourceForm={resourceForm}
                      reservationForm={reservationForm}
                      exportingResourceBlocks={exportingResourceBlocks}
                      exportingResources={exportingResources}
                      exportingReservations={exportingResourceReservations}
                      savingResourceBlock={savingResourceBlock}
                      savingResource={savingResource}
                      savingReservation={savingReservation}
                      selectedRange={selectedRange}
                      selectedResourceDetailId={routeResourceId}
                      setAvailabilityForm={setAvailabilityForm}
                      setResourceFilters={setResourceFilters}
                      setResourceBlockForm={setResourceBlockForm}
                      setResourceForm={setResourceForm}
                      setReservationForm={setReservationForm}
                      summary={resourceSummary?.summary || {}}
                      updatingResourceBlockId={updatingResourceBlockId}
                      updatingResourceId={updatingResourceId}
                      updatingReservationId={updatingReservationId}
                    />
                  ) : (
                    <EmptyPanel
                      title="Resources unavailable"
                      message={applicationUnavailableMessage("Resources")}
                      actionLabel={selectedRange ? "Open workspace menu" : "Open home"}
                      onAction={() => navigate(selectedRange ? `${selectedRangePath}/applications` : "/console")}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/events"
                  element={(eventsEnabled || applicationGateLoading) ? (
                    <EventsPage
                      eventAttendance={eventAttendance}
                      eventAttendanceFilters={eventAttendanceFilters}
                      eventAttendanceLoading={eventAttendanceLoading}
                      eventAttendanceSummary={eventAttendanceSummary}
                      eventFilters={eventFilters}
                      eventForm={eventForm}
                      editingEventId={editingEventId}
                      exportingEventAttendance={exportingEventAttendance}
                      exportingEventRegistrations={exportingEventRegistrations}
                      exportingEvents={exportingEvents}
                      events={events}
                      eventRegistrationFilters={eventRegistrationFilters}
                      eventRegistrationForm={eventRegistrationForm}
                      eventRegistrations={eventRegistrations}
                      eventRegistrationsLoading={eventRegistrationsLoading}
                      eventRegistrationsSummary={eventRegistrationsSummary}
                      chapters={eventChapterOptions}
                      chaptersEnabled={chaptersEnabled}
                      chaptersLoading={chaptersLoading}
                      canReviewEventApproval={!workspaceOnlyAccess && ["owner", "admin"].includes(String(selectedRange?.access?.role || "").toLowerCase())}
                      loading={eventsLoading || applicationGateLoading}
                      members={members}
                      membersLoading={membersLoading}
                      onCreateEvent={handleCreateEvent}
                      onCancelEventEdit={handleCancelEventEdit}
                      onEditEvent={handleEditEvent}
                      onCreateEventRegistration={handleCreateEventRegistration}
                      onExportEvents={handleExportEvents}
                      onExportEventAttendance={handleExportEventAttendance}
                      onExportEventRegistrations={handleExportEventRegistrations}
                      onPromoteNextEventWaitlist={handlePromoteNextEventWaitlist}
                      onQueueEventReminders={handleQueueEventReminders}
                      onRecordEventAttendance={handleRecordEventAttendance}
                      onReviewEventApproval={handleReviewEventApproval}
                      onSignOffEvent={handleSignOffEvent}
                      onUpdateEventAttendance={handleUpdateEventAttendance}
                      onUpdateEvent={handleUpdateEvent}
                      onUpdateEventRegistration={handleUpdateEventRegistration}
                      onStartStripeConnect={handleStartStripeConnect}
                      paymentSettings={paymentSettings}
                      paymentsLoading={paymentsLoading}
                      recordingAttendanceKey={recordingAttendanceKey}
                      resources={resources}
                      savingEvent={savingEvent}
                      savingEventRegistration={savingEventRegistration}
                      selectedEventId={selectedEventId}
                      selectedRange={selectedRange}
                      startingStripeConnect={startingStripeConnect}
                      setEventAttendanceFilters={setEventAttendanceFilters}
                      setEventFilters={setEventFilters}
                      setEventForm={setEventForm}
                      setEventRegistrationFilters={setEventRegistrationFilters}
                      setEventRegistrationForm={setEventRegistrationForm}
                      setSelectedEventId={setSelectedEventId}
                      summary={eventsSummary}
                      membershipSummary={membershipSummary}
                      updatingEventId={updatingEventId}
                      updatingEventRegistrationKey={updatingEventRegistrationKey}
                    />
                  ) : (
                    <EmptyPanel
                      title="Events unavailable"
                      message={applicationUnavailableMessage("Events")}
                      actionLabel={selectedRange ? "Open workspace menu" : "Open home"}
                      onAction={() => navigate(selectedRange ? `${selectedRangePath}/applications` : "/console")}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/insights"
                  element={(selectedRange && (insightsEnabled || rangeApplicationsLoading)) ? (
                    <InsightsPage selectedRange={selectedRange} />
                  ) : (
                    <EmptyPanel
                      title="Insights unavailable"
                      message={applicationUnavailableMessage("Insights")}
                      actionLabel={selectedRange ? "Open workspace menu" : "Open home"}
                      onAction={() => navigate(selectedRange ? `${selectedRangePath}/applications` : "/console")}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/non-members"
                  element={(eventsEnabled || rangeApplicationsLoading) ? nonMemberAttendeesRouteElement : (
                    <EmptyPanel
                      title="Guest leads unavailable"
                      message={applicationUnavailableMessage("Programs & Events")}
                      actionLabel={selectedRange ? "Open workspace menu" : "Open home"}
                      onAction={() => navigate(selectedRange ? `${selectedRangePath}/applications` : "/console")}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/chapters"
                  element={(!selectedRange || rangeApplicationsLoading) && applicationGateLoading ? (
                    <EmptyPanel
                      title="Loading workspace"
                      message="Loading application state..."
                    />
                  ) : !chapterOrganizationWorkspace ? (
                    <Navigate replace to={resourcesEnabled ? `${selectedRangePath}/apps/resources` : `${selectedRangePath}/applications`} />
                  ) : (chaptersEnabled || rangeApplicationsLoading) ? (
                    <ChaptersPage
                      events={events}
                      members={members}
                      membersLoading={membersLoading}
                      membershipConfigForm={membershipConfigForm}
                      membershipSummary={membershipSummary}
                      onCreateMember={handleCreateMemberFromPayload}
                      onSaveMembershipConfig={handleSaveMembershipConfig}
                      onUpdateMemberProfile={handleUpdateMemberProfileFromChapter}
                      onUpdateMemberStatus={handleUpdateMemberStatusFromChapter}
                      savingMember={savingMember}
                      savingMembershipConfig={savingMembershipConfig}
                      selectedRange={selectedRange}
                      setMembershipConfigForm={setMembershipConfigForm}
                      updatingMemberProfileId={updatingMemberProfileId}
                      updatingMemberStatusId={updatingMemberStatusId}
                    />
                  ) : (
                    <EmptyPanel
                      title="Chapters unavailable"
                      message={applicationUnavailableMessage("Chapters")}
                      actionLabel={selectedRange ? "Open workspace menu" : "Open home"}
                      onAction={() => navigate(selectedRange ? `${selectedRangePath}/applications` : "/console")}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/staff-portal"
                  element={teamWorkspaceRouteElement}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/donations"
                  element={donationsEnabled ? (
                    <DonationsPage
                      donationForm={donationForm}
                      donationFilters={donationFilters}
                      donationSettingsForm={donationSettingsForm}
                      donations={donations}
                      exportingDonations={exportingDonations}
                      loadingDonationReceiptId={loadingDonationReceiptId}
                      loading={donationsLoading}
                      onCreateDonation={handleCreateDonation}
                      onDownloadDonationReceipt={handleDownloadDonationReceipt}
                      onExportDonations={handleExportDonations}
                      onRecordDonationRefund={handleRecordDonationRefund}
                      onSaveDonationSettings={handleSaveDonationSettings}
                      recordingDonationRefundId={recordingDonationRefundId}
                      savingDonation={savingDonation}
                      savingDonationSettings={savingDonationSettings}
                      selectedRange={selectedRange}
                      setDonationForm={setDonationForm}
                      setDonationFilters={setDonationFilters}
                      setDonationSettingsForm={setDonationSettingsForm}
                      summary={donationsSummary}
                      settings={donationSettings}
                    />
                  ) : (
                    <EmptyPanel
                      title="Donations unavailable"
                      message={applicationUnavailableMessage("Donations")}
                      actionLabel={selectedRange ? "Open workspace menu" : "Open home"}
                      onAction={() => navigate(selectedRange ? `${selectedRangePath}/applications` : "/console")}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/daily-ops"
                  element={chapterOrganizationWorkspace ? (
                    <Navigate replace to={`${selectedRangePath}/applications`} />
                  ) : (
                    <DailyOpsPage
                      loading={membershipLoading || resourcesLoading}
                      membershipTypes={membershipSummary?.membershipTypes || []}
                      onAddMemberFlag={handleAddMemberFlag}
                      onCreateOperationNote={handleCreateOperationNote}
                      onCreateReservation={handleCreateReservationFromPayload}
                      onUpdateReservation={handleUpdateReservation}
                      onUpdateResource={handleUpdateResource}
                      onUpdateResourceSectionCommand={handleUpdateResourceSectionCommand}
                      members={members}
                      operationNotes={operationNotes}
                      operationNotesLoading={operationNotesLoading}
                      resourceBlocks={resourceBlocks}
                      resources={resources}
                      reservations={resourceReservations}
                      savingOperationNote={savingOperationNote}
                      updatingReservationId={updatingReservationId}
                      updatingResourceId={updatingResourceId}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/compliance"
                  element={<Navigate replace to={chapterOrganizationWorkspace ? `${selectedRangePath}/applications` : location.pathname.replace(/\/apps\/compliance$/, "/apps/daily-ops")} />}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/equipment"
                  element={chapterOrganizationWorkspace ? (
                    <Navigate replace to={`${selectedRangePath}/applications`} />
                  ) : (
                    <EquipmentPage
                      loading={resourcesLoading}
                      onExportResources={handleExportResources}
                      onUpdateResource={handleUpdateResource}
                      resources={resources}
                      updatingResourceId={updatingResourceId}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/armory"
                  element={chapterOrganizationWorkspace ? (
                    <Navigate replace to={`${selectedRangePath}/applications`} />
                  ) : (
                    <ArmoryPage
                      events={events}
                      loading={resourcesLoading || membershipLoading}
                      members={members}
                      onCreateArmoryItem={handleCreateArmoryItem}
                      onUpdateResource={handleUpdateResource}
                      reservations={resourceReservations}
                      resources={resources}
                      savingArmoryItem={savingResource}
                      updatingResourceId={updatingResourceId}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/launch"
                  element={(
    <LaunchCommandPage
      loading={rangeReadinessLoading}
      navigate={navigate}
      readiness={rangeReadiness}
                      selectedRange={selectedRange}
                      selectedRangePath={selectedRangePath}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/accounting"
                  element={accountingProductionReady ? (
                    <AccountingPage
                      donations={donations}
                      loading={paymentOverviewLoading || resourcesLoading || donationsLoading || eventsLoading}
                      onExportDonations={handleExportDonations}
                      onExportEvents={handleExportEvents}
                      onExportPaymentEvents={handleExportPaymentEvents}
                      onExportPaymentReconciliation={handleExportPaymentReconciliation}
                      onExportPaymentReceipts={handleExportPaymentReceipts}
                      onExportReservations={handleExportResourceReservations}
                      paymentEvents={paymentOverviewEvents}
                      paymentReceipts={paymentOverviewReceipts}
                      reservations={resourceReservations}
                    />
                  ) : (
                    <EmptyPanel
                      title="Accounting unavailable"
                      message="Payment reconciliation is not enabled for this workspace yet. Use Payments to finish Stripe setup before opening accounting exports."
                      actionLabel="Open workspace menu"
                      onAction={() => navigate(`${selectedRangePath}/applications`)}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/reports"
                  element={(
                    <ReportsPage
                      events={events}
                      loading={membershipLoading || resourcesLoading || eventsLoading}
                      members={members}
                      onExportEvents={handleExportEvents}
                      onExportMembers={handleExportMembers}
                      onExportReservations={handleExportResourceReservations}
                      reservations={resourceReservations}
                      resources={resources}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/communications"
                  element={communicationsProductionReady ? (
                    <CommunicationsPage
                      loading={notificationsLoading || membershipLoading}
                      chapters={chapters}
                      chaptersEnabled={chaptersEnabled}
                      members={members}
                      notifications={notifications}
                      notificationSummary={notificationSummary}
                      onDeliverNotification={handleDeliverNotification}
                      onExportNotifications={handleExportNotifications}
                      onPreviewAudience={handlePreviewCommunicationAudience}
                      onQueueNotification={handleQueueCustomNotification}
                      onSendBroadcast={handleSendCommunicationBroadcast}
                      ownerEmail={ownerLabel.includes("@") ? ownerLabel : ""}
                      queueing={queueingCustomNotification}
                      updatingNotificationId={updatingNotificationId}
                    />
                  ) : (
                    <EmptyPanel
                      title="Communications unavailable"
                      message="Member broadcasts open after sender verification, unsubscribe handling, failed-delivery handling, and send history are ready. Use Notifications for review work and Guest Leads for visitor follow-up until then."
                      actionLabel="Open workspace menu"
                      onAction={() => navigate(`${selectedRangePath}/applications`)}
                    />
                  )}
                />
                <Route
                  path="/console/ranges/:rangeId/apps/leads"
                  element={(leadsEnabled || rangeApplicationsLoading) ? (
                    <LeadsPage
                      notificationPreferences={notificationPreferences}
                      onRefreshSenderEmailVerification={handleRefreshSenderEmailVerification}
                      onSaveNotificationPreferences={handleSaveNotificationPreferences}
                      onSendLeadEmail={handleSendRangeLeadEmail}
                      onStartSenderEmailVerification={handleStartSenderEmailVerification}
                      savingNotificationPreferences={savingNotificationPreferences}
                      selectedRange={selectedRange}
                      sendingLeadEmailId={sendingGuestLeadEmailId}
                      updatingSenderEmail={updatingSenderEmail}
                    />
                  ) : (
                    <EmptyPanel
                      title="Leads unavailable"
                      message={leadsAvailable
                        ? applicationUnavailableMessage("Leads")
                        : "Leads open after the RangeGrid-hosted public website is live for this workspace."}
                      actionLabel={selectedRange ? "Open workspace menu" : "Open home"}
                      onAction={() => navigate(selectedRange ? `${selectedRangePath}/applications` : "/console")}
                    />
                  )}
                />
                <Route path="*" element={<Navigate to="/console" replace />} />
              </Routes>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
